import { PLAN_LIMITS } from '../../shared/plans.js';

/**
 * Copy for both languages.
 *
 * `desktop` and `mobile` hold the wording from the two design files verbatim —
 * mobile shortens several labels to fit a 402px screen. `shell` holds the
 * strings the prototype never needed: sign-in, sync state and error reporting.
 */

/**
 * Polish plurals.
 *
 * Three forms, not two: 1 zmiana, 2 zmiany, 5 zmian. English speakers writing
 * Polish copy reach for the singular and the genitive and stop there, which is
 * how an app ends up saying "2 zmian" - grammatically wrong in a way every
 * native speaker notices immediately, and the surest sign that nobody who
 * speaks the language read the screen.
 *
 * The exception in the middle is real: 12, 13 and 14 take the many form even
 * though they end in 2, 3 and 4.
 */
const pl = (n, one, few, many) => {
  const count = Math.abs(Number(n));
  if (count === 1) return one;
  const last = count % 10;
  const lastTwo = count % 100;
  if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return few;
  return many;
};

/* The free plan's limits, quoted in the messages that explain them. */
const FREE = PLAN_LIMITS.free;

/**
 * 1st, 2nd, 3rd, 4th -- and 11th, 12th, 13th, which are the ones people get wrong.
 *
 * English only. Polish says "14. dnia" and needs nothing of the sort.
 */
const ordinalSuffix = n => {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return 'th';
  if (last === 1) return 'st';
  if (last === 2) return 'nd';
  if (last === 3) return 'rd';
  return 'th';
};

/*
 * The month a change is measured against, written out in full.
 *
 * It used to be the three-letter label from the chart -- "+71 436 zł vs sie" --
 * and in Polish that abbreviation reads as the word "się" at a glance. Polish
 * also needs it in the genitive after "wobec" ("wobec sierpnia"), which no
 * abbreviation can carry.
 */
const MONTHS_FULL_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
/* Nominative, for a month named on its own: "na bieżący miesiąc: wrzesień 2026". */
const MONTHS_FULL_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'
];
/* Locative, after "w": "we wrześniu 2026", "w lutym 2026". */
const MONTHS_LOCATIVE_PL = [
  'styczniu', 'lutym', 'marcu', 'kwietniu', 'maju', 'czerwcu',
  'lipcu', 'sierpniu', 'wrześniu', 'październiku', 'listopadzie', 'grudniu'
];
/* "we" before a cluster "w" cannot carry alone: we wrześniu. */
const inMonthPl = key => (monthIndex(key) === 8 ? 'we ' : 'w ')
  + monthIn(MONTHS_LOCATIVE_PL, key) + ' ' + String(key).slice(0, 4);
const MONTHS_GENITIVE_PL = [
  'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
  'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'
];
/** '2026-08' -> 7. */
const monthIndex = key => parseInt(String(key).slice(5, 7), 10) - 1;
/* Anything that is not a month key is shown as given rather than as "undefined". */
/* "before June": przed czerwcem -- the instrumental, for the trend's empty months. */
const MONTHS_INSTRUMENTAL_PL = [
  'styczniem', 'lutym', 'marcem', 'kwietniem', 'majem', 'czerwcem',
  'lipcem', 'sierpniem', 'wrześniem', 'październikiem', 'listopadem', 'grudniem'
];

const monthIn = (names, key) => names[monthIndex(key)] || String(key);

/*
 * Expense category names. Declared once because the mobile tables shorten two
 * of them: at the 16px a phone's fields are forced to, "Subskrypcje i usługi
 * cyfrowe" does not fit a select on a 375px screen.
 */
const CAT_EN = {
  Housing: 'Housing', Groceries: 'Groceries', Dining: 'Restaurants & cafés',
  Shopping: 'Shopping', Vehicle: 'Vehicle', Transport: 'Transport',
  Utilities: 'Phone & internet', Subscriptions: 'Subscriptions & digital', Health: 'Health',
  Insurance: 'Insurance', Leisure: 'Leisure & hobbies', Fees: 'Fees & taxes',
  'Debt service': 'Debt service', Other: 'Other'
};

const CAT_PL = {
  Housing: 'Mieszkanie', Groceries: 'Zakupy spożywcze', Dining: 'Restauracje i kawiarnie',
  Shopping: 'Zakupy', Vehicle: 'Pojazd', Transport: 'Transport',
  Utilities: 'Media i telefon', Subscriptions: 'Subskrypcje i usługi cyfrowe', Health: 'Zdrowie',
  Insurance: 'Ubezpieczenia', Leisure: 'Rozrywka i hobby', Fees: 'Opłaty i podatki',
  'Debt service': 'Obsługa długu', Other: 'Inne'
};

const EN = {
  brandKicker: 'PERSONAL LEDGER',
  desk: 'DESK',
  language: 'LANGUAGE',
  savedLocally: 'SYNCED',
  entries: 'entries',
  trackedSince: 'tracked since',
  netWorth: 'NET WORTH',
  incomeMonth: 'INCOME · MONTH',
  spendMonth: 'SPEND · MONTH',
  savedMonth: 'SAVED · MONTH',
  debtOutstanding: 'DEBT OUTSTANDING',
  netTrend: 'Net worth trend',
  assetsMinusLiab: 'ASSETS − LIABILITIES',
  whereMoney: 'Where the money sits',
  invested: 'invested',
  unrealised: 'unrealised',
  spendByCat: 'Spend by category',
  attention: 'Attention',
  incomeCap: 'INCOME',
  expensesCap: 'EXPENSES',
  savingsRateCap: 'SAVINGS RATE',
  income: 'Income',
  expenses: 'Expenses',
  add: 'ADD',
  outstanding: 'OUTSTANDING',
  paidOff: 'PAID OFF',
  monthlyServicing: 'MONTHLY SERVICING',
  interestYear: 'INTEREST · YEAR',
  payoffQueue: 'Payoff queue',
  logPayment: 'LOG PAYMENT',
  addDebt: 'ADD DEBT',
  apr: 'APR',
  marketValue: 'MARKET VALUE',
  costBasis: 'COST BASIS',
  unrealisedCap: 'UNREALISED',
  returnCap: 'RETURN',
  thHolding: 'HOLDING',
  thClass: 'CLASS',
  thUnits: 'UNITS',
  thPrice: 'PRICE',
  thValue: 'VALUE',
  thPl: 'P/L',
  allocByClass: 'Allocation by class',
  refreshPrices: 'REFRESH PRICES',
  backfillPrices: 'FILL HISTORY',
  vehicleCap: 'VEHICLE',
  fuelUpkeep: 'fuel + upkeep',
  avgEconomy: 'AVG ECONOMY',
  spentTotal: 'SPENT · TOTAL',
  fuelLog: 'Fuel log',
  fillUp: 'FILL UP',
  fuelEntry: 'Fuel',
  log: 'LOG',
  serviceOwnership: 'Service & ownership',
  serviceKicker: 'MAINTENANCE · INSURANCE · TAX',
  thDate: 'DATE',
  thOdo: 'ODO',
  thCost: 'COST',
  phSource: 'source',
  phAmount: 'amount',
  phLender: 'lender / loan',
  phBalance: 'balance',
  phOriginal: 'original',
  phApr: 'APR %',
  phPayment: 'payment',
  phTerm: 'months',
  phFees: 'fees',
  phTicker: 'ticker',
  phName: 'name',
  phUnits: 'units',
  phAvgCost: 'cost / unit',
  phYield: 'rate % p.a.',
  phBoughtOn: 'bought on',
  phDay: 'day',
  phFromMonth: 'from',
  phUntilMonth: 'until',
  phPrice: 'price / unit',
  phOdometer: 'odometer',
  phCost: 'cost',
  phNote: 'note',
  nav: { overview: 'Overview', cash: 'Income & spend', debt: 'Debt', invest: 'Investments', car: 'Vehicle', journal: 'Trading', tax: 'Taxes' },
  pages: {
    overview: ['DASHBOARD', 'Financial overview'],
    cash: ['CASHFLOW', 'Income & spend'],
    debt: ['LIABILITIES', 'Debt & payoff'],
    invest: ['ASSETS', 'Investments'],
    car: ['OWNERSHIP', 'Vehicle costs'],
    journal: ['TRADER TOOLS', 'Trading'],
    tax: ['TAX RETURN', 'Taxes']
  },
  cat: CAT_EN,
  cls: {
    'Equity / ETF': 'Equity / ETF', Crypto: 'Crypto', Retirement: 'Retirement',
    /* The stored key still says cash; the screen does not, because the class
       beside it is the one that means cash. */
    'Bonds / cash': 'Bonds', 'Real estate': 'Real estate',
    'Precious metals': 'Precious metals', Cash: 'Cash'
  },
  /* The Taxes tab: one dictionary per country, and what they share. */
  tax: {
    head: 'TAX RETURN',
    headSub: 'pick the country you file in',
    ready: 'READY',
    fresh: 'NEW',
    countries: {
      pl: ['Poland', 'PIT-38'],
      us: ['USA · futures', 'Form 6781 · §1256 · 60/40'],
      us8949: ['USA · shares', 'Form 8949 · Schedule D'],
      de: ['Germany', 'Anlage KAP · 25% + Soli'],
      uk: ['United Kingdom', 'SA108 · Capital Gains Tax']
    },
    year: 'TAX YEAR',
    journal: 'Include the journal',
    journalCount: (n, label) => n + (n === 1 ? ' trade' : ' trades') + ' in ' + label,
    propExcluded: n => (n === 1 ? '1 trade' : n + ' trades') + ' taken on a prop account ' + (n === 1 ? 'is' : 'are') + ' left out. The account is the firm’s, so its trades are not your capital gains: what is taxed is the payout, as income of another kind. Record payouts on the prop account’s screen and they go to your income.',
    nbp: 'CONVERT AT NBP RATES',
    nbpDone: '✓ NBP RATES',
    nbpBusy: 'CONVERTING…',
    nbpTitle: 'the NBP mid rate of the last business day before each trade',
    fromJournal: 'FROM THE JOURNAL',
    journalOff: 'Turn on “Include the journal” to count this basket.',
    noTrades: 'no trades from this period in the journal',
    futText: (n, gains, losses) => n + (n === 1 ? ' trade' : ' trades') + ': gains ' + gains + ', losses ' + losses + ' · at the rates in Preferences',
    unconverted: n => n + (n === 1 ? ' trade is' : ' trades are') + ' in a currency with no rate for its month, so left out. Add the rate in Preferences.',
    income: 'income',
    result: 'result',
    gross: 'gross',
    pl: {
      heroCap: next => 'TO PAY WITH PIT-38 · BY 30 APRIL ' + next,
      heroDue: next => 'pay it into your tax micro-account by 30.04.' + next,
      heroNone: 'nothing to pay — still file PIT-38 if there was any sale',
      partSec: 'Securities and derivatives',
      partSecSub: v => 'base ' + v,
      partCrypto: 'Crypto',
      partCryptoGain: v => 'income ' + v,
      partCryptoCarry: v => 'excess costs ' + v,
      partDiv: 'Dividends',
      partDivSub: v => v + ' withheld abroad',
      secTitle: 'Shares, ETFs and derivatives',
      secSub: 'part C · together with futures and CFDs',
      secLoss: (a, b) => 'loss — deduct it in ' + a + '–' + b,
      jText: (n, income, cost, nbp) => n + (n === 1 ? ' trade' : ' trades') + ': income ' + income + ', costs ' + cost
        + (nbp ? ' · NBP rate of the day before' : ' · at the rates in Preferences — convert at NBP rates before filing'),
      p8Income: 'PIT-8C income (Polish broker)',
      p8Cost: 'PIT-8C costs',
      secIncome: 'other income (foreign broker)',
      secCost: 'other costs: purchases, commissions',
      secNote: 'A Polish broker sends PIT-8C — copy its amounts as they are. A foreign broker sends none: enter the totals from its annual report.',
      cryptoTitle: 'Crypto',
      cryptoSub: 'part E · its own basket, never offset against share losses',
      cryptoIncome: 'income (sold for money or goods)',
      cryptoCost: year => 'costs from ' + year,
      cryptoCarried: 'excess costs from earlier years',
      cryptoCarryLab: next => 'excess costs to ' + next,
      cryptoCarryNote: (v, next) => 'Enter ' + v + ' as excess costs in your return for ' + next + '.',
      divTitle: 'Foreign dividends',
      divSub: 'part G · 19% less the tax withheld abroad',
      divLab: 'to pay',
      divGross: 'gross dividends, in złoty',
      divWithheld: 'tax withheld abroad',
      divOver: v => 'More than 19% was withheld — only ' + v + ' counts. Claim the rest back from the foreign payer.',
      divUs: 'The US usually withholds 15% with a W-8BEN on file — you pay the remaining 4% in Poland.',
      lossTitle: 'Losses from earlier years',
      lossSub: 'shares and derivatives · deducted oldest first, as they lapse after 5 years',
      lossYear: year => 'deducted in ' + year,
      lossAmount: 'loss',
      lossUsed: 'already deducted',
      lossLast: 'last year!',
      lossUntil: year => 'until ' + year,
      lossHalf: 'up to 50%',
      lossHalfTitle: 'at most half the loss in one year',
      lossOnce: 'one-off',
      lossOnceTitle: 'up to 5 million złoty at once',
      lossDeduct: v => 'you deduct ' + v,
      lossNoIncome: 'no income to deduct it from',
      lossLeft: v => v + ' left',
      lossLapses: v => v + ' lapses',
      lossDone: '✓ settled',
      lossNote: 'Each year you can deduct up to 50% of a loss. Once, up to 5 million złoty in one go; the rest still goes at 50% a year.',
      formTitle: 'Copy into PIT-38',
      formSub2025: 'whole złoty · field numbers of the PIT-38(18) form, for 2025',
      formSubOther: 'whole złoty · by part — field numbers change with each version of the form',
      field: n => 'f. ' + n,
      part: p => 'part ' + p,
      lines: {
        p8Income: 'Income from PIT-8C', p8Cost: 'Costs from PIT-8C',
        otherIncome: 'Other income', otherIncomeSub: 'the journal and foreign brokers', otherCost: 'Other costs',
        income: 'Total income', cost: 'Total costs', gain: 'Income', loss: 'Loss',
        losses: 'Losses from earlier years', base: 'Tax base', tax: 'Tax 19%', due: 'Tax due',
        cIncome: 'Crypto income', cCost: 'Crypto costs', cCostSub: 'this year and earlier', cGain: 'Crypto income',
        cCarry: 'Excess costs to next year', cTax: 'Tax due on crypto',
        dTax: 'Flat-rate tax on dividends', dCredit: 'Tax paid abroad', dDue: 'Difference to pay',
        total: 'Total to pay'
      },
      disclaimer: 'Prop firm payouts don’t go on PIT-38 — they are usually income from other sources or a business; ask an accountant. An estimate to help fill in the return, not tax advice; check the figures against PIT-8C and your broker’s statements.'
    },
    us: {
      heroCap: (year, next) => 'ESTIMATED TAX · ' + year + ' · DUE 15 APRIL ' + next,
      heroLoss: v => 'loss ' + v + ' — no tax, see carryback',
      heroSub: (v, blended, state) => 'on ' + v + ' · blended rate ' + blended + (state ? ' + state ' + state + '%' : ''),
      partLt: '60% long-term',
      partSt: '40% short-term',
      partAll: 'If it were all short-term',
      partSave: v => 'you save ' + v,
      rateTitle: 'Your rate',
      rateSub: 'federal bracket — both halves of 60/40 depend on it',
      other: 'Other contracts from 1099-B',
      otherHint: 'box 11 — accounts outside the journal',
      mtm: 'Open position adjustment',
      mtmHint: 'marked to market on 31 December',
      state: 'State tax',
      stateHint: 'optional, e.g. 4.95 in Illinois',
      splitTitle: '60 / 40 split',
      splitSub: 'however long the contract was held',
      lt: r => 'long-term ' + r + '%',
      st: r => 'short-term ' + r + '%',
      blended: 'BLENDED RATE',
      blendedSub: r => 'instead of ' + r + '% short-term',
      save: 'YOU SAVE',
      saveSub: 'against ordinary short-term gains',
      instTitle: 'Instruments in the journal',
      instSub: 'ticked ones are Section 1256 contracts — tap to change',
      yes: '§1256',
      no: 'NOT §1256',
      trades: n => n + (n === 1 ? ' trade' : ' trades'),
      pickNote: 'Known futures and index options are ticked for you. Untick shares, ETFs, spot crypto and forex — those aren’t Section 1256 contracts. Trades logged on a prop account are already left out.',
      noTrades: year => 'No real trades in the journal for ' + year + '.',
      formTitle: 'Form 6781 · Part I',
      formSub: year => 'Section 1256 contracts, ' + year,
      line: n => 'line ' + n,
      lines: {
        inst: root => root + ' — futures', instSub: n => 'from the journal · ' + n + (n === 1 ? ' trade' : ' trades'),
        other: 'Others from 1099-B', mtm: 'Mark-to-market adjustment', net: 'Net Section 1256 result',
        st: '40% short-term', stSub: '→ Schedule D, line 4', lt: '60% long-term', ltSub: '→ Schedule D, line 11'
      },
      notes: {
        noLots: 'No lot matching and no wash-sale rule — the year’s result is what counts, so the journal is enough.',
        which: 'Covers regulated contracts (ES, MES, NQ, MNQ, CL…). CFDs and spot forex don’t belong here.',
        lossNow: 'Net loss: you can carry it back 3 years against Section 1256 gains (box D on Form 6781), or forward.',
        lossLater: 'A net Section 1256 loss can be carried back 3 years — handy in a weaker year.',
        approx: 'The long-term rate follows your bracket only roughly: its real thresholds are income figures of their own.'
      },
      disclaimer: 'A federal estimate without NIIT, and without state tax unless you enter it. Journal results are taken as realised on 31 December. Prop firm payouts are business income, not Section 1256 gains. Check against box 11 of your broker’s 1099-B; not tax advice.'
    },
    us8949: {
      heroCap: (year, next) => 'ESTIMATED TAX · ' + year + ' · DUE 15 APRIL ' + next,
      heroSub: (net, state) => 'on a net capital gain of ' + net + (state ? ' · plus state ' + state + '%' : ''),
      heroLossCap: (year, next) => 'NET CAPITAL LOSS · ' + year + ' · RETURN DUE 15 APRIL ' + next,
      heroLossSub: (allowed, saving) => allowed + ' comes off your other income this year — about ' + saving + ' less tax',
      partShort: 'Short-term', partShortSub: rate => 'taxed like income, ' + rate + '%',
      partLong: 'Long-term', partLongSub: rate => 'held over a year, ' + rate + '%',
      part6781: 'Futures (Form 6781)', part6781Sub: 'in lines 4 and 11', part6781Off: 'not included',
      partAllowed: 'Deducted this year', partAllowedSub: limit => 'up to ' + limit + ' a year',
      partCarryShort: 'Carried forward, short', partCarryLong: 'Carried forward, long', partCarrySub: next => 'to ' + next,
      rateTitle: 'Your rate', rateSub: 'federal bracket — short-term gains are taxed at it, long-term ones at 0, 15 or 20%',
      separate: 'Married filing separately', separateSub: 'the yearly loss deduction is 1,500 $ instead of 3,000 $',
      with6781: 'Include futures (Form 6781)', with6781Sub: 'the futures card’s 40/60 split, on Schedule D lines 4 and 11',
      carryShort: 'Short-term loss carryover', carryLong: 'Long-term loss carryover',
      jTitle: 'Journal trades', jSub: 'what is not a Section 1256 contract — shares, ETFs, options on shares',
      jText: (n, roots) => n + (n === 1 ? ' trade' : ' trades') + ' (' + roots + ') · taken as short-term, box A',
      jNone: 'no trades outside Section 1256 contracts this year',
      jNote: 'The instruments unticked on the futures card are counted here. If you enter your broker’s 1099-B totals below, switch “Include the journal” off — the 1099-B already has these trades.',
      shortLab: 'short-term', longLab: 'long-term',
      boxTitle: key => 'Box ' + key + ' · 1099-B',
      boxSub: {
        A: 'short-term, cost basis reported to the IRS', B: 'short-term, basis not reported', C: 'short-term, no 1099-B',
        D: 'long-term, cost basis reported to the IRS', E: 'long-term, basis not reported', F: 'long-term, no 1099-B'
      },
      proceeds: 'proceeds', cost: 'cost basis', adjustment: 'adjustment', adjustmentHint: 'a disallowed wash-sale loss (code W)',
      moreBoxes: 'SHOW BOXES B, C, E, F', fewerBoxes: 'HIDE BOXES B, C, E, F',
      salesTitle: 'Sales without a 1099-B', salesSub: 'one row per sale — the dates decide short or long term',
      desc: 'Description', descPh: 'e.g. 100 sh. XYZ', acquired: 'Date acquired', sold: 'Date sold',
      addSale: '+ ADD SALE', removeSale: 'Remove this sale',
      descNeeded: 'Describe the sale, e.g. “100 sh. XYZ”.',
      datesNeeded: 'Give both dates, the sale on or after the purchase.',
      amountsNeeded: 'Give the proceeds and the cost basis.',
      salesNote: 'Held more than a year — from the day after the purchase’s anniversary — is long-term.',
      formTitle: 'Schedule D · from Form 8949', formSub: year => 'capital gains and losses, ' + year,
      part: p => 'part ' + p, line: n => 'line ' + n,
      partI: 'Short-term', partII: 'Long-term',
      boxLine: key => 'Box ' + key, boxLineSub: { A: 'and the journal', C: 'and sales without a 1099-B', F: 'and sales without a 1099-B' },
      from6781Short: 'Form 6781, 40%', from6781Long: 'Form 6781, 60%',
      netShort: 'Net short-term', netLong: 'Net long-term', netAll: 'Net capital gain or loss',
      lossLine: limit => 'Loss deducted (up to ' + limit + ')',
      totalTax: 'Estimated federal and state tax', totalLoss: 'Loss carried forward',
      notes: {
        basis: 'Box A and D sales with nothing to adjust can go straight on Schedule D lines 1a and 8a — Form 8949 is for the rest.',
        holding: 'Short-term gains are taxed like income; long-term ones at 0, 15 or 20% — the long-term rate follows your bracket only roughly.',
        wash: 'A loss on shares bought back within 30 days is a wash sale: it is disallowed now and added to the new shares’ cost. Your broker’s 1099-B shows it in the adjustment column.',
        niit: 'The 3.8% net investment income tax on higher incomes is not included.'
      },
      disclaimer: 'An estimate to help you fill in Form 8949 and Schedule D, not tax advice. Check the figures against your broker’s 1099-B, and when in doubt ask a tax professional.'
    },
    de: {
      heroCap: (year, next) => 'ESTIMATED TAX · ' + year + ' · RETURN DUE 31 JULY ' + next,
      heroLoss: (v, next) => 'loss ' + v + ' carries over to ' + next,
      heroSub: (allowance, base) => 'after ' + allowance + ' of your allowance · base ' + base,
      partCap: 'Abgeltungsteuer',
      partCapSub: v => '25% of ' + v,
      partSoli: 'Soli',
      partSoliSub: '5.5% of the tax',
      partChurch: 'Kirchensteuer',
      partChurchSub: p => (p ? p + '% of the tax' : 'not applicable'),
      settings: 'Settings',
      joint: 'Joint assessment',
      jointSub: 'the allowance rises from €1,000 to €2,000',
      no: 'NO',
      yes: 'YES',
      church: 'Kirchensteuer',
      churchSub: '8% in Bavaria and Baden-Württemberg, 9% elsewhere',
      none: 'NONE',
      futTitle: 'Futures and CFDs',
      futSub: 'Termingeschäfte · losses uncapped since 2024, offset against other capital income',
      shareTitle: 'Shares and ETFs',
      shareSub: 'a pot of its own — share losses offset share gains only',
      shareGains: 'gains from share sales',
      shareLosses: 'losses from share sales',
      shareCarry: 'share losses from earlier years',
      shareLossLab: 'loss to next year',
      shareCarryNote: (v, next) => 'You carry ' + v + ' of share losses into ' + next + '.',
      divTitle: 'Dividends and interest',
      divSub: 'foreign tax counts up to the German tax',
      dividends: 'gross dividends and interest',
      withheld: 'tax withheld abroad',
      otherCarry: 'other losses from earlier years',
      formTitle: 'Copy into Anlage KAP',
      formSub: 'amounts in euro',
      lines: {
        total: 'Capital income in total', totalSub: 'Kapitalerträge — after offsetting the pots',
        shareGains: 'of which gains from shares', shareGainsSub: 'Gewinne aus Aktienveräußerungen',
        shareLosses: 'Losses from shares', shareLossesSub: 'Verluste aus Aktienveräußerungen',
        fut: 'Result from futures', futSub: 'Termingeschäfte',
        allowance: 'Allowance', allowanceSub: v => 'Sparer-Pauschbetrag ' + v,
        cap: 'Abgeltungsteuer', capSub: (church, credit) => (church ? '25% adjusted for Kirchensteuer' : '25%' + (credit ? ' less ' + credit + ' paid abroad' : '')),
        soli: 'Solidaritätszuschlag 5.5%', church: 'Kirchensteuer', total2: 'Total'
      },
      notes: {
        german: 'A German broker withholds the tax itself — then Anlage KAP is only for a refund or a foreign account.',
        foreign: 'A foreign broker (e.g. IBKR) withholds nothing: everything has to be declared.',
        guenstiger: 'If your personal rate is below 25%, tick Günstigerprüfung — the tax office works out whichever is lower.'
      },
      disclaimer: 'Journal trades are converted with the rates in Preferences; in the return, use the ECB rate of each trade’s day. Not tax advice — check against your Jahressteuerbescheinigung or your broker’s report.'
    },
    uk: {
      heroCap: (label, due) => 'ESTIMATED CGT · ' + label + ' · PAY BY 31 JANUARY ' + due,
      heroLoss: v => 'loss ' + v + ' — report it to carry it forward',
      heroSub: v => 'from ' + v + ' after the £3,000 allowance',
      partNet: 'Net result',
      partNetSub: v => 'gains ' + v,
      partAea: 'Allowance',
      partAeaSub: 'Annual Exempt Amount',
      partCf: 'Losses to carry forward',
      partCfSub: 'losses carried forward',
      settings: 'Settings',
      band: 'Your income band',
      bandSub: 'gains within the basic band: 18%, above it: 24%',
      basic: 'BASIC 18%',
      higher: 'HIGHER 24%',
      futTitle: 'Futures and CFDs',
      futSub: 'contracts and CFDs — no matching rules, the result comes from the journal',
      shareTitle: 'Shares and ETFs',
      shareSub: 'totals after the rules: same day → 30 days → Section 104 pool',
      proceeds: 'disposal proceeds',
      cost: 'costs: purchase and fees',
      shareNote: 'Your broker’s capital gains report usually has the lots matched already — copy its totals.',
      bfTitle: 'Losses from earlier years',
      bfSub: 'reported to HMRC within 4 years of the year of the loss',
      bf: 'losses brought forward',
      bfLab: 'used now',
      bfNote: 'Used only to bring gains down to the £3,000 allowance — the rest stays for later years.',
      formTitle: 'Copy into SA108',
      formSub: label => 'amounts in pounds · ' + label,
      lines: {
        gains: 'Gains in the year', losses: 'Losses in the year', net: 'Net result',
        bf: 'Losses brought forward used', aea: 'Annual Exempt Amount', aeaSub: '£3,000',
        taxable: 'Taxable', tax: rate => 'Capital Gains Tax ' + rate + '%', cf: 'Losses carried forward'
      },
      notes: {
        must: 'You need to file SA108: gains above £3,000 or disposal proceeds above £50,000.',
        mustNot: 'Below the thresholds — SA108 isn’t required, but reporting a loss keeps it.',
        year: 'The tax year runs from 6 April to 5 April — the journal is split by date automatically.',
        spread: 'Spread betting is exempt from CGT and isn’t included here.'
      },
      disclaimer: 'Journal trades are converted with the rates in Preferences; HMRC accepts the rate of each trade’s day. Not tax advice — when in doubt, ask an accountant.'
    },
    later: {
      title: 'Other countries',
      sub: 'we’ll build them if people need them — tap if you do',
      subDemo: 'we’ll build them if people need them — create an account to ask for one',
      items: {
        ca: ['Canada', 'average cost'],
        au: ['Australia', '50% discount after 12 months'],
        nl: ['Netherlands', 'Box 3 — a tax on wealth, not on trades']
      },
      need: '+ I NEED IT',
      asked: '✓ ASKED',
      skip: 'SKIPPED',
      skipTitle: 'nothing to work out from trades'
    }
  },
  /* The admin panel -- only ever shown to the owner's account. */
  admin: {
    taxVotes: 'Tax summaries people asked for',
    taxVoteNames: { us8949: 'USA · shares', ca: 'Canada', au: 'Australia' },
    open: 'ADMIN PANEL',
    sidebar: 'ADMIN',
    title: 'Admin panel',
    refresh: 'REFRESH',
    loading: 'Loading…',
    loadFailed: 'The panel could not load its data. Try refreshing.',
    statsAt: when => 'As of ' + when,
    accounts: 'Accounts',
    accountsSub: (week, month) => '+' + week + ' in 7 days · +' + month + ' in 30 days',
    active: 'Active',
    activeSub: (week, month) => week + ' in 7 days · ' + month + ' in 30 days',
    paying: 'Paying',
    payingSub: (monthly, yearly) => monthly + ' monthly · ' + yearly + ' yearly',
    mrr: 'Monthly revenue',
    mrrSub: 'from subscriptions, trials not counted',
    subsNote: (trial, cancelling, pastDue) =>
      'In trial: ' + trial + ' · cancelling at period end: ' + cancelling + ' · payment overdue: ' + pastDue,
    plansTitle: 'Plans',
    plan: { free: 'Free', trial: 'Trial', gift: 'Launch gift', manual: 'Granted', stripe: 'Subscription', demo: 'Demo' },
    usageTitle: 'Usage',
    verified: 'Confirmed emails',
    discord: 'Linked to Discord',
    terms: 'Accepted the current terms',
    demos: 'Demo accounts',
    aiLabels: 'AI suggestions this month',
    aiSub: n => 'for ' + n + (n === 1 ? ' account' : ' accounts'),
    imports: 'CSV imports',
    importsSub: (month, entries) => month + ' this month · ' + entries + (entries === 1 ? ' entry' : ' entries') + ' in total',
    entries: 'Ledger entries',
    trades: 'Journal trades',
    signups: 'New accounts by month',
    listTitle: 'Accounts',
    search: 'Search by email',
    none: 'No account matches the search.',
    created: 'joined',
    lastSeen: 'last seen',
    confirmed: 'confirmed',
    unconfirmed: 'unconfirmed',
    adminBadge: 'admin',
    until: date => 'until ' + date,
    noEnd: 'no end date',
    subscription: (interval, status, date) =>
      'Stripe: ' + (interval === 'year' ? 'yearly' : 'monthly') + ', ' + status + (date ? ', period ends ' + date : ''),
    subStatus: {
      active: 'active', trialing: 'in trial', past_due: 'payment overdue', canceled: 'cancelled',
      unpaid: 'unpaid', incomplete: 'incomplete', incomplete_expired: 'expired', paused: 'paused'
    },
    cancelling: 'cancels at period end',
    untilLabel: 'Until (optional)',
    grant: 'GRANT LIQUIDITY+',
    update: 'SAVE DATE',
    revoke: 'REVOKE',
    stripeManaged: 'Paid through Stripe — change the plan in the Stripe dashboard.',
    grantNote: 'Liquidity+ granted here without a date does not expire. “Revoke” takes back only what was granted by hand: a trial or the launch gift runs on from its own dates.'
  },
  carType: {
    Maintenance: 'Maintenance', Repair: 'Repair', Insurance: 'Insurance',
    Registration: 'Registration', Tyres: 'Tyres', 'Loan payment': 'Loan payment',
    Inspection: 'Inspection', Other: 'Other'
  },
  alloc: { investments: 'investments', cash: 'cash', debt: 'debt' },
  strategy: { rate: 'HIGHEST INTEREST RATE FIRST', balance: 'LOWEST BALANCE FIRST' },
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  /*
   * Seeded income lines carry a language-independent `key` alongside their
   * stored label, so the demo ledger reads naturally in either language. A row
   * the user typed has no key and is shown exactly as entered.
   */
  seedLabels: {
    salary: 'Salary — Northwind',
    freelance: 'Freelance / consulting',
    dividends: 'Dividends'
  },
  units: {
    km: 'km', mile: 'mi', litre: 'L', gallon: 'gal',
    per100: 'L/100 km', mpg: 'mpg',
    metric: 'Metric', imperial: 'US / imperial',
    metricNote: 'kilometres, litres, L/100 km',
    imperialNote: 'miles, gallons, mpg'
  },
  fx: {
    ofIncome: p => p + ' of income',
    servicing: a => a + '/mo servicing',
    topCat: (c, a) => c + ' leads at ' + a,
    nothing: 'nothing logged',
    vsMonth: (s, key) => s + ' vs ' + monthIn(MONTHS_FULL_EN, key),
    vsLast: s => s + ' vs last month',
    firstMonth: 'first tracked month',
    /* Said under the income figure, because a salary lands a month after the
       work it pays for and the two are easy to read as the same month. */
    incomeForWorked: 'for last month’s work',
    monthsLeft: n => '~' + n + ' mo left',
    noPlan: 'no plan',
    /*
     * Not "no plan": the payment exists, it is simply smaller than the interest,
     * so the balance grows. That is the single most useful thing a person in
     * this position can be told, and the old copy hid it.
     */
    interestOutpaces: 'payment below interest',
    derivedPayment: 'from the term',
    debtFormNote:
      'Give either a payment or a term — whichever the agreement states. '
      + 'Fees are the one-off cost of borrowing and count towards the total. '
      + 'The debt counts from the month on screen.',
    /* The debt tab, month by month. */
    debtTrend: 'Debt month by month',
    debtTrendNote: 'owed at the end of each month',
    debtSame: key => 'no change vs ' + monthIn(MONTHS_FULL_EN, key),
    debtFirst: 'first month on record',
    debtCleared: 'paid off this month',
    debtProjected: 'Months ahead carry the latest balance forward — a payment counts from the month it is logged in.',
    debtProjectedBar: 'This month has not happened yet, so the latest balance is carried forward.',
    costsInterest: total => 'costs ' + total + ' in interest',
    repaid: (p, o) => p + ' repaid of ' + o,
    costPerUnit: unit => 'COST PER ' + unit.toUpperCase(),
    lastFill: v => 'last fill ' + v,
    fuelOf: a => a + ' fuel',
    alertDebtTitle: n => n + ' is your costliest debt',
    alertDebtBody: (r, b, i) => r + '% APR on ' + b + ' — roughly ' + i + ' of interest this month.',
    alertSaveTitle: (a, key) => 'Saved ' + a + ' in ' + monthIn(MONTHS_FULL_EN, key) + ' ' + String(key).slice(0, 4),
    /* The payoff sentence only when there is a debt to pay off. */
    alertSaveBody: (p, mo) => p + '% of income.'
      + (mo ? ' At this pace, you could pay off your smallest balance in ' + mo + (mo === 1 ? ' month.' : ' months.') : ''),
    alertShortTitle: 'Spending exceeded income',
    alertBudgetTitle: category => category + ' is over budget',
    alertBudgetBody: (amount, others) => amount + ' over this month’s limit' + (others ? ', and ' + others + (others === 1 ? ' more category is' : ' more categories are') + ' over too.' : '.'),
    alertShortBody: (a, c) => 'You are ' + a + ' short this month. Largest line: ' + c + '.',
    alertCarTitle: c => 'Vehicle runs at ' + c,
    alertCarBody: (tot, dist, econ) => tot + ' across ' + dist + ' since purchase, averaging ' + econ + '.',
    /* Consumption needs two fills. Until then the sentence simply stops rather
       than reporting an average that was never measured. */
    alertCarBodyNoEconomy: (tot, dist) => tot + ' across ' + dist + ' since purchase.'
  },
  mobile: {
    cat: { ...CAT_EN, Dining: 'Restaurants', Subscriptions: 'Subscriptions' },
    expensesCap: 'SPEND',
    savedMonth: 'SAVED',
    debtOutstanding: 'DEBT',
    interestYear: 'INTEREST · YR',
    avgEconomy: 'AVG ECONOMY',
    spentTotal: 'spent total',
    quickAdd: 'Quick add',
    pay: 'PAY',
    sheet: { income: 'Income', expense: 'Expense', fuel: 'Fuel' },
    nav: { overview: 'Overview', cash: 'Cashflow', debt: 'Debt', invest: 'Invest', car: 'Vehicle', journal: 'Trading', tax: 'Taxes', settings: 'Settings' },
    pages: {
      overview: ['DASHBOARD', 'Overview'],
      cash: ['CASHFLOW', 'Income & spend'],
      debt: ['LIABILITIES', 'Debt'],
      invest: ['ASSETS', 'Investments'],
      car: ['OWNERSHIP', 'Vehicle'],
      journal: ['TOOLS', 'Trading'],
      tax: ['TAX RETURN', 'Taxes']
    },
    fx: {
      servicing: a => a + '/mo',
      alertSaveBody: p => p + '% of income kept this month.',
      alertShortBody: (a, c) => a + ' short. Largest line: ' + c + '.',
      alertCarBody: (tot, econ) => tot + ' since purchase, averaging ' + econ + '.',
      alertCarBodyNoEconomy: tot => tot + ' since purchase.'
    }
  },
  shell: {
    signIn: 'SIGN IN',
    signUp: 'CREATE ACCOUNT',
    tryDemo: 'TRY THE DEMO',
    email: 'email',
    password: 'password',
    passwordAgain: 'repeat password',
    currentPassword: 'current password',
    addPassword: 'ADD A PASSWORD',
    changePassword: 'CHANGE PASSWORD',
    addPasswordNote: 'A password lets you sign in without Discord. Set one before you unlink your Discord account.',
    haveAccount: 'Already have an account?',
    noAccount: 'No account yet?',
    signOut: 'SIGN OUT',
    account: 'ACCOUNT',
    guest: 'temporary account',
    claim: 'CREATE ACCOUNT',
    claimTitle: 'Create your account',
    claimHint: 'An email and a password, and this ledger is yours — on every device.',
    claimFresh: 'Start with an empty ledger',
    claimFreshNote: 'The sample figures are removed. Language and currency stay as they are.',
    claimKeep: 'Keep everything that is here now',
    claimKeepNote: 'The sample figures stay alongside your own entries — delete them whenever you like.',
    claimedFresh: 'Account created — your ledger is empty and ready.',
    demoBanner: 'This is the demo — every figure here is an example.',
    demoBannerSub: 'A demo account is deleted after 3 days without use. Create an account to keep your changes — the basic features are free.',
    cancel: 'CANCEL',
    save: 'SAVE',
    synced: 'SYNCED',
    syncing: 'SYNCING…',
    offline: 'OFFLINE',
    pendingChanges: n => n + ' change' + (n === 1 ? '' : 's') + ' queued',
    offlineHint: 'Changes are saved on this device and will sync automatically.',
    dismiss: 'DISMISS',
    loading: 'Loading your ledger…',
    passwordsDiffer: 'Passwords do not match.',
    passwordShort: 'Password must be at least 8 characters long.',
    pricesUpdated: 'Prices updated.',
    pricesPartial: missed => 'Updated. No quote for: ' + missed,
    pricesEurHint: ticker => 'A euro ticker needs its exchange: e.g. ' + ticker + '.DE for Xetra, ' + ticker + '.AS for Amsterdam — change it with ✎.',

    heading: 'Sign in',
    headingSignUp: 'Create an account',
    subSignUp: 'All it takes is an email address and a password.',
    or: 'OR',
    discordNote: 'Discord shares only your username, avatar and email. We never post anything or read your servers.',
    subPassword: 'Sign in with your email and password, or create an account in a few seconds.',
    ctaDiscord: 'CONTINUE WITH DISCORD',
    /* Split round the two links: before, terms, between, privacy, after. */
    termsAgree: ['I accept the ', 'terms of service', ' and the ', 'privacy policy', '.'],
    termsNeeded: 'Accept the terms of service and the privacy policy to create an account.',
    termsName: 'Terms of service',
    privacyName: 'Privacy policy',
    giftTitle: 'Liquidity+ for three months, on us',
    giftBody: date => 'Thank you for being here from the start. Your account has Liquidity+ free of charge until ' + date + ':',
    giftIncludes: [
      'CSV statement import without a limit',
      'AI category suggestions (Claude by Anthropic)',
      'automatic prices and price history',
      'a trading journal without a monthly limit'
    ],
    giftAfter: 'Nothing to do and nothing is charged. When it ends the account moves to the Free plan, unless you choose to subscribe.',
    giftOk: 'GREAT',
    giftSeePlan: 'SEE MY PLAN',
    termsGateTitle: 'Terms of service and privacy policy',
    termsGateBody: 'To keep using your account, please accept the terms of service and the privacy policy. They describe how the app works and what happens to your data.',
    termsAccept: 'I ACCEPT',
    termsGateNeeded: 'Tick the box to accept them and keep using your account.',
    yourData: 'YOUR DATA',
    yourDataNote: 'Download a copy of everything in your account, or delete the account. Deleting cannot be undone.',
    downloadData: 'DOWNLOAD A COPY OF YOUR DATA',
    deleteAccount: 'DELETE ACCOUNT',
    deleteDemo: 'DELETE DEMO ACCOUNT',
    deleteWarning: 'This deletes your account and everything in it: income, expenses, debts, investments, vehicle and journal. It cannot be undone — download a copy first if you want one.',
    deleteDemoWarning: 'This deletes the demo account and the changes you made in it. It cannot be undone.',
    deletePasswordNeeded: 'Enter your password to delete the account.',
    deleteForever: 'DELETE FOR GOOD',
    accountDeleted: 'The account has been deleted, together with all its data.',
    connectingTitle: 'Redirecting to Discord',
    connectingBody: 'Approve access in the authorization window that opened.',
    steps: ['Preparing a secure request', 'Waiting for your approval', 'Finishing sign-in'],
    cancel: 'CANCEL',
    verified: 'CONNECTED',
    session: 'session',
    tokenExpiry: 'session expires',
    enter: 'ENTER LEDGER',
    switchAccount: 'switch account',
    authErrorTitle: 'Authorization cancelled',
    authErrorBody: 'Discord did not confirm the sign-in. Try again or sign in with your password.',
    retry: 'TRY AGAIN',
    back: 'BACK TO SIGN-IN',
    footer: 'OAUTH 2.0 · PKCE · BCRYPT',
    linkDiscord: 'LINK DISCORD',
    unlinkDiscord: 'UNLINK DISCORD',
    linkedAs: 'linked as',
    settingsTitle: 'Preferences',
    optHideBalances: 'Hide balances',
    optHideBalancesNote: 'Turns every figure into dots — handy when somebody can see your screen.',
    optStrategy: 'Payoff strategy',
    optStartTab: 'Opening screen',
    strategyRate: 'Highest interest rate first',
    strategyBalance: 'Lowest balance first',
    savedForAccount: 'Saved to your account.',
    close: 'CLOSE',
    preferences: 'PREFERENCES',

    verifyBannerTitle: 'Confirm your email address',
    verifyBannerBody: 'We sent a link to your inbox. Until you click it, your account stays unconfirmed.',
    verifyResend: 'SEND A NEW LINK',
    verifyOk: 'Address confirmed — thank you.',
    verifyFailed: 'This confirmation link has expired. Request a new one.',
    verifySent: 'A new link is on its way.',
    verifyNotSent: 'This server cannot send email — ask the administrator to confirm your address.',
    verifyBlocked: 'This address has not been confirmed yet.',

    forgotLink: 'Forgot your password?',
    forgotSend: 'SEND A RESET LINK',
    forgotBack: 'Back to sign in',
    forgotNote: 'We will email a link to this address if an account exists for it. The link works once and expires after an hour.',
    emailNeeded: 'Enter your email address first.',
    emailInvalid: 'That does not look like an email address — check it for a typo.',
    resetHeading: 'Set a new password',
    resetSub: 'Choose a new password for your account. You will be signed in automatically after that.',
    resetSubmit: 'SAVE AND SIGN IN',
    newPassword: 'new password',
    resetSent: 'If an account exists for that address, a link is on its way.',
    resetDone: 'Password changed.',
    resetExpired: 'That reset link has expired. Ask for a new one.',
    rateBackfill: 'FILL HISTORY',
    rateBackfillNote: '“Fill history” looks up what the currency was worth in each month that has foreign entries, and records a rate for each of them. Rates you typed yourself are left alone.',
    rateRefreshNote: month =>
      '“Fetch rates” records today’s rate for ' + month + ', not for the month on screen.',
    ratesBackfilled: n =>
      'Filled in ' + n + (n === 1 ? ' monthly rate.' : ' monthly rates.'),
    ratesBackfillNone: 'Nothing to fill in — every month already has its rate.',
    aiSection: 'AI suggestions',
    aiNote: 'On the import review screen, “Suggest categories” asks Claude, by Anthropic, to categorise the rows the dictionary did not recognise. '
      + 'Only the descriptions are sent — never amounts, dates or account details — and Claude may look an unfamiliar name up on the web. '
      + 'Suggestions are only proposals: nothing is saved until you press “Import”.',
    aiSearched: n => n + (n === 1 ? ' web search' : ' web searches'),
    aiSort: 'SUGGEST CATEGORIES',
    aiSorted: (applied, asked, searches) =>
      'Sorted ' + applied + ' of ' + asked + ' rows'
      + (searches ? ', after ' + searches + (searches === 1 ? ' web search' : ' web searches') : '')
      + '. The rest were left alone — check them before importing.',
    aiNothing: 'No expense rows to sort.',
    aiAllSet: 'Every expense already has a category — nothing was sent to the AI.',
    costTitle: 'Cost basis',
    costUnits: 'units',
    costPerUnit: 'cost per unit',
    costTotal: 'total paid',
    /* Beside the price the way "total paid" sits beside the cost: both boxes
       are per unit, and both invite a total instead. */
    positionValue: 'position value',
    /*
     * Written out because this is the mistake, not a nicety. The column holds a
     * price per unit; a total typed into it is multiplied by the quantity a
     * second time, and the position then reports a loss that never happened.
     */
    costNote:
      'Two ways of saying the same thing — fill in whichever you know and the other '
      + 'follows. What gets stored is the price per unit, so a total typed into that '
      + 'box is counted once per unit and reads back as a loss you never took.',
    costEdit: 'Cost basis — tap to correct',
    exitTitle: 'What happens to this position?',
    exitSell: month => 'SOLD IN ' + month,
    exitSellNote: month =>
      'Stops counting from ' + month + ' on. Everything before it keeps the position, '
      + 'because you did hold it then.',
    exitDelete: 'DELETE FROM ALL HISTORY',
    exitDeleteNote:
      'Removes it from every month, as if it had never been bought. Use this for a row '
      + 'entered by mistake, not for something you sold.',
    exitReopen: 'PUT IT BACK ON THE BOOKS',
    exitReopenNote: month => 'Sold in ' + month + '. This undoes that and counts it again.',
    soldIn: month => 'sold ' + month,
    purchasePrice: 'purchase price',
    purchaseDate: 'bought on',
    ownershipTotal: 'TOTAL OWNERSHIP',
    serviceDate: 'date',
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    jClear: 'CLEAR',
    /* The unchosen state for a select that must not guess on the reader's behalf. */
    jUnset: 'choose…',
    jPairHint: 'e.g. MES',
    jReportHint: 'https://…',
    jPlHint: '+ / −',
    jYesShort: 'YES',
    jNoShort: 'NO',
    serviceDateTitle: 'When it happened',
    serviceDateNote: 'Pick the day of the service or the fill-up — it does not have to be today.',
    jDate: 'date',
    jPair: 'pair',
    jSession: 'session',
    jReport: 'report link',
    jLots: 'lots',
    jSide: 'type',
    jSetup: 'setup',
    jTimeFrame: 'time frame',
    jExit: 'exit',
    jRules: 'rules',
    jNetPl: 'net P&L',
    jCurrency: 'currency',
    jMm: 'money mgmt',
    jRisk: 'risk',
    jAdd: 'LOG TRADE',
    jWinRate: 'WIN RATE',
    jFollowRate: 'PLAN FOLLOWED',
    jPlanSplit: (followed, broken) => 'to plan ' + followed + ' · off plan ' + broken,
    jLoading: 'Loading the journal…',
    jFailed: 'The journal could not be loaded. Nothing has been lost — this is only the reading of it.',
    jRetry: 'TRY AGAIN',
    jMixed: list => 'mixed currencies (' + list + ') — no single total',
    jEmpty: 'Nothing logged yet. A trade recorded honestly is worth more than three recorded kindly.',
    jModeLive: 'Journal',
    jModeBacktest: 'Backtesting',
    btSession: 'Backtesting session',
    btIntro: 'A session is a strategy tested on past charts. Its trades stay out of the journal and its statistics, and do not count towards the Free plan’s limit.',
    btNamePh: 'session name, e.g. MES ORB — March 2025',
    btNew: 'NEW SESSION',
    btNameNeeded: 'Give the session a name.',
    btNone: 'No sessions yet. Name the first one — the strategy and the period you are testing — and start logging.',
    btDelete: 'DELETE SESSION',
    btDeleteAsk: n => 'Delete this session and its ' + n + (n === 1 ? ' trade' : ' trades') + '? This cannot be undone. The journal is not affected.',
    btDeleteYes: 'DELETE',
    btEmpty: 'No trades in this session yet. Log them just like in the journal, with the date of the candle the position was on.',
    btProfitFactor: 'profit factor',
    btProfitFactorSub: 'gross profit ÷ gross loss',
    btExpectancy: 'per trade',
    btExpectancySub: 'average result of one trade',
    btDrawdown: 'max drawdown',
    btDrawdownSub: n => 'longest losing streak: ' + n,
    btAvg: 'avg win / avg loss',
    btEquity: 'Equity curve',
    btEquitySub: 'cumulative result, trade by trade',
    btMixed: 'This session mixes currencies, so the money figures are left out.',
    gapTitle: 'Discipline gap',
    gapSub: 'result per trade: backtest against real trading',
    gapHint: 'Give a setup the same name in a backtesting session and in the journal, and this shows how much of the strategy’s edge reaches your real result.',
    gapBacktest: 'backtest',
    gapLive: 'real',
    gapDiff: 'difference',
    gapCounts: (backtest, live) => backtest + ' in backtests · ' + live + ' real',
    gapFew: 'Too few trades to judge yet: at least 5 in backtests and 3 real ones.',
    gapBroken: (amount, n) => 'Trades against the plan (' + n + ') earn ' + amount + ' less on average than trades that kept it.',
    gapTilt: (amount, n) => 'Trades taken right after a loss (' + n + ') earn ' + amount + ' less on average than the rest.',
    gapAhead: 'Real trading beats the backtest here.',
    gapMixed: 'This setup mixes currencies, so the money figures are left out.',
    jModeProp: 'Prop firms',
    jModePit: 'PIT-38',
    pitYear: 'Tax year',
    pitJournal: 'Include the journal',
    pitJournalNote: 'Only if the journal holds trades on your own brokerage account. Prop-firm trades and payouts do not go on PIT-38 — ask an accountant how to settle those.',
    pitConvert: 'CONVERT AT NBP RATES',
    pitConverting: 'Converting…',
    pitConverted: (n, year) => n + (n === 1 ? ' trade' : ' trades') + ' from ' + year + ' converted at the NBP rate of the business day before each',
    pitNoTrades: year => 'No real trades in the journal for ' + year + '.',
    pitManual: 'Enter by hand (in złoty)',
    pitSecurities: 'Shares, ETFs, funds',
    pitSecIncome: 'income from sales',
    pitSecCost: 'costs (purchase, fees)',
    pitCrypto: 'Crypto — a basket of its own',
    pitCryptoIncome: 'income',
    pitCryptoCost: 'costs this year',
    pitCryptoCarried: 'unused costs from earlier years',
    pitDividends: 'Dividends from abroad',
    pitDivGross: 'gross, in złoty',
    pitDivWithheld: 'tax already paid abroad',
    pitLosses: 'Losses from earlier years (shares and derivatives)',
    pitLossAmount: year => 'loss ' + year,
    pitLossUsed: 'already deducted',
    pitLossOnce: 'deduct in one go (up to 5 million zł)',
    pitCapital: 'Securities and derivatives',
    pitCryptoSection: 'Crypto',
    pitDivSection: 'Dividends from abroad',
    pitIncome: 'income',
    pitCost: 'costs',
    pitGain: 'profit',
    pitLoss: 'loss (to deduct in the next five years)',
    pitDeducted: 'earlier losses deducted',
    pitBase: 'tax base',
    pitTax: 'tax 19%',
    pitCarry: 'unused costs carried to next year',
    pitDivDue: '19% of the dividends',
    pitDivCredit: 'tax paid abroad (counted up to 19%)',
    pitDue: 'to pay',
    pitTotal: (amount, year) => 'To pay with PIT-38 by 30 April ' + year + ': ' + amount,
    pitFromJournal: 'from the journal',
    pitDisclaimer: 'An estimate to help you fill in PIT-38, not tax advice. It counts what it is given: check the figures against your broker’s statements, and when in doubt ask an accountant.',
    pitRate: 'NBP',
    apiTaxRates: 'NBP rates could not be fetched just now. Try again in a moment.',
    apiReviewWait: 'One review a day: the next one can be written tomorrow.',
    rvTitle: 'Your week, reviewed',
    rvSub: 'written by Claude from your figures, only when you ask',
    rvAsk: 'WRITE THIS WEEK’S REVIEW',
    rvWriting: 'Writing…',
    rvNext: date => 'The next review from ' + date + '.',
    rvWritten: date => 'Written ' + date,
    rvPlus: 'The weekly review is part of Liquidity+.',
    rvNone: 'No review yet. Press the button and Claude will look over your month and your journal.',
    rvPrivacy: 'Claude gets totals, rates and changes only — no names, descriptions or anything that identifies you — and only when you press the button. Anthropic does not train models on it.',
    tmNotice: date => 'New terms from ' + date + ': statement imports become unlimited on Free; Free keeps one backtesting session and one prop firm account, and the tax summaries (Poland, and now the US, Germany and the UK) are part of Liquidity+. Also the weekly review by Claude.',
    tmRead: 'Read the terms',
    tmAccept: 'ACCEPT NOW',
    wiTitle: 'What if?',
    wiSub: 'Your net worth month by month — as things are and with a change.',
    wiSubShort: 'Net worth as things are and with a change',
    wiPresets: ['Cautious', 'Balanced', 'Bold'],
    wiPresetTitle: (extra, invest, ret) => 'Overpay ' + extra + ', invest ' + invest + ', ' + ret + ' a year',
    wiPerMonthShort: amount => amount + '/mo',
    wiKpiEnd: years => 'IN ' + years.toUpperCase(),
    wiKpiGain: 'THE CHANGE ADDS',
    wiKpiDebt: 'DEBT-FREE',
    wiKpiInterest: 'INTEREST SAVED',
    wiKpiLessInterest: 'LESS INTEREST',
    wiWithout: value => 'as things are ' + value,
    wiInstead: value => 'instead of ' + value,
    wiAboutMonth: value => '≈ ' + value + ' a month',
    wiAboutMonthShort: value => '≈ ' + value + '/mo',
    wiInterestInstead: (now, before) => 'interest ' + now + ' instead of ' + before,
    wiBeyond: 'beyond the horizon',
    wiGainOverdrawn: 'assumes money you do not have — see below',
    wiShortTitle: month => 'Cash drops below zero in ' + month,
    wiShortTrim: amount => 'At these amounts you spend more than a month leaves. Lower the overpayment or the investing by about ' + amount + ' to stay liquid.',
    wiShortAlways: 'Even as things are, you spend more than a month leaves — this is not the sliders.',
    wiChart: 'Net worth',
    wiLegendDiff: 'difference',
    wiHover: 'hover over the chart',
    wiTouch: 'slide a finger along the chart',
    wiTouching: 'sliding',
    wiDiff: 'difference',
    wiTagShort: month => 'cash < 0 · ' + month,
    wiTagDebt: month => 'debt-free · ' + month,
    wiTagDebtNow: month => 'as things are · ' + month,
    wiMarkDebt: 'Debt-free — with the change',
    wiMarkDebtNow: 'Debt-free — as things are',
    wiMarkShort: 'Cash below zero',
    wiMixTitle: 'What the result is made of',
    wiMixAt: month => 'AT THE END · ' + month.toUpperCase(),
    wiMixDebt: 'debts',
    wiMixCash: 'cash',
    wiMixInvest: 'investments',
    wiMixDebtOf: value => 'debt ' + value,
    wiMixAssets: (cash, investments) => 'cash ' + cash + ' · investments ' + investments,
    wiMixAssetsShort: value => 'assets ' + value,
    wiQueue: 'The overpayment goes to the first debt in your payoff queue. Once every debt is paid, it is invested.',
    wiAxisK: 'k',
    wiAxisM: 'M',
    bt2Now: 'today',
    bt2NoSettings: 'no settings yet',
    bt2Settings: 'Session settings',
    bt2NoTrades: 'no trades yet',
    bt2Sessions: 'BACKTEST SESSIONS',
    bt2SessionsSub: 'kept apart from the journal — they do not touch its figures or the Free plan’s limit',
    bt2New: '+ NEW SESSION',
    bt2NewHint: 'the strategy and the period you are testing',
    bt2NumbersNeeded: 'Check the numbers — the capital cannot be negative, and 1R has to be more than zero.',
    bt2PeriodOrder: 'The period has to start before it ends.',
    bt2PeriodNote: 'The months of the chart you are replaying.',
    bt2FormNew: 'NEW SESSION',
    bt2FormEdit: 'SESSION SETTINGS',
    bt2Name: 'Session name',
    bt2NamePh: 'e.g. MES ORB — Q1 2025',
    bt2From: 'Period from',
    bt2To: 'Period to',
    bt2Capital: 'Starting capital',
    bt2Risk: 'Risk per trade (1R)',
    bt2Delete: 'Delete session',
    bt2RiskNote: '1R is what one trade risks — with it, results compare across strategies of any size.',
    bt2RiskNoteKept: 'Trades keep their result in money: a new 1R changes what each one reads in R.',
    bt2Create: '+ CREATE SESSION',
    bt2Ev: 'EXPECTANCY',
    bt2EvSub: money => '≈ ' + money + ' per trade',
    bt2WinSub: (wins, losses, be) => wins + (wins === 1 ? ' win' : ' wins') + ' · ' + losses + (losses === 1 ? ' loss' : ' losses') + ' · ' + be + ' BE',
    bt2PfSub: (win, loss) => 'avg win ' + win + ' · avg loss ' + loss,
    bt2DdSub: 'the deepest fall from a peak',
    bt2SampleShort: n => 'Sample: ' + n + ' / 30',
    bt2SampleShortSub: left => left + (left === 1 ? ' more trade' : ' more trades') + ' before the result starts to mean anything',
    bt2SampleOk: n => '✓ A usable sample: ' + n,
    bt2SampleOkSub: 'past 100 the drawdown will be more reliable too',
    bt2SampleStrong: n => '✓ A strong sample: ' + n,
    bt2SampleStrongSub: 'the result can be trusted statistically',
    bt2Capital2: (from, to, change) => 'capital ' + from + ' → ' + to + ' (' + change + ')',
    bt2SetCapital: 'set the capital with the session’s ✎',
    bt2CurveR: 'in R, trade by trade',
    bt2CurveEmpty: 'The curve appears after the first trade.',
    bt2ListSub: n => n + (n === 1 ? ' trade' : ' trades') + ' · newest first',
    bt2QuickHint: 'quick entry: the direction and the result in R — Enter saves and clears for the next one',
    bt2PairFallback: 'BACKTEST',
    bt2Quick: 'QUICK ENTRY',
    bt2OneR: amount => '1R = ' + amount,
    bt2NoR: 'no 1R set — results in money',
    bt2Candle: 'Candle date',
    bt2Result: 'Result',
    bt2Exits: { 'TP hit': 'TP', 'SL hit': 'SL', BE: 'BE', Manual: 'Manual' },
    bt2Fast: 'quick:',
    bt2More: 'setup and note ▾',
    bt2Less: 'less ▴',
    bt2SetupPh: 'stays for the next entries',
    bt2Note: 'Note',
    bt2NotePh: 'e.g. entered too early',
    bt2AutoExit: 'With no exit chosen: a win counts as TP, −1R as SL, 0 as BE.',
    bt2SetRisk: 'Set 1R with the session’s ✎ to enter results in R.',
    bt2Save: 'SAVE ↵',
    bt2Spread: 'How results spread',
    bt2SpreadSub: 'how many trades fall in each band of R',
    bt2Sides: 'Long vs short',
    bt2Total: total => 'in total ' + total,
    tlNet: 'NET RESULT',
    tlNetSub: (n, approx) => n + (n === 1 ? ' trade' : ' trades') + (approx ? ' · ≈ ' + approx : ''),
    tlWinSub: (wins, losses) => wins + (wins === 1 ? ' win' : ' wins') + ' · ' + losses + (losses === 1 ? ' loss' : ' losses'),
    tlAvg: 'AVERAGE PER TRADE',
    tlAvgSub: (win, loss) => 'win ' + win + ' · loss ' + loss,
    tlAvgNoLoss: 'no losses yet — the win/loss ratio needs the first one',
    rcOkTitle: amount => 'Your risk capital: ' + amount,
    rcOkSub: 'what trading can use without touching the cushion',
    rcProgress: (savings, cushion, n) => savings + ' / ' + cushion + ' · ' + n + (n === 1 ? ' month' : ' months') + ' of spending',
    rcOpen: 'DETAILS ▾',
    rcClose: 'FOLD ▴',
    tlList: 'Trade journal',
    tlListBacktest: 'Session trades',
    tlListSub: 'click a trade to see its details',
    tlAll: 'All',
    tlLong: 'Long',
    tlShort: 'Short',
    tlOff: 'Off plan',
    tlRange: label => 'Period: ' + label,
    tlRangeClear: 'Show every date',
    tlNoMatch: 'No trades for this filter.',
    tlLots: lots => lots + ' lots',
    tlNoCtx: 'no context',
    tlOnPlan: '✓ on plan',
    tlOffPlan: list => '! off plan: ' + list,
    tlBreakRules: 'rules',
    tlBreakMm: 'money management',
    tlExitL: 'Exit',
    tlRiskL: 'Risk',
    tlRulesL: 'Rules',
    tlMmL: 'Money management',
    tlReport: '↗ REPORT',
    tlEdit: '✎ Edit',
    tlNew: '+ TRADE',
    tlNewHint: 'the result, the context and 4 questions about discipline — about 20 seconds',
    tiButton: '⇪ IMPORT FROM TRADOVATE',
    tiHint: 'the Performance report, exported as CSV',
    tiUnknown: 'That file is not a Tradovate Performance export. In Tradovate, open Reports → Performance and export it as CSV.',
    tiEmpty: 'That Performance export has no closed trades in it.',
    tiFound: n => n + (n === 1 ? ' trade' : ' trades') + ' found',
    tiWinLoss: (wins, losses) => wins + (wins === 1 ? ' win' : ' wins') + ', ' + losses + (losses === 1 ? ' loss' : ' losses'),
    tiAbout: 'P&L as Tradovate reports it, before commissions. Partial exits are joined into one trade, and the prices and times go into the note. The trades come in on plan — open one to add the setup or mark a broken rule. A trade already in the journal is skipped, so the same export can be imported again.',
    tiSkipped: lines => 'Rows that could not be read, skipped: ' + lines,
    tiRoom: n => 'The free plan has room for ' + n + ' more ' + (n === 1 ? 'trade' : 'trades') + ' this month: the oldest are imported, and the rest can be imported next month or on Liquidity+.',
    tiNoRoom: 'This month\'s free journal trades are used. The import can be run again next month, or on Liquidity+.',
    tiImport: n => 'IMPORT ' + n,
    tiCancel: 'Cancel',
    tiClose: 'Done',
    tiDone: n => n === 0 ? 'Nothing new to import.' : (n === 1 ? '1 trade' : n + ' trades') + ' added to the journal.',
    tiDuplicates: n => (n === 1 ? '1 was' : n + ' were') + ' already there.',
    tiOverLimit: n => n + ' did not fit in this month\'s free allowance.',
    tiTyped: n => (n === 1 ? 'One trade looks' : n + ' trades look') + ' like ' + (n === 1 ? 'one' : 'ones') + ' you typed in by hand — same day, instrument, side and about the same result. ' + (n === 1 ? 'It is' : 'They are') + ' left out so nothing is counted twice:',
    tiTypedAs: (pair, result) => '↔ typed: ' + pair + ' ' + result,
    tiKeepTyped: 'Import them anyway',
    tiTypedSkipped: n => n + ' left out as already typed in by hand.',
    tiUpdated: n => (n === 1 ? '1 trade' : n + ' trades') + ' from an earlier import brought up to date with the exits closed since.',
    tlFormNew: 'NEW TRADE',
    tlFormEdit: 'EDITING A TRADE',
    tlStep1: 'The trade',
    tlStep2: 'Context',
    tlStep3: 'Discipline',
    tlOptional: 'optional',
    tlDateL: 'Date',
    tlPairL: 'Instrument',
    tlSideL: 'Direction',
    tlLotsL: 'Lots',
    tlPlL: 'Net result',
    tlRecent: 'recent:',
    tlSessionL: 'Session',
    tlSessionPh: 'e.g. New York, NY AM',
    tlTfL: 'Time frame',
    tlSetupL: 'Setup',
    tlSetupPh: 'what was on the chart, e.g. sweep + FVG',
    tlReportL: 'Report link',
    tlQExit: 'How did the trade end?',
    tlQRisk: 'Risk size',
    tlQRules: 'By the rules?',
    tlQMm: 'Money management kept?',
    tlExits: { 'TP hit': 'TP hit', 'SL hit': 'SL hit', BE: 'BE', Manual: 'Manual' },
    tlRisks: { FULL: 'Full', HALF: 'Half', QUARTER: 'Quarter' },
    tlVerdictAsk: 'Answer the questions about rules and money management.',
    tlVerdictOn: '✓ This trade counts as on plan.',
    tlVerdictOff: '! It will go under “Off plan”.',
    tlResultNeeded: 'Enter the net result — a figure, plus for a win and minus for a loss.',
    tlDisciplineNeeded: 'Say whether the trade kept to the rules and to money management — without that it cannot be judged honestly.',
    tlCal: 'P&L calendar',
    tlCalSub: (n, up, down) => n + (n === 1 ? ' trade' : ' trades') + ' · ' + up + (up === 1 ? ' day' : ' days') + ' up · ' + down + ' down',
    tlCalMixed: base => 'several currencies — in ' + base + ' at today’s rates',
    tlModes: ['Day', 'Week', 'Month'],
    tlDow: ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'],
    tlMonthsFull: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    tlWeek: n => 'Week ' + n,
    tlWeekShort: n => 'wk ' + n,
    tlTrShort: n => n + (n === 1 ? ' trade' : ' trades'),
    tlGain: 'profit',
    tlLoss: 'loss',
    tlCalHint: 'click a period to filter the journal',
    vhCar: 'VEHICLE',
    vhNoName: 'No name yet — ✎ to add one',
    vhOdo: 'Odometer',
    vhSince: 'Since purchase',
    vhTank: 'Tank',
    vhRange: 'Range',
    vhPerKmSub: distance => 'fuel + upkeep · ' + distance,
    vhPerKmNoDistance: 'shows once the odometer moves past the first reading',
    vhConsSub: distance => 'measured over ' + distance,
    vhYear: year => 'SPENT IN ' + year,
    vhYearSub: fuel => 'of which fuel ' + fuel,
    vhOwnSub: (price, running) => 'purchase ' + price + ' + running ' + running,
    vhNoPrice: 'running costs only — add the purchase price with ✎',
    vhFuelSub: (n, total) => n + (n === 1 ? ' entry' : ' entries') + ' · ' + total,
    vhFull: 'FULL',
    vhFirstFull: 'first full tank',
    vhWaitsFull: 'waits for a full tank',
    vhFuelNew: '+ FILL-UP',
    vhFuelNewHint: 'consumption is measured between two fill-ups to the brim',
    vhFuelForm: 'NEW FILL-UP',
    vhFuelEdit: 'EDITING A FILL-UP',
    vhOdoF: 'Odometer',
    vhVolF: 'Filled',
    vhCostF: 'Paid',
    vhFullToggle: 'Filled to the brim',
    vhFullNote: 'Leave it ticked when the pump clicked off. A top-up is counted with the next full tank.',
    vhAdd: '+ ADD',
    vhSplit: 'Where the money goes',
    vhSplitSub: 'since purchase, without the price of the car',
    vhFuel: 'Fuel',
    vhUpcoming: 'Coming up',
    vhUpcomingSub: 'worked out from your entries',
    vhUpcomingEmpty: 'Log an insurance payment, an inspection, or a service marked as an oil change, and the next date shows up here.',
    vhPolicy: 'Insurance renewal',
    vhOilMark: 'Oil change — count the next one from this entry',
    vhOilTag: 'OIL',
    vhInspection: 'Vehicle inspection',
    vhIcsFile: 'vehicle-dates.ics',
    vhIcsCalendar: 'Vehicle dates',
    vhIcsTitle: 'Download these dates as a calendar file (.ics), each with a reminder 14 days ahead',
    icsButton: '↓ CALENDAR (.ICS)',
    icsFrom: 'From Portfolio Tracker · liquidityraiders.pl',
    vhLastTime: (date, amount) => 'last ' + date + ' · ' + amount,
    vhInDays: n => 'in ' + n + (n === 1 ? ' day' : ' days'),
    vhOverdueDays: n => n + (n === 1 ? ' day' : ' days') + ' overdue',
    vhSvcOdoF: 'Odometer',
    vhOptional: 'optional',
    vhOilAt: reading => ' at ' + reading,
    vhOilEstimated: ' · odometer from the nearest fill-up — add it to the service entry to make this exact',
    vhOil: 'Oil change',
    vhOilSub: (interval, date) => 'every ' + interval + ' · last ' + date,
    vhOilNoOdo: 'no fill-up before it to measure from',
    vhInDist: distance => 'in ' + distance,
    vhOverdueDist: distance => distance + ' overdue',
    vhInterval: 'Interval',
    vhThousands: n => n + 'k',
    vhService: 'Service and upkeep',
    vhServiceSub: (n, total) => n + (n === 1 ? ' entry' : ' entries') + ' · ' + total,
    vhAll: 'All',
    vhServiceEmpty: 'No service entries yet.',
    vhServiceNew: '+ SERVICE ENTRY',
    vhServiceNewHint: 'repair, insurance, tyres, registration',
    vhServiceForm: 'NEW ENTRY',
    vhServiceEdit: 'EDITING AN ENTRY',
    vhWhat: 'What was done',
    vhWhatPh: 'e.g. Oil and filter change',
    vhAmount: 'Amount',
    vhServiceAdd: '+ ADD ENTRY',
    ivValueSub: (n, foreign) => n + (n === 1 ? ' position' : ' positions') + (foreign ? ' · ' + foreign + ' in foreign currencies' : ''),
    ivCostSub: (n, month) => n + (n === 1 ? ' purchase' : ' purchases') + (month ? ' since ' + month : ''),
    ivRes: 'UNREALISED RESULT',
    ivResSub: (up, down) => up + ' up · ' + down + ' down',
    ivPctSub: 'on cost, before dividends',
    ivPositions: 'Positions',
    ivPricesSub: (base, rates) => 'values in ' + base + (rates ? ' · rate ' + rates : ''),
    ivHistTitle: 'Looks up what each holding was worth in the months that have no price, so a past month shows what it was worth then',
    ivColAsset: 'ASSET',
    ivColUnits: 'UNITS · AVG COST',
    ivColPrice: 'PRICE',
    ivColValue: 'VALUE',
    ivColShare: 'SHARE',
    ivColRes: 'RESULT',
    ivLots: n => n + (n === 1 ? ' PURCHASE' : ' PURCHASES'),
    ivLotsTitle: 'Show the purchases in this position',
    ivEditLot: 'Edit purchase',
    ivRemoveLot: 'Sell or remove',
    ivUnits: n => n + (String(n) === '1' ? ' unit' : ' units'),
    ivUnitShort: 'units',
    ivAvg: amount => 'avg ' + amount,
    ivYieldLine: rate => rate + '% a year',
    ivNoRate: 'no rate',
    ivNoMonth: 'NO DATE',
    ivEmpty: 'No investments yet. Add your first purchase — shares, ETFs, metals, crypto or cash.',
    ivNew: '+ NEW PURCHASE',
    ivNewHint: 'shares, ETFs, metals, crypto — the same ticker joins the position you already hold',
    ivFormNew: 'NEW PURCHASE',
    ivFormEdit: 'EDITING A PURCHASE',
    ivTicker: 'Ticker',
    /* By asset class; anything else falls back to a fund's ticker. */
    ivTickerPh: cls => ({ Crypto: 'e.g. BTC, ETH', 'Bonds / cash': 'e.g. IB01.L', 'Real estate': 'e.g. VNQ', Cash: 'e.g. SAVINGS' })[cls] || 'e.g. VWCE.DE, AAPL',
    ivName: 'Name',
    ivNamePh: cls => ({ Crypto: 'e.g. Bitcoin', Retirement: 'e.g. IKE account', 'Bonds / cash': 'e.g. Treasury bonds', 'Real estate': 'e.g. Flat in Kraków', Cash: 'e.g. Savings account' })[cls] || 'e.g. Vanguard FTSE All-World',
    ivClass: 'Class',
    ivUnitsF: 'Units',
    ivCostF: 'Cost per unit',
    ivPriceF: 'Price now / unit',
    ivPricePh: 'optional',
    ivYieldF: 'Interest rate',
    ivSinceF: 'Month bought',
    ivFormL: 'Form',
    ivPhysical: 'Physical (bars, coins)',
    ivPaper: 'Paper (ETF, ETC)',
    ivMetalL: 'Metal',
    ivMetals: { XAU: 'Gold', XAG: 'Silver', XPT: 'Platinum', XPD: 'Palladium' },
    ivMetalsOf: { XAU: 'Gold', XAG: 'Silver', XPT: 'Platinum', XPD: 'Palladium' },
    ivWeightL: 'Weighed in',
    ivGrams: 'Grams',
    ivOunces: 'Troy ounces',
    ivUnitAbbr: { g: 'g', oz: 'oz t' },
    ivWeightF: 'Amount',
    ivCostPer: per => 'Cost per ' + per,
    ivPricePer: per => 'Price now / ' + per,
    ivPaperTickerPh: 'e.g. PHAU.L, SGLN.L',
    ivPaperNamePh: 'e.g. Physical gold ETC',
    ivMetalNamePh: 'e.g. 20 g bar, Krugerrand',
    ivMetalHint: (metal, month, price, stamp, piece) => metal + ' · ' + month + ': ' + price + ' pure'
      + (stamp ? ' · at ' + stamp + ': ' + piece : '') + ' — filled in as the cost; change it if you paid otherwise.',
    ivMetalLoading: 'Looking up the price in the month of purchase…',
    ivMetalNoRate: month => 'No exchange rate on record for ' + month + ' — enter the cost yourself, or add the rate in Preferences.',
    ivMetalMissing: 'No price found for that month — enter the cost yourself.',
    ivMetalManual: 'With Liquidity+ the price in the month of purchase fills itself in. On Free, enter the cost yourself.',
    ivPhysicalNote: 'Enter what the piece weighs, in the unit you chose. It is valued at its pure content — weight × fineness — at the ounce price, converted to grams if that is how you weigh it.',
    ivPurityL: 'Fineness',
    ivPurityOther: 'Other',
    ivPurityF: 'Fineness',
    ivPurityNeeded: 'Enter the fineness per mille, e.g. 900 — from 1 to 1000.',
    ivCurF: 'Currency',
    ivAmountF: 'Amount',
    ivNote: 'Bought a fraction? Enter part of a unit, e.g. 0.0472. The price now is shared by the whole position — a refresh overwrites it.',
    ivAdd: '+ ADD PURCHASE',
    ivClasses: 'Split by class',
    ivClassesSub: 'share of the portfolio’s market value',
    ivWhen: 'When it was bought',
    ivWhenSub: 'purchases month by month, newest first',
    ivPaidIn: 'paid in, in total',
    guideLink: 'Guide',
    guideSub: 'how to use the app',
    guideAria: 'Guide: how to use the app (opens in a new tab)',
    dbLeft: 'OUTSTANDING',
    dbLeftSub: n => n + (n === 1 ? ' debt' : ' debts'),
    dbPaid: 'REPAID',
    dbPaidSub: (paid, original) => paid + ' of ' + original,
    dbRata: 'MONTHLY INSTALMENTS',
    dbNoRata: n => n + (n === 1 ? ' debt' : ' debts') + ' with no set instalment',
    dbAllRata: 'every debt has a set instalment',
    dbNoDebtsSub: 'no debts on record',
    dbInt12: 'INTEREST · 12 MONTHS',
    dbInt12Sub: amount => '≈ ' + amount + '/mo on instalments alone',
    dbChart: 'Debt month by month',
    dbChartSub: 'the balance at the end of each month, and 12 months ahead',
    dbLegRecorded: 'recorded balance',
    dbLegBase: 'instalments alone',
    dbLegPlan: 'with the payoff plan',
    dbQueueRate: 'highest interest rate at the top',
    dbQueueBalance: 'lowest balance at the top',
    dbKindLoan: 'Loan',
    dbKindCard: 'Credit card',
    dbCardTag: 'CARD',
    dbCardRow: 'card — the balance moves with your payments',
    dbCardShort: 'card',
    dbCardRataF: 'Regular repayment (optional)',
    dbCardNote: 'A card is not projected: its balance changes only when you log a payment or correct it. The interest rate is used for the payoff plan.',
    dbPayFuture: 'A payment is logged in the month it is made. Months after today are a projection from the instalments.',
    dbProjected: 'PROJECTED',
    dbPayDayF: 'Payment day',
    dbPayDayShort: d => 'due on the ' + d + (d % 10 === 1 && d !== 11 ? 'st' : d % 10 === 2 && d !== 12 ? 'nd' : d % 10 === 3 && d !== 13 ? 'rd' : 'th'),
    dbPayDayNeeded: 'The payment day is a whole number from 1 to 31.',
    dbIcsTitle: 'Download the instalments as a calendar file (.ics) for Google, Apple or Outlook, with a reminder the day before',
    dbIcsNone: 'Set a payment day on a debt (✎) to add its instalments to your calendar.',
    dbIcsFile: 'debt-instalments.ics',
    dbIcsCalendar: 'Debt instalments',
    dbIcsSummary: (name, amount) => 'Instalment: ' + name + ' — ' + amount,
    dbIcsLeft: n => n + (n === 1 ? ' instalment left' : ' instalments left') + ' · from Portfolio Tracker',
    dbProjectedTitle: 'A month after today: balances are worked out from the last known one, paying the instalments alone',
    dbCount: n => n + (n === 1 ? ' entry' : ' entries'),
    dbRepaid: (pct, paid, original) => 'repaid ' + pct + ' · ' + paid + ' of ' + original,
    dbFee: amount => 'fee ' + amount,
    dbPaidOff: 'paid off ✓',
    dbNoRataRow: 'no instalment — set one to see when it ends',
    dbMonthsLeft: (n, month) => '~' + n + (n === 1 ? ' month' : ' months') + ' left · until ' + month,
    dbInterest: amount => 'interest ' + amount,
    dbRataLine: amount => 'instalment ' + amount,
    dbRataDerived: 'from the term',
    dbNoRataShort: 'no instalment',
    dbPayOpen: '+ PAYMENT',
    dbPayClose: '× CLOSE',
    dbPayIn: key => 'Payment in ' + monthIn(MONTHS_FULL_EN, key) + ' ' + String(key).slice(0, 4),
    dbPayHint: 'empty = the instalment',
    dbNew: '+ NEW DEBT',
    dbNewHint: 'a loan, a card, 0% instalments — enter the balance and the instalment, and it joins the queue and the plan',
    dbFormNew: 'NEW DEBT',
    dbFormEdit: 'EDITING A DEBT',
    dbLender: 'Bank / lender',
    dbLenderPh: 'e.g. Barclays, Klarna',
    dbBalNow: 'Balance now',
    dbOrig: 'Original amount',
    dbRateF: 'Interest rate',
    dbRataF: 'Instalment',
    dbMonthsF: 'or number of months',
    dbFeeF: 'Fee',
    dbCurrencyF: 'Currency',
    dbAdd: '+ ADD DEBT',
    dbPlan: 'Payoff plan',
    dbPlanSub: 'the overpayment, and the instalments of debts already paid, go to the first in the queue',
    dbStrat: { rate: ['Most expensive first', 'highest interest rate · least interest'], balance: ['Smallest first', 'lowest balance · debts disappear sooner'] },
    dbExtra: 'Overpayment every month',
    dbFree: 'DEBT-FREE',
    dbBaseFree: month => 'instalments alone: ' + month,
    dbNever: 'never',
    dbIntTotal: 'TOTAL INTEREST',
    dbBaseInt: amount => 'instalments alone: ' + amount,
    dbBaseIntNever: 'instalments alone: it never stops growing',
    dbOrder: 'ORDER AND PAYOFF DATE',
    dbPaidOffShort: 'paid off',
    dbNoteNoRata: (names, n) => names + (n === 1 ? ' has no instalment — without a plan it will not go away.' : ' have no instalments — without a plan they will not go away.')
      + ' The plan pays ' + (n === 1 ? 'it' : 'them') + ' from the instalments freed once the others are paid.',
    dbNoteSave: (months, amount) => 'You save ' + months + (months === 1 ? ' month' : ' months') + ' and ' + amount + ' of interest against paying the instalments alone.',
    dbNoteMove: 'Move the slider to see what an overpayment does.',
    dbEmpty: 'No debts. If you have a loan, a card or instalments, add them here and they will join the payoff plan.',
    cfIncome: 'INCOME',
    cfSpend: 'SPENDING',
    cfLeft: 'LEFT OVER',
    cfFixed: 'FIXED COSTS',
    cfSources: n => n + (n === 1 ? ' source' : ' sources'),
    cfItems: (n, c) => n + (n === 1 ? ' entry' : ' entries') + ' · ' + c + (c === 1 ? ' category' : ' categories'),
    cfOfIncome: pct => pct + ' of income',
    cfPerMonth: amount => amount + '/mo',
    cfFixedSub: (pct, n) => (pct === null ? '' : pct + ' of income · ') + n + (n === 1 ? ' cost' : ' costs'),
    cfOpenFixed: '+ NEW FIXED COST',
    cfOpenFixedHint: 'e.g. rent, Netflix, insurance — set it up once and it comes round on its own cycle',
    cfPostHint: (n, key) => n + (n === 1 ? ' fixed cost' : ' fixed costs') + ' for ' + monthIn(MONTHS_FULL_EN, key)
      + (n === 1 ? ' is' : ' are') + ' waiting to go into your transactions.',
    cfAllPostedHint: key => monthIn(MONTHS_FULL_EN, key) + '’s fixed costs are already in your transactions.',
    cfShowIncome: 'Show only income',
    cfShowSpend: 'Show only spending',
    cfShowAll: 'Show everything again',
    cfFlowTitle: 'Where the income went',
    cfFlowHint: 'click a category to filter the list',
    cfFlowLeft: 'Left over',
    cfTx: 'Transactions',
    cfAdd: '+ ADD',
    cfClose: '× CLOSE',
    cfAll: 'All',
    cfIncomes: 'Income',
    cfExpenses: 'Spending',
    cfOut: 'Expense',
    cfIn: 'Income',
    cfNameOut: 'on what? e.g. Tesco',
    cfNameIn: 'from where? e.g. salary',
    cfCustom: 'a category of your own, e.g. Gifts',
    cfRecurringToggle: 'a fixed cost, every month',
    cfDay: 'on day',
    cfCancel: 'Cancel',
    cfSave: 'SAVE',
    cfCount: n => n + (n === 1 ? ' entry' : ' entries'),
    cfShare: pct => pct + '% of income',
    cfFixedBadge: 'FIXED',
    cfRate: (original, rate) => original + ' · rate ' + rate,
    cfNoRate: 'no rate',
    cfEmptyTx: 'Nothing here this month yet. Add the first entry, or import a statement.',
    cfFilterClear: 'Clear the filter',
    cfFxSub: (pct, monthly, rarer) => (pct === null ? '' : pct + ' of income · ') + monthly + ' monthly, ' + rarer + ' less often',
    cfMonthly: 'a month',
    cfPostN: n => 'ADD ' + n + ' TO TRANSACTIONS',
    cfAllPosted: '✓ ALL IN TRANSACTIONS',
    cfAllPostedTitle: 'This month’s fixed costs are already in your transactions. Click to add them again.',
    cfCharged: amount => 'charged ' + amount,
    cfEveryHead: { 1: 'EVERY MONTH', 3: 'EVERY QUARTER', 6: 'EVERY SIX MONTHS', 12: 'EVERY YEAR' },
    cfPer: { 3: 'quarter', 6: 'half-year', 12: 'year' },
    cfIncomeHead: 'FIXED INCOME',
    cfApproxMonth: amount => '≈ ' + amount + '/mo',
    cfNewFixed: 'NEW FIXED COST',
    cfEditFixed: 'EDITING A FIXED COST',
    cfCycle: 'BILLING CYCLE',
    cfCycles: { 1: ['Monthly', '12× a year'], 3: ['Quarterly', '4× a year'], 6: ['Every 6 months', '2× a year'], 12: ['Yearly', '1× a year'] },
    cfFxName: 'name, e.g. Netflix',
    cfInMonth: 'in',
    cfFromMonth: 'from',
    cfAddFixed: '+ ADD FIXED COST',
    cfSaveChanges: 'SAVE CHANGES',
    cfMore: 'End date and pausing →',
    cfFxNoteA: 'Add this month’s fixed costs to your transactions with the button above. There they are marked ',
    cfFxNoteB: ', and nothing is added twice. Quarterly, six-monthly and yearly costs count here as a monthly share.',
    cfPaused: 'paused',
    cfMonths: MONTHS_FULL_EN,
    ovIncome: name => 'INCOME · ' + name.toUpperCase(),
    ovSpend: name => 'SPENDING · ' + name.toUpperCase(),
    ovSaved: name => 'SAVED · ' + name.toUpperCase(),
    ovIncomeShort: 'INCOME',
    ovSpendShort: 'SPENDING',
    ovSavedShort: 'SAVED',
    ovDebtShort: 'DEBT',
    ovMonthName: key => monthIn(MONTHS_FULL_EN, key),
    ovMonthOnMonth: value => value + ' m/m',
    ovTopShare: (pct, name, amount) => pct + ' is ' + name.toLowerCase() + ' (' + amount + ')',
    ovTopShareShort: (pct, name) => pct + ' is ' + name.toLowerCase(),
    ovOfIncome: pct => pct + ' of income',
    ovDebtSub: (amount, n) => 'instalments ' + amount + '/mo · ' + n + (n === 1 ? ' debt' : ' debts'),
    ovDebtSubShort: amount => 'instalments ' + amount + '/mo',
    ovTrendSub: 'assets minus liabilities',
    ovNoDataBefore: key => 'no data before ' + monthIn(MONTHS_FULL_EN, key),
    ovNow: 'NOW',
    ovActual: 'actual',
    ovForecast: 'forecast at today’s prices',
    ovForecastShort: 'forecast',
    ovBelowZero: 'below zero',
    ovNoData: 'no data',
    ovSlide: 'slide a finger along the chart',
    ovPicked: 'month picked',
    ovClickToOpen: 'click to open this month',
    ovForecastTag: 'FORECAST',
    ovFirstMonth: 'first month in the ledger',
    ovAtPrices: 'at today’s prices',
    ovVsPrev: (value, key) => value + ' against ' + monthIn(MONTHS_FULL_EN, key),
    ovNet: 'NET',
    ovDebts: 'Debts',
    ovInvestments: 'Investments',
    ovCash: 'Cash',
    ovLeftDebts: '← debts',
    ovRightAssets: 'assets →',
    ovInstalments: amount => 'instalments ' + amount + '/mo',
    ovUnrealised: amount => amount + ' unrealised',
    ovFillCash: 'enter your balance',
    ovCoverPre: 'Assets cover ',
    ovCoverPost: ' of the debt.',
    ovCover: 'cover',
    ovTrendEmpty: 'Your net worth, month by month, appears here once you add income, a debt, savings or an investment.',
    ovNetRecorded: 'as recorded when the month ended · the split below uses today’s cash',
    ovCoverTimes: n => 'Assets are ' + n + ' times the debt.',
    ovNoDebt: 'No debts: everything here is yours.',
    ovAvg3: '3-month average',
    ovAvg: 'average',
    ovAboutAvg: '≈ average',
    ovTotal: 'Total',
    ovAvgTotal: 'average',
    ovVsAvg: value => value + ' vs average',
    ovOverNote: 'red only where you spend over 20% more than usual',
    ovNoAverage: 'no earlier months to compare with yet',
    ovUpcomingSub: (days, from, to) => 'fixed costs in the next ' + days + ' days · ' + from + ' – ' + to,
    ovUpcomingSubShort: days => 'fixed costs · ' + days + ' days',
    ovForeign: (amount, converted, rate) => 'incl. ' + amount + ' ≈ ' + converted + (rate ? ' · rate ' + rate : ''),
    ovThisWeek: 'THIS WEEK',
    ovLater: 'LATER',
    ovToday: 'TODAY',
    ovShowAll: n => 'SHOW ALL (' + n + ')',
    ovCollapse: 'SHOW FEWER',
    rvForWeek: 'This week:',
    rvPrepared: (date, next) => 'Written ' + date + (next ? ' · the next review from ' + next : '') + '.',
    rvByClaude: date => 'by Claude · ' + date,
    wiSummary: (extra, invest, ret, years) => 'overpay ' + extra + ' · invest ' + invest + ' · ' + ret + ' · ' + years,
    wiOpen: 'OPEN THE SIMULATOR →',
    wiBack: 'Overview',
    wiInYears: years => 'in ' + years,
    wiChangeGivesLabel: 'the change adds',
    wiDebtFreeLabel: 'debt-free',
    wiChangeGives: amount => 'Overpaying and investing add ' + amount,
    wiShortAround: month => 'Cash drops below zero around ' + month + '.',
    wiSetSliders: 'Set an overpayment or investing amount in the simulator to see the difference.',
    wiExtra: 'Extra towards debts',
    wiInvest: 'Invest every month',
    wiReturn: 'Assumed yearly return',
    wiYears: 'Horizon',
    wiPerMonth: amount => amount + ' / month',
    wiYearsValue: n => n + (n === 1 ? ' year' : ' years'),
    wiHeadline: (years, amount, baseline) => 'In ' + years + ': ' + amount + ' (as things are: ' + baseline + ')',
    wiGain: amount => 'The change adds ' + amount,
    wiLoss: amount => 'The change costs ' + amount,
    wiDebtFree: (scenario, baseline) => 'Debt-free: ' + scenario + ' (as things are: ' + baseline + ')',
    wiNever: 'not within the horizon',
    wiDebtFreeSame: date => 'Debt-free: ' + date,
    wiNoDebt: 'no debts',
    wiInterest: amount => 'Interest saved: ' + amount,
    wiShort: date => 'Careful: at these amounts your savings drop below zero around ' + date + '.',
    wiLegendNow: 'as things are',
    wiLegendChange: 'with the change',
    wiHint: 'Move the overpayment or investing slider to see the difference.',
    wiHeadlineSame: (years, amount) => 'In ' + years + ', as things are: ' + amount,
    wiAxis: symbol => 'net worth at the end of each year, in thousands of ' + symbol,
    wiNote: 'A projection, not a forecast: income and spending stay at the level of your last three months, and the return is steady at the rate you set.',
    rcTitle: 'Your risk capital',
    rcNone: 'Your savings do not cover the cushion yet — trade only with what is left over each month',
    rcUnknown: 'Log your expenses and savings, and this works out a safe amount for trading.',
    rcWarning: share => 'Careful: this month trading has already taken ' + share + ' of your risk capital',
    rcSavings: 'savings',
    rcCushion: 'cushion',
    rcCushionSub: n => n + ' months of spending',
    rcRunway: 'without income',
    rcRunwayValue: n => n + (n === 1 ? ' month' : ' months'),
    rcRunwaySub: amount => 'at ' + amount + ' a month',
    rcTraded: 'trading this month',
    rcTradedSub: share => share + ' of risk capital',
    rcMonths: 'Cushion',
    rcMonthsOption: n => n + ' mo',
    rcUnconverted: list => 'Trades in ' + list + ' are left out — there is no exchange rate for them in Preferences.',
    rcNote: 'The cushion is money for living, not for trading: months of your average spending over the last three months. Only what the savings hold above it is risk capital. Backtests are never counted.',
    rcPropCheck: 'I trade on prop firm accounts',
    rcPropCheckSub: 'the firm’s capital takes the losses, not your savings — so no cushion is kept',
    rcPropTitle: 'Trading on prop firm accounts',
    rcPropSub: 'your savings are not at risk, only the fees — no cushion needed',
    rcPropMonth: (spent, back) => 'this month: fees ' + spent + ' · payouts ' + back,
    rcPropSavingsSub: 'not used for trading',
    rcPropFees: 'fees this month',
    rcPropFeesSub: 'challenges, resets, activations',
    rcPropBack: 'payouts this month',
    rcPropBackSub: 'payouts and refunds',
    rcPropTradedSub: 'on the firm’s capital',
    rcPropNote: 'On a prop firm account a losing day takes the firm’s money, not yours: what trading costs you is the fees, and what it pays is the payouts. Both come from the accounts under “Prop firms”. Trading on your own capital too? Untick the box and the cushion comes back.',
    propVerdictUp: amount => 'Prop trading pays for itself: ' + amount,
    propVerdictDown: amount => 'So far prop trading costs you ' + amount,
    propVerdictNone: 'Add your first prop account and log what it cost. Payouts go in the same place.',
    propVerdictNote: 'Every fee, reset and activation against every payout and refund, over all your accounts.',
    propCosts: 'spent',
    propCostsSub: 'fees, resets, activations',
    propReturns: 'got back',
    propReturnsSub: n => n + (n === 1 ? ' payout or refund' : ' payouts and refunds'),
    propRoi: 'return on fees',
    propPassed: 'passed',
    propPassedSub: (passed, failed) => passed + ' passed · ' + failed + ' failed',
    propPerPayout: 'fees per payout',
    propPerPayoutSub: 'what one payout has cost',
    propByFirm: 'By firm',
    propMixed: 'Your accounts use more than one currency, so the totals are left out.',
    propNew: 'New account',
    propFirm: 'firm, e.g. Apex',
    propProgram: 'program, e.g. 50K',
    propSize: 'size',
    propFee: 'fee paid',
    propAmount: 'amount',
    propAdd: 'ADD ACCOUNT',
    propFirmNeeded: 'Name the firm.',
    propAmountNeeded: 'Give an amount.',
    propStatus: { challenge: 'Challenge', phase2: 'Phase 2', funded: 'Funded', failed: 'Failed', closed: 'Closed' },
    pfHeroCap: 'PROP TRADING BALANCE · ALL ACCOUNTS',
    pfHeroDown: (amount, covered) => 'so far prop trading costs you ' + amount + ' — ' + (covered === null ? 'the first payout will change that' : 'payouts have covered ' + covered + '% of the fees'),
    pfHeroUp: amount => 'payouts have covered the fees and made ' + amount + ' on top',
    pfHeroEven: 'payouts have covered the fees to the cent',
    pfHeroNothing: 'no fees or payouts logged yet — add what the account cost',
    pfSpent: amount => 'spent ' + amount,
    pfBack: amount => 'recovered ' + amount,
    pfRoiLater: 'worked out after the first payout',
    pfPerPayout: amount => 'one payout has cost ' + amount + ' in fees',
    pfPassedSub: (passed, failed, active) => passed + ' funded · ' + failed + ' failed · ' + active + ' in progress',
    pfAccounts: 'ACCOUNTS',
    pfAccountsSub: 'costs go to Income & spend as “Fees” and payouts as income — unless you pick “only here”',
    pfNew: '+ NEW ACCOUNT',
    pfNewSub: 'firm, program and fee — the fee is logged straight away',
    pfNewTitle: 'NEW ACCOUNT',
    pfFirm: 'Firm', pfFirmPh: 'e.g. Apex',
    pfProgram: 'Program', pfProgramPh: 'e.g. 50K',
    pfSize: 'Account size', pfFee: 'Fee paid', pfCurrency: 'Currency',
    pfPopular: 'popular:',
    pfAdd: '+ ADD ACCOUNT',
    pfSince: day => 'since ' + day,
    pfNet: 'account balance',
    pfSettingsTitle: 'Rules and settings',
    pfRulesAdd: '+ ACCOUNT RULES',
    pfRulesAddSub: 'daily limit, drawdown and target — see how much room is left',
    pfBalance: 'ACCOUNT BALANCE',
    pfTarget: 'Profit target', pfTargetDone: '✓ target reached', pfTargetLeft: amount => amount + ' to go',
    pfDaily: 'Daily loss limit', pfLeft: amount => amount + ' left',
    pfDailySub: (today, limit) => 'today ' + today + ' of a ' + limit + ' limit',
    pfDd: 'Max drawdown', pfDdSub: (floor, type) => 'the account closes at ' + floor + ' · ' + type,
    pfSettings: 'RULES AND SETTINGS',
    pfDailyPh: 'e.g. 1250', pfDdPh: 'e.g. 2500', pfTargetPh: 'e.g. 3000',
    pfDelete: 'Delete account',
    pfEntries: 'FEES AND PAYOUTS',
    pfEntryCount: n => n + (n === 1 ? ' entry' : ' entries'),
    pfToFees: '→ budget: Fees', pfToIncome: '→ budget: income', pfOnlyHere: 'only here',
    pfRemoveEntry: 'Remove this entry',
    pfEntryAdd: '+ FEE / PAYOUT',
    pfDate: 'Date', pfAmount: 'Amount',
    pfToBudget: 'Add to budget', pfOnlyHereOption: 'Only here',
    pfLog: '+ ADD',
    propKind: { fee: 'Challenge fee', reset: 'Reset', activation: 'Activation', payout: 'Payout', refund: 'Fee refund' },
    propInLedger: 'in budget',
    propToLedger: { yes: 'add to budget', no: 'journal only' },
    propLog: 'ADD',
    propNoEvents: 'No fees or payouts logged on this account yet.',
    propDelete: 'DELETE ACCOUNT',
    propDeleteAsk: 'Delete this account with everything logged on it, including the entries it added to your budget? This cannot be undone.',
    propLedgerNote: 'Costs go to your expenses as Fees and payouts to your income, in the month they happened — unless you choose “journal only”.',
    adLabel: 'SPONSORED',
    adSettings: 'change',
    adConsentBody:
      'The free version carries one ad here. It sits in an isolated frame — the ad '
      + 'network cannot see this page, your figures or anything you have imported. '
      + 'May it use cookies to personalise what it shows you?',
    adConsentYes: 'ALLOW PERSONALISED',
    adConsentNo: 'NON-PERSONALISED ONLY',
    cashAmount: 'amount',
    cashNote:
      'Cash held as an allocation. No units and no purchase price — what a cash position '
      + 'loses is purchasing power, so that is what its gain column reports. A deposit or a '
      + 'savings account earns its way back: give it a rate and the interest counts, '
      + 'compounded yearly from the month you set the money aside.',
    /*
     * Said plainly because getting it wrong is not visible afterwards: interest
     * is taxed in most places and this app knows neither the rate nor the
     * regime, so it applies exactly the figure it is given.
     */
    cashYieldNote: 'Optional. Enter the rate the account actually pays you — net of tax, if it is taxed.',
    /*
     * Said before anything is typed, because the live sums underneath cannot
     * be: they appear once there are numbers to multiply, and the question is
     * what to put in the boxes in the first place.
     *
     * "cost" and "price" are near-synonyms in both languages, and nothing on
     * the form said which one meant then and which meant now. Somebody entered
     * what they had paid in total as the price of one share and got a 412%
     * gain that every figure downstream then agreed with.
     */
    holdNote:
      'Both figures are per unit, not for the whole position. Cost / unit is what you paid '
      + 'for one when you bought it; price / unit is what one is worth today — the gap '
      + 'between them is what the position has made or lost.',
    holdNoteTotal:
      'Only know the total? Divide it by the number of units — the sums below work '
      + 'themselves out as you type, so you can check it comes back to what you paid.',
    /*
     * Said out loud because the alternative was saying nothing at all. A field
     * that cannot be read as a number used to make the add button do nothing —
     * no row, no message, no clue — which reads as a broken button.
     */
    /*
     * What the server refused, said in the reader's language. Its own messages
     * are English sentences written for a log file and name the field by its
     * API spelling; neither belongs on somebody's screen.
     */
    apiRefused: 'The server would not accept that. Check the figures and try again.',
    apiRefusedField: field => `The server would not accept the “${field}” field — check that value.`,
    apiNotFound: 'That is not there any more — it may have been removed on another device.',
    apiConflict: 'That already exists.',
    apiForbidden: 'You are not allowed to do that.',
    /* 401 is the sign-in answer far more often than it is anything else. */
    apiUnauthorized: 'Wrong email or password.',
    apiWrongPassword: 'That is not your password.',
    apiRateLimited: 'Too many attempts — wait a moment and try again.',
    apiQuotesOff: 'Live pricing is switched off on this server.',
    apiQuotesDown: 'The price provider did not answer. Try again in a moment.',
    /* The free plan's limits, when the server refuses past one. */
    apiPlanImports: 'The free plan’s statement imports are used. Liquidity+ imports without a limit.',
    apiPlanBacktests: 'The free plan includes ' + FREE.backtestSessions + ' backtesting session, and it is in use. Liquidity+ has no limit — or delete the session to start another.',
    apiPlanProp: 'The free plan includes ' + FREE.propAccounts + ' prop firm account, and it is in use. Liquidity+ has no limit — or delete the account to add another.',
    apiPlanTaxes: 'Tax summaries are part of Liquidity+.',
    apiPlanJournal: 'The free plan includes ' + FREE.journalPerMonth + ' journal trades a month, and this month’s are used. Liquidity+ has no limit.',
    apiPlanQuotes: 'Automatic prices are part of Liquidity+. You can still type a price in by hand.',
    apiAiQuota: 'This month’s Liquidity+ suggestions are used. The allowance renews on the 1st.',
    /* A day written out in full, for a sentence: "15 January 2027". */
    dateFull: iso => {
      const date = new Date(iso);
      return date.getUTCDate() + ' ' + MONTHS_FULL_EN[date.getUTCMonth()] + ' ' + date.getUTCFullYear();
    },
    planSection: 'Plan',
    planStarts: date => 'Free and Liquidity+ begin on ' + date + '. Until then everything works without limits, as it does now.',
    planGiftAhead: date => 'From that day this account has Liquidity+ free until ' + date + ' — a thank-you to the people who were here first.',
    planTrial: date => 'Trial until ' + date + '. After that the account moves to Free on its own — nothing is charged.',
    planGift: date => 'A thank-you to the people who were here first: Liquidity+ free until ' + date + '.',
    planGranted: 'Liquidity+ granted to this account, with no end date.',
    planGrantedUntil: date => 'Liquidity+ granted to this account until ' + date + '.',
    planDemo: 'A demo shows everything Liquidity+ includes, except AI category suggestions.',
    planFreeNote: 'The full register of your finances, with no limit on entries.',
    planIncludes: 'Liquidity+: a journal with no monthly limit, unlimited backtesting and prop firm accounts, tax summaries (Poland, US, Germany, UK), automatic prices, AI category suggestions and the weekly review.',
    planSeePlans: 'SEE LIQUIDITY+',
    planUsedBacktests: (used, limit) => 'Backtesting sessions: ' + used + ' of ' + limit,
    planUsedProp: (used, limit) => 'Prop firm accounts: ' + used + ' of ' + limit,
    planBacktestsUsed: (used, limit) => 'Free plan — backtesting sessions: ' + used + ' of ' + limit + '.',
    planBacktestsSpent: 'The free plan includes one backtesting session. Liquidity+ has no limit; the session you have keeps working either way.',
    planPropUsed: (used, limit) => 'Free plan — prop firm accounts: ' + used + ' of ' + limit + '.',
    planPropSpent: 'The free plan includes one prop firm account. Liquidity+ has no limit; the account you have keeps working either way.',
    planTaxesTitle: 'Tax summaries',
    planTaxesLocked: 'The figures for your tax return, worked out from your journal: in Poland PIT-38, with every trade at the NBP rate of the business day before it, dividends with the foreign tax credit and losses from earlier years; in the USA Form 6781, your futures and index options split 60/40; in Germany Anlage KAP, in the UK SA108. Part of Liquidity+; your journal stays free.',
    planKeepData: 'Whatever the plan, your data stays yours: nothing is hidden or deleted when a plan ends.',
    planUsedImports: (used, limit) => 'Statement imports used: ' + used + ' of ' + limit,
    planUsedJournal: (used, limit) => 'Journal trades added this month: ' + used + ' of ' + limit,
    planImportsUsed: (used, limit) => 'Free plan — statement imports used: ' + used + ' of ' + limit + '.',
    planImportsSpent: 'The free plan’s statement imports are used. Liquidity+ imports without a limit.',
    planJournalUsed: (used, limit) => 'Free plan — trades added this month: ' + used + ' of ' + limit + '.',
    planJournalSpent: 'This month’s ' + FREE.journalPerMonth + ' trades are used. Liquidity+ has no monthly limit.',
    planQuotesNote: 'Automatic prices are part of Liquidity+. On Free, type the price in by hand — the valuation works the same way.',
    aiServerOn: (used, limit) => 'This month: ' + used + ' of ' + limit + ' descriptions used.',
    aiServerOff: 'Category suggestions are part of Liquidity+.',
    planRegionLabel: 'Prices for',
    planRegions: {
      'eu:PLN': 'European Union · złoty',
      'eu:EUR': 'European Union · euro',
      'global:USD': 'US, Canada, Australia and more · dollar',
      'global:GBP': 'United Kingdom · pound',
      'global:CHF': 'Switzerland · franc',
      'emerging:USD': 'Other countries · dollar'
    },
    planBuyMonth: amount => amount + ' / MONTH',
    planBuyYear: amount => amount + ' / YEAR',
    planYearNote: 'A year costs the same as eight months.',
    planTrialBuyNote: date => 'Subscribing now does not cut the trial short: the first payment is taken when it ends, on ' + date + '.',
    planTrialBuyNow: 'The trial ends in less than two days, so the first payment is taken straight away.',
    planRenewNote: 'The subscription renews automatically. You can cancel it at any time, effective at the end of the paid period. Payments are handled by Stripe — card details never reach our server.',
    planManage: 'MANAGE SUBSCRIPTION',
    planSubActive: (amount, interval, date) => 'Subscription: ' + amount + ' ' + (interval === 'year' ? 'a year' : 'a month') + '. Next payment on ' + date + '.',
    planSubTrialing: date => 'Subscription active. The first payment is taken on ' + date + '.',
    planSubCancelled: date => 'Subscription cancelled — Liquidity+ stays until ' + date + '.',
    planSubPastDue: 'The last payment did not go through. Update your card under “Manage subscription” to keep Liquidity+.',
    planNative: 'Liquidity+ can be bought on the website: liquidityraiders.pl/portfolio.',
    billingSuccess: 'Thank you — Liquidity+ is active.',
    billingPending: 'The payment is still being processed. Liquidity+ switches on as soon as Stripe confirms it.',
    billingCancel: 'Checkout cancelled — nothing was charged.',
    apiBillingClosed: 'Liquidity+ is not on sale yet.',
    apiBillingActive: 'This account already has a subscription — manage it in the Plan section.',
    apiBillingUnavailable: 'The payment provider could not be reached. Nothing was charged — try again in a moment.',
    apiBillingGuest: 'A demo cannot subscribe — create an account first.',
    apiBillingNone: 'This account has no subscription to manage.',
    apiAdminStripe: 'This account pays through Stripe — change its plan in the Stripe dashboard.',
    adminSaved: email => 'Plan saved for ' + email + '.',
    editIncomeTitle: 'Edit this income',
    editExpenseTitle: 'Edit this expense',
    editEntryNote:
      'The month is a field, not a fixture — filing something in the wrong month '
      + 'is the commonest mistake there is, and this is how it gets moved.',
    editFuelTitle: 'Edit this fill-up',
    editFuelNote:
      'The odometer counts twice: it is this reading and the baseline the next '
      + 'fill measures against, so a wrong digit reports two wrong economy figures.',
    editCarExpenseTitle: 'Edit this charge',
    editCarExpenseNote: 'The matching line in the ledger follows this one.',
    editTradeTitle: 'Edit this trade',
    editTradeNote:
      'A mistyped result moves the win rate and the followed-rules figure, which '
      + 'is what a journal is kept for.',
    monthNeeded: 'A month has to look like 2026-08.',
    dateNeeded: 'A date has to look like 2026-08-14.',
    monthLabel: 'month',
    dateLabel: 'date',
    categoryLabel: 'category',
    typeLabel: 'type',
    editHoldingTitle: 'Edit this position',
    editDebtTitle: 'Edit this debt',
    editDebtNote:
      'The balance is recorded for the month on screen; earlier months keep theirs. '
      + 'Leave the term empty to let the payoff be worked out from the instalment. '
      + 'Payments already logged are kept.',
    debtBalanceIn: m => 'balance · ' + m,
    tickerNeeded: 'A position needs a ticker.',
    classLabel: 'class',
    currencyLabel: 'currency',
    numberNeeded: 'Check the numbers — a quantity and a price are needed, and both must be figures.',
    amountNeeded: 'Check the numbers — an amount is needed, and it must be a figure.',
    pairNeeded: 'Enter the instrument — a trade cannot be saved without it.',
    rateNeeded: 'An exchange rate has to be a number above zero.',
    inflationNeeded: 'An inflation rate has to be a number — a minus sign is allowed, prices do fall.',
    labelNeeded: 'Give it a name and an amount.',
    balanceNeeded: 'A balance needs a three-letter currency code and an amount.',
    fuelNeeded: 'A fill-up needs an odometer reading and a volume, both as figures.',
    cashTitle: 'Cash position',
    cashRate: 'annual rate %',
    cashEdit: 'Rate and amount — tap to correct',
    cashNoYield: 'no interest',
    cashEarned: 'earned',
    inflationSection: 'Inflation',
    inflationNote: 'Annual rate per currency, used to value cash positions. Nothing is assumed: a currency with no rate here simply shows no erosion.',
    inflationMissing: 'no inflation rate',
    inflationHeld: (months, rate) => months + (months === 1 ? ' month' : ' months') + ' at ' + rate + '%',
    phInflation: 'CPI %',
    pricesConverted: n =>
      'Prices updated. ' + n + (n === 1 ? ' was' : ' were')
      + ' quoted in another currency and converted at your stored rate — check that rate if a figure looks off.',
    pricesNoRate: list =>
      'Not updated: ' + list + ' — the quote currency has no exchange rate on record. Add the rate, then refresh.',
    version: 'VERSION',
    versionNote: 'Quote this if you report something that looks wrong.',
    ai_unavailable: 'Claude could not be reached. Nothing was changed — try again in a moment.',
    ai_unconfigured: 'Category suggestions are not available on this server.',
    ai_plan: 'Category suggestions are part of Liquidity+.',
    ai_quota: 'This month’s Liquidity+ suggestions are used. The allowance renews on the 1st.',
    heldSince: 'Held since — the month this position starts counting',
    heldSinceTitle: 'MONTH OF PURCHASE',
    heldSinceNote: 'Pick the month this holding was bought in. It counts from that month on, and its price is fetched from Yahoo for every month since.',
    heldSinceNoteCash: 'Pick the month this money was set aside. Interest and inflation count from that month on.',
    trendScale: (low, high) => 'SCALE ' + low + ' – ' + high,
    /*
     * Shown only while a bar is in the future. The chart otherwise reads as
     * twelve equal measurements, and one of them would be a guess.
     */
    trendProjected: 'Months ahead are drawn at today’s prices — no price exists for them yet.',
    trendProjectedBar: 'This month has not happened yet. The asset price for it is not available, so the latest one is carried forward.',
    ratesBackfillMissed: list => 'No history available for ' + list + '.',
    pricesBackfilled: n =>
      'Filled in ' + n + (n === 1 ? ' monthly price.' : ' monthly prices.'),
    pricesBackfillNone: 'Nothing to fill in — every month already has its prices.',
    pricesBackfillMissed: list => 'No price history available for ' + list + '.',
    pricesNote: 'Prices are refreshed once a day. “Fill history” looks up what each holding was worth month by month, so a past month shows what it was worth then.',
    ratesMonthNote: month =>
      'A rate you enter is recorded against ' + month + ' and stays in force until you enter another, so closed months keep the rate they were valued at.',
    rateFrom: month => 'in force since ' + month,
    rateStanding: 'standing',
    rateDropMonth: 'Remove this rate',
    importTitle: 'Review before importing',
    importColumns: 'Columns',
    importField: {
      date: 'Date', label: 'Description', amount: 'Amount', currency: 'Currency'
    },
    importNoColumn: 'not in the file',
    importNote: base =>
      'Rows with no currency column are read as ' + base +
      '. Money out is negative, the way a bank writes it.',
    importRows: 'Rows',
    importInclude: 'Include this row',
    importUnusable: 'No date or no amount could be read from this row',
    importUnusableNote: n =>
      n + (n === 1 ? ' row was' : ' rows were') +
      ' left out: no date or no amount. Check the column mapping above.',
    importIn: 'IN',
    importOut: 'OUT',
    importFlip: 'Swap income and expense',
    importMore: n =>
      n + ' more ' + (n === 1 ? 'row is' : 'rows are') +
      ' not shown — they will be imported too.',
    importSummary: (included, bad, transfers = 0) =>
      included + ' selected' + (bad ? ', ' + bad + ' unusable' : '')
      + (transfers ? ', ' + transfers + (transfers === 1 ? ' own transfer' : ' own transfers') + ' left out' : ''),
    importCategory: 'Category',
    importNoCategory: 'none (Other)',
    /* Where a row's category came from, said beside it. */
    importSource: {
      file: 'from the file', rule: 'your rule', dictionary: 'dictionary', bank: 'the bank’s category', ai: 'AI', manual: 'set by you'
    },
    importTransfer: 'own transfer — left out',
    importDuplicate: 'already in your ledger — left out',
    importVoided: 'reverted or declined by the bank — left out',
    pushTitle: 'Payment reminders',
    pushNote: 'A notification on this phone or computer before a fixed cost, a subscription or a loan instalment is due — around 9 in the morning.',
    pushThisDevice: 'Reminders on this device',
    pushOnNote: n => (n === 1 ? 'on — this is the only device that gets them' : 'on — ' + n + ' devices get them'),
    pushOffNote: 'off — the browser asks for permission when you turn them on',
    pushOffBlocked: 'off — notifications are blocked for this site',
    pushWhen: 'When',
    pushLead: { 0: 'On the day', 1: 'A day before', 3: '3 days before' },
    pushWhat: 'About',
    pushBills: 'Fixed costs and subscriptions',
    pushDebts: 'Loan and card instalments',
    pushIncome: 'Income coming in',
    pushTest: 'SEND A TEST',
    pushPrivacy: 'Only charges with a day of the month set get a reminder. The notification travels through your browser maker’s push service, encrypted so that the service cannot read it.',
    pushGuest: 'Create an account to turn reminders on — the demo is only sample figures.',
    pushIosHome: 'On iPhone and iPad, add the app to the Home Screen first (Share → Add to Home Screen), open it from there and turn reminders on.',
    pushNative: 'Not in this app yet — open Portfolio Tracker in the browser on this device to turn reminders on.',
    pushNone: 'This browser cannot show notifications.',
    pushBlocked: 'Notifications are blocked for this site — allow them in the browser’s site settings, then turn reminders on.',
    pushFailed: 'The notification could not be delivered. Turn reminders off and on again on this device.',
    pushOn: 'Reminders are on for this device.',
    pushOff: 'Reminders are off for this device.',
    pushTestSent: 'Test sent — it should appear in a moment.',
    prefTestSent: '✓ SENT — CHECK THIS DEVICE',
    prefSections: {
      general: ['General', 'language, start screen and how figures show'],
      currencies: ['Currencies & rates', 'base currency, exchange rates and inflation'],
      finance: ['Finance', 'payoff strategy and cash'],
      reminders: ['Notifications', 'payment reminders'],
      ai: ['AI & privacy', 'what goes to Claude, and when'],
      account: ['Account & data', 'sign-in, a copy of your data, deleting the account'],
      plan: ['Plan', 'what your plan includes']
    },
    prefAutoSave: 'saves by itself',
    prefSaved: '✓ saved',
    prefBack: 'Back',
    prefVersion: v => 'version ' + v,
    prefCopy: 'copy',
    prefCopied: '✓ copied',
    prefClaim: 'Create an account',
    prefLinkDiscord: 'Link Discord',
    prefUnlinkDiscord: 'Unlink Discord',
    prefChangePw: 'Change password',
    prefAddPw: 'Add a password',
    prefYourData: 'Your data',
    prefLanguage: 'Language',
    prefLangSub: 'interface, dates and number formats',
    prefStartSub: 'what you see after signing in',
    prefBaseSub: 'all totals are shown in it · amounts you entered are not converted',
    prefRatesTitle: 'Rates and inflation',
    prefRatesSub: (base, month) => 'how many ' + base + ' one unit of the currency costs · a rate holds from ' + month + ' until the next one, so closed months keep theirs',
    prefColCurrency: 'CURRENCY',
    prefColRate: 'RATE',
    prefColInflation: 'INFLATION Y/Y',
    prefColHistory: 'RECENT MONTHS',
    prefBase: 'base',
    prefNoRate: 'no rate yet',
    prefRemoveCurrency: 'Remove the rate',
    prefAddCurrency: 'Add a currency:',
    prefInflationNote: 'Inflation is optional — it only shows how cash loses buying power. With no rate entered, nothing is assumed.',
    prefStrategySub: 'sets the queue on the Debt tab and the hints on the Overview',
    prefStrategyRateSub: 'least interest overall',
    prefStrategyBalanceSub: 'positions disappear sooner',
    prefCashTitle: 'Savings · cash',
    prefCashSub: 'one balance per currency · counted in “Where the money sits” and the risk capital',
    prefAddBalance: '+ BALANCE',
    prefFixedTitle: 'Fixed costs',
    prefFixedSub: 'subscriptions and rent live under Income & spend',
    prefCsvTitle: 'CSV import and export',
    prefCsvSub: 'beside the entries they fill, under Income & spend',
    prefPushWhen: 'When · around 9:00',
    prefAiTitle: 'Category suggestions',
    prefAiSub: 'on the import screen, “Suggest categories” asks Claude about the rows the dictionary does not know',
    prefAiUsed: 'used this month',
    prefAiOf: (used, limit) => used + ' / ' + limit + ' descriptions',
    prefAiSend: 'WE SEND',
    prefAiSendBody: 'only the transaction descriptions',
    prefAiNever: 'NEVER',
    prefAiNeverBody: 'amounts, dates or account details',
    prefAiNote: 'Claude may look an unfamiliar name up on the web. Suggestions are only proposals — nothing is saved until you press “Import”.',
    prefAiOff: 'Not offered on this server.',
    prefReviewTitle: 'Weekly review',
    prefReviewSub: 'also written by Claude — only when you ask, on the Overview',
    prefSyncedEntries: n => 'SYNCED · ' + n + (n === 1 ? ' ENTRY' : ' ENTRIES'),
    prefLinkDiscordSub: 'then sign in with either one',
    prefUnlinkSub: 'you will then sign in with email and password only',
    prefChangePwSub: 'your current password and a new one',
    prefAddPwSub: 'so you can sign in without Discord too',
    prefAdminSub: 'counts and accounts — only for you',
    prefSignOutSub: 'on this device',
    prefClaimSub: 'keep this demo as an account of your own',
    prefDataSub: 'download a copy of everything, or delete the account — deleting cannot be undone',
    prefDownload: '↓ DOWNLOAD A COPY (JSON)',
    prefMenuPrefs: 'Preferences',
    prefMenuAdmin: 'Admin panel',
    prefMenuSignOut: 'Sign out',
    prefAccountMenu: 'Account menu',
    prefCardLine: (n, plan) => '✓ ' + n + (n === 1 ? ' entry' : ' entries') + ' · ' + plan,
    prefCardSyncing: 'syncing…',
    prefCardOffline: n => 'offline' + (n ? ' · ' + n + ' waiting' : ''),
    prefPlanGift: 'GIFT',
    prefPlanTrial: 'TRIAL',
    prefPlanGiftLine: date => 'for those with us from the start · until ' + date,
    prefPlanTrialLine: date => 'trial until ' + date,
    prefPlanSubLine: 'subscription · manage it here',
    prefPlanGrantLine: date => (date ? 'until ' + date : 'granted'),
    prefPlanFreeLine: 'see what Liquidity+ adds',
    prefPlanSoonLine: date => 'plans start on ' + date,
    prefSumRates: n => n + (n === 1 ? ' rate' : ' rates'),
    prefSumNoCash: 'no balances',
    prefSumCash: n => n + (n === 1 ? ' balance' : ' balances'),
    prefOn: 'on',
    prefOff: 'off',
    importShowAll: n => 'SHOW ALL ' + n,
    importLearnNote:
      'A category you change is remembered for that payee, and the next statement is sorted the same way.',
    importConfirm: n => 'IMPORT ' + n,
    /*
     * `duplicates` is not a failure and does not read as one: the rows were
     * already in the ledger, which is the answer somebody re-importing a
     * statement wanted. Saying so is what stops the same file being imported a
     * third time to check.
     */
    importDone: (imported, skipped, duplicates) =>
      'Imported ' + imported + (imported === 1 ? ' entry' : ' entries') +
      (duplicates ? ', ' + duplicates + ' already there' : '') +
      (skipped ? ', skipped ' + skipped : '') + '.',
    importNothing: 'No rows are selected.',
    importUnreadable: 'That file could not be read.',
    importEmpty: 'That file has no rows in it.',
    dataSection: 'Statements',
    dataNote: month =>
      'Bring in a CSV statement from your bank — the transactions are imported into the current month, '
      + month + '. You check every row before anything is saved.',
    dataMoved: tab => 'CSV import and export are on the ' + tab + ' tab now, beside the entries they fill.',
    /* A month written out in full, for a sentence rather than a column. */
    monthFull: key => monthIn(MONTHS_FULL_EN, key) + ' ' + String(key).slice(0, 4),
    importMonthTitle: 'Month',
    importMonthViewed: month => 'Current month: ' + month,
    importMonthDated: 'By the dates in the file',
    importMonthViewedNote: month => 'Every selected row is imported into the current month, ' + month + '.',
    importMonthDatedNote:
      'Each row goes into the month of its own date — a statement covering several months is spread across them.',
    importMonthOutside: (n, months) =>
      n + (n === 1 ? ' row is' : ' rows are') + ' dated in another month (' + months + '). '
      + 'If the statement covers several months, choose “By the dates in the file”.',
    importFrom: month => 'dated ' + month + ' in the file',
    dataImport: 'IMPORT CSV',
    dataExport: 'EXPORT CSV',
    exportEmpty: 'There is nothing to export yet.',
    exportDone: n => 'Wrote ' + n + (n === 1 ? ' row.' : ' rows.'),
    recurring: 'Fixed costs',
    recurringNote: 'Rent, salary, insurance, subscriptions — anything that comes back. '
      + 'Set one up once, and add it with a single tap when it is due.',
    recurringAdd: 'ADD',
    recurringPost: n => 'ADD ' + n + (n === 1 ? ' ENTRY' : ' ENTRIES'),
    /* The heading a fixed cost gets on the cashflow screen, where it is about
       subscriptions far more often than about rent. */
    fixedCosts: 'Fixed costs',
    fixedCostsNote: 'What repeats: subscriptions, rent, insurance, salary.',
    fixedEmpty: 'Nothing repeating yet. Add a subscription or a bill and it will wait here for you.',
    fixedCommitted: 'COMMITTED / MONTH',
    fixedIncoming: 'RECURRING INCOME',
    /* Stated because it is not obvious: a yearly bill is shown as a twelfth
       here, while the entry it posts is the whole amount in its own month. */
    fixedAveraged: 'Yearly and quarterly costs are shown here as a monthly share. '
      + 'The entry itself is added in full, in the month it is charged.',
    fixedShare: pct => pct + '% of this month\u2019s income',
    fixedNoIncome: 'no income recorded this month',
    fixedThisMonth: 'CHARGED THIS MONTH',
    fixedNextCharge: month => 'next: ' + month,
    fixedNoNext: 'not charged again',
    fixedOnDay: day => 'on the ' + day + ordinalSuffix(day),
    fixedUnconverted: n => n + (n === 1 ? ' cost is' : ' costs are')
      + ' in a currency with no rate, and not in this total.',
    cadence: { 1: 'monthly', 3: 'quarterly', 6: 'twice a year', 12: 'yearly' },
    cadenceShort: { 1: 'MO', 3: '3MO', 6: '6MO', 12: 'YR' },
    fixedEvery: 'repeats',
    fixedFrom: 'first charged',
    fixedUntil: 'ends after',
    fixedUntilNone: 'runs until you stop it',
    editFixedTitle: 'Edit fixed cost',
    upcoming: 'Coming up',
    upcomingNote: days => 'Fixed costs falling due in the next ' + days + ' days.',
    upcomingLeaving: 'LEAVING THE ACCOUNT',
    upcomingEmpty: 'Nothing due in the next few weeks. Anything repeating shows up here '
      + 'before it is charged, not after.',
    upcomingToday: 'today',
    upcomingTomorrow: 'tomorrow',
    upcomingInDays: n => 'in ' + n + ' days',
    /* A charge whose day was never recorded: the month is all that is known. */
    upcomingSomeTime: 'day not set',
    /* Already written into the ledger. The money still leaves on the day. */
    upcomingPosted: 'in the ledger',
    fixedMoved: 'Subscriptions, rent and anything else that repeats now live with your '
      + 'income and spending, next to the money they come out of.',
    recurringPosted: n => 'Added ' + n + (n === 1 ? ' entry.' : ' entries.'),
    recurringNone: 'Nothing left to add this month.',
    recurringAgainTitle: 'Fixed costs already added',
    recurringAgainQuestion: 'Do you want to add the fixed costs to this month’s records again?',
    recurringAgainNote: (n, key) => 'They are already in the records for ' + monthIn(MONTHS_FULL_EN, key) + ' '
      + String(key).slice(0, 4) + '. Confirming adds them a second time: ' + n + (n === 1 ? ' entry.' : ' entries.'),
    recurringAgainYes: 'YES, ADD AGAIN',
    /* Said under the dash rather than left as a dash: one fill is a fill that
       was saved, not a fill that went missing. */
    /*
     * Said because the alternative is silence about work that has not been
     * saved yet. Entered offline, shown as recorded, and still on its way.
     */
    queueHeld: 'Your session ended. Changes you made offline are kept — sign in again to send them.',
    queueRestored: 'Changes you made offline are on their way.',
    economyNeedsTwo: 'consumption shows after the second full tank',
    economyNoFills: 'no fill-ups yet',
    recurringPaused: 'paused',
    recurringResume: 'RESUME',
    recurringPause: 'PAUSE',
    undoDeleted: kind => kind + ' deleted',
    paymentLogged: name => 'Payment logged: ' + name,
    undo: 'UNDO',
    removeLabel: 'Remove',
    undoKinds: {
      income: 'Income', expense: 'Expense', debt: 'Debt',
      holding: 'Holding', fuel: 'Fill-up', service: 'Service record',
      recurring: 'Fixed cost', trade: 'Trade'
    },
    optBaseCurrency: 'Base currency',
    optBaseCurrencyNote: 'Every total is shown in this currency. Amounts you already entered are not rewritten.',
    savings: 'Savings',
    savingsNote: 'One balance per currency you hold.',
    addBalance: 'ADD BALANCE',
    rates: 'Exchange rates',
    ratesNote: n => 'How many ' + n + ' one unit of each currency buys.',
    rateRefresh: 'FETCH RATES',
    rateManual: 'manual',
    ratesUpdated: 'Rates updated.',
    ratesPartial: missed => 'Updated. No rate for: ' + missed,
    ratesAuto: codes => 'Exchange rate added for ' + codes + ' — the amounts count in your totals now. You can change it in Preferences.',
    prRules: 'RULES',
    prRulesNone: 'Set the daily loss limit, the drawdown and the target, and this card keeps track of how much room is left.',
    prSetRules: 'SET RULES',
    prEditRules: 'Change the rules',
    prDaily: 'Daily loss limit',
    prMaxDd: 'Maximum drawdown',
    prDdType: 'Drawdown',
    prDdTypes: { static: 'Static', eod: 'Trailing, end of day', intraday: 'Trailing, intraday' },
    prTarget: 'Profit target',
    prSave: 'SAVE RULES',
    prToday: 'TODAY',
    prTodayVal: (room, limit) => room + ' left of ' + limit,
    prTodaySub: pnl => 'today ' + pnl,
    prDrawdown: 'DRAWDOWN',
    prDdVal: room => room + ' above the floor',
    prDdSub: (floor, type) => 'floor ' + floor + ' · ' + type,
    prTargetLab: 'TARGET',
    prTargetVal: (pnl, target) => pnl + ' of ' + target,
    prTargetSub: pct => pct + '% of the way',
    prBreached: 'A limit is breached — at most firms that fails the account. Check with the firm.',
    prWarning: 'Less than a fifth of the room is left on a limit — careful today.',
    prReached: 'Profit target reached.',
    prNoTrades: 'Pick this account when logging a trade in the journal, and its balance and limits are worked out here.',
    prBalance: (balance, n) => 'balance ' + balance + ' · ' + n + (n === 1 ? ' trade' : ' trades'),
    prTrailNote: 'A trailing drawdown follows the highest balance and stops at the starting balance, as at most firms.',
    jAccount: 'Account',
    jOwnAccount: 'Own money',
    bgTitle: 'Budgets',
    bgSub: month => 'monthly limits · ' + month,
    bgEmpty: 'No limits yet. Set one for a category below and see how much of it is left as the month goes on.',
    bgOf: (a, b) => a + ' of ' + b,
    bgLeft: (left, pct) => left + ' left · ' + pct + '% used',
    bgOver: amount => amount + ' over the limit',
    bgTotal: (spent, limit) => 'Categories with a limit: ' + spent + ' of ' + limit,
    bgOverCount: n => n + (n === 1 ? ' category over' : ' categories over'),
    bgCategory: 'Category',
    bgLimit: code => 'Monthly limit (' + code + ')',
    bgAdd: 'SET LIMIT',
    bgSave: 'SAVE',
    bgEdit: 'Change the limit',
    bgRemove: 'Remove the limit',
    glTitle: 'Savings goals',
    glSub: 'what you are saving for, and when it will be there',
    glEmpty: 'An emergency fund, a holiday, a car — add a goal and see when you will reach it at your pace.',
    glDone: '✓ reached',
    glReady: month => 'ready in ' + month,
    glOnTrack: month => 'on track for ' + month,
    glNeeds: (amount, month) => amount + ' a month needed to make ' + month,
    glMissed: month => 'the deadline (' + month + ') has passed',
    glNeedsFrom: (amount, month) => amount + ' a month would make ' + month,
    glNoPace: 'add a monthly amount to see when you will get there',
    glPace: amount => amount + ' a month',
    glName: 'Goal',
    glNamePh: 'e.g. Emergency fund',
    glTarget: 'Target',
    glSaved: 'Saved so far',
    glMonthly: 'Each month',
    glDeadline: 'By',
    glDeadlineNote: 'the month you want it by (optional)',
    glNoDeadline: 'NO DEADLINE',
    glAdd: 'ADD GOAL',
    glSaveChanges: 'SAVE CHANGES',
    glNew: '+ NEW GOAL',
    glEdit: 'Change this goal',
    glRemove: 'Delete this goal',
    missingRateTitle: 'Missing exchange rate',
    missingRateBody: codes =>
      'Amounts in ' + codes + ' are left out of every total until a rate is set.',
    setRates: 'SET RATES',
    amount: 'amount',
    a11yBalance: code => 'Savings in ' + code, a11yBalanceCurrency: 'Currency of the new balance', a11yBalanceAmount: 'Amount of the new balance',
    a11yRate: code => 'Rate for ' + code, a11yOtherCurrency: 'Other currency',
    rate: 'rate',
    optUnits: 'Vehicle units',
    editVehicle: 'Edit vehicle',
    vehicleName: 'vehicle',
    odometer: 'odometer',
    startOdometer: 'odometer at purchase',
    saveVehicle: 'SAVE VEHICLE',
    vehicleSaved: 'Vehicle updated.',
    odometerOrder: 'The purchase reading cannot be higher than the current one.',
    sheetDebt: 'Debt',
    sheetHolding: 'Holding',
    sheetService: 'Service',
    sheetVehicle: 'Vehicle',

    ariaPrevMonth: 'Previous month',
    ariaNextMonth: 'Next month',
    ariaLanguage: 'Change language',

    fuelFull: 'FULL TANK',
    fuelPartial: 'PARTIAL FILL',
    fuelFullNote: 'Consumption is measured between two full tanks. Switch this off for a top-up: its litres are counted with the next full tank.',
    fuelPartialShort: 'partial',
    fillTooBig: tank => 'That is more than the tank holds (' + tank + ') — check the figure, or the tank capacity under the vehicle.',
    tankLabel: 'tank capacity',
    rangeTitle: 'RANGE',
    rangeSub: tank => 'on a full ' + tank + ' tank',
    vinLabel: 'VIN',
    vinInvalid: 'A VIN is 17 letters and digits, without I, O or Q.',

    emptyTitle: 'Your ledger is empty',
    emptyBody: 'Add what you earn and spend under Income & spend. Debts, holdings and the car each have their own tab.',
    emptyList: 'Nothing here yet',
    nothingToFlag: 'Everything looks fine.'
  }
};

const PL = {
  brandKicker: 'REJESTR OSOBISTY',
  desk: 'PULPIT',
  language: 'JĘZYK',
  savedLocally: 'ZSYNCHRONIZOWANO',
  entries: 'wpisy',
  trackedSince: 'od',
  netWorth: 'WARTOŚĆ NETTO',
  incomeMonth: 'PRZYCHÓD · MIESIĄC',
  spendMonth: 'WYDATKI · MIESIĄC',
  savedMonth: 'OSZCZĘDNOŚCI · MIESIĄC',
  debtOutstanding: 'DŁUG DO SPŁATY',
  netTrend: 'Trend wartości netto',
  assetsMinusLiab: 'AKTYWA − ZOBOWIĄZANIA',
  whereMoney: 'Gdzie leżą pieniądze',
  invested: 'zainwestowane',
  unrealised: 'niezrealizowane',
  spendByCat: 'Wydatki według kategorii',
  attention: 'Uwaga',
  incomeCap: 'PRZYCHÓD',
  expensesCap: 'WYDATKI',
  savingsRateCap: 'STOPA OSZCZĘDNOŚCI',
  income: 'Przychody',
  expenses: 'Wydatki',
  add: 'DODAJ',
  outstanding: 'POZOSTAŁO',
  paidOff: 'SPŁACONO',
  monthlyServicing: 'RATY MIESIĘCZNE',
  interestYear: 'ODSETKI · ROK',
  payoffQueue: 'Kolejka spłat',
  logPayment: 'ZAPISZ WPŁATĘ',
  addDebt: 'DODAJ DŁUG',
  apr: 'OPROCENTOWANIE',
  marketValue: 'WARTOŚĆ RYNKOWA',
  costBasis: 'KOSZT ZAKUPU',
  unrealisedCap: 'NIEZREALIZOWANE',
  returnCap: 'STOPA ZWROTU',
  thHolding: 'WALOR',
  thClass: 'KLASA',
  thUnits: 'SZTUKI',
  thPrice: 'CENA',
  thValue: 'WARTOŚĆ',
  thPl: 'WYNIK',
  allocByClass: 'Podział według klas',
  refreshPrices: 'ODŚWIEŻ CENY',
  backfillPrices: 'UZUPEŁNIJ HISTORIĘ',
  vehicleCap: 'POJAZD',
  fuelUpkeep: 'paliwo + utrzymanie',
  avgEconomy: 'ŚREDNIE SPALANIE',
  spentTotal: 'WYDANO · RAZEM',
  fuelLog: 'Rejestr tankowań',
  fillUp: 'TANKOWANIE',
  fuelEntry: 'Tankowanie',
  log: 'DODAJ',
  serviceOwnership: 'Serwis i utrzymanie',
  serviceKicker: 'SERWIS · UBEZPIECZENIE · PODATEK',
  thDate: 'DATA',
  thOdo: 'LICZNIK',
  thCost: 'KOSZT',
  phSource: 'źródło',
  phAmount: 'kwota',
  phLender: 'bank / pożyczkodawca',
  phBalance: 'saldo',
  phOriginal: 'kwota pierwotna',
  phApr: 'oprocentowanie %',
  phPayment: 'rata',
  phTerm: 'miesięcy',
  phFees: 'prowizja',
  phTicker: 'ticker',
  phName: 'nazwa',
  phUnits: 'sztuki',
  phAvgCost: 'koszt/szt.',
  phYield: '% rocznie',
  phBoughtOn: 'data zakupu',
  phDay: 'dzień',
  phFromMonth: 'od',
  phUntilMonth: 'do',
  phPrice: 'cena/szt.',
  phOdometer: 'licznik',
  phCost: 'koszt',
  phNote: 'notatka',
  nav: { overview: 'Przegląd', cash: 'Przychody i wydatki', debt: 'Długi', invest: 'Inwestycje', car: 'Pojazd', journal: 'Trading', tax: 'Podatki' },
  pages: {
    overview: ['PANEL', 'Przegląd finansów'],
    cash: ['PRZEPŁYWY', 'Przychody i wydatki'],
    debt: ['ZOBOWIĄZANIA', 'Długi i spłaty'],
    invest: ['AKTYWA', 'Inwestycje'],
    car: ['UTRZYMANIE', 'Koszty eksploatacji pojazdu'],
    journal: ['NARZĘDZIA TRADERA', 'Trading'],
    tax: ['ZEZNANIE', 'Podatki']
  },
  cat: CAT_PL,
  cls: {
    'Equity / ETF': 'Akcje / ETF', Crypto: 'Krypto', Retirement: 'Konta emerytalne',
    'Bonds / cash': 'Obligacje', 'Real estate': 'Nieruchomości',
    'Precious metals': 'Metale szlachetne', Cash: 'Gotówka'
  },
  /* Zakładka Podatki: osobny słownik dla każdego kraju i to, co wspólne. */
  tax: {
    head: 'ROZLICZENIE PODATKOWE',
    headSub: 'wybierz kraj, w\u00a0którym się rozliczasz',
    ready: 'GOTOWE',
    fresh: 'NOWE',
    countries: {
      pl: ['Polska', 'PIT-38'],
      us: ['USA · futures', 'Form 6781 · §1256 · 60/40'],
      us8949: ['USA · akcje', 'Form 8949 · Schedule D'],
      de: ['Niemcy', 'Anlage KAP · 25% + Soli'],
      uk: ['Wielka Brytania', 'SA108 · Capital Gains Tax']
    },
    year: 'ROK PODATKOWY',
    journal: 'Uwzględnij dziennik',
    journalCount: (n, label) => n + ' tr. w\u00a0' + label,
    propExcluded: n => 'Pominięto ' + n + ' ' + pl(n, 'transakcję', 'transakcje', 'transakcji') + ' z\u00a0kont prop. Konto należy do firmy, więc te transakcje nie są Twoim zyskiem kapitałowym (nie trafiają do PIT-38) — opodatkowana jest wypłata, jako przychód innego rodzaju. Wypłaty zapisuj na ekranie konta prop, a\u00a0trafią do przychodów.',
    nbp: 'PRZELICZ PO KURSACH NBP',
    nbpDone: '✓ KURSY NBP',
    nbpBusy: 'PRZELICZAM…',
    nbpTitle: 'kurs średni NBP z\u00a0ostatniego dnia roboczego przed transakcją',
    fromJournal: 'Z DZIENNIKA',
    journalOff: 'Włącz „Uwzględnij dziennik”, żeby policzyć ten koszyk.',
    noTrades: 'brak transakcji z\u00a0tego okresu w\u00a0dzienniku',
    futText: (n, gains, losses) => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + ': zyski ' + gains + ', straty ' + losses + ' · kursy z\u00a0Preferencji',
    unconverted: n => n + ' ' + pl(n, 'transakcja jest', 'transakcje są', 'transakcji jest') + ' w\u00a0walucie bez kursu z\u00a0jej miesiąca, więc ' + (n === 1 ? 'została pominięta' : 'zostały pominięte') + '. Dodaj kurs w\u00a0Preferencjach.',
    income: 'dochód',
    result: 'wynik',
    gross: 'brutto',
    pl: {
      heroCap: next => 'DO ZAPŁATY Z\u00a0PIT-38 · DO 30 KWIETNIA ' + next,
      heroDue: next => 'przelej na mikrorachunek podatkowy do 30.04.' + next,
      heroNone: 'nic do zapłaty — złóż PIT-38, jeśli była jakakolwiek sprzedaż',
      partSec: 'Papiery i\u00a0pochodne',
      partSecSub: v => 'podstawa ' + v,
      partCrypto: 'Kryptowaluty',
      partCryptoGain: v => 'dochód ' + v,
      partCryptoCarry: v => 'nadwyżka kosztów ' + v,
      partDiv: 'Dywidendy',
      partDivSub: v => 'pobrano ' + v + ' za granicą',
      secTitle: 'Akcje, ETF-y i\u00a0instrumenty pochodne',
      secSub: 'część C · razem z\u00a0kontraktami terminowymi i\u00a0CFD',
      secLoss: (a, b) => 'strata — odliczysz w\u00a0latach ' + a + '–' + b,
      jText: (n, income, cost, nbp) => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + ': przychód ' + income + ', koszty ' + cost
        + (nbp ? ' · kurs NBP z\u00a0dnia poprzedzającego' : ' · kursy z\u00a0Preferencji — przelicz po NBP przed złożeniem'),
      p8Income: 'przychód z\u00a0PIT-8C (polski broker)',
      p8Cost: 'koszty z\u00a0PIT-8C',
      secIncome: 'inny przychód (zagraniczny broker)',
      secCost: 'inne koszty: zakup, prowizje',
      secNote: 'Polski broker wystawia PIT-8C — przepisz jego kwoty wprost. Zagraniczny go nie wystawia: wpisz sumy z\u00a0jego rocznego raportu.',
      cryptoTitle: 'Kryptowaluty',
      cryptoSub: 'część E · osobny koszyk, nie łączy się ze stratami z\u00a0akcji',
      cryptoIncome: 'przychód (sprzedaż za pieniądze lub towary)',
      cryptoCost: year => 'koszty z\u00a0' + year,
      cryptoCarried: 'nadwyżka kosztów z\u00a0lat ubiegłych',
      cryptoCarryLab: next => 'nadwyżka kosztów na ' + next,
      cryptoCarryNote: (v, next) => 'Wpisz ' + v + ' jako nadwyżkę kosztów w\u00a0zeznaniu za ' + next + '.',
      divTitle: 'Dywidendy zagraniczne',
      divSub: 'część G · 19% minus podatek pobrany za granicą',
      divLab: 'do dopłaty',
      divGross: 'dywidendy brutto, w\u00a0złotych',
      divWithheld: 'podatek pobrany za granicą',
      divOver: v => 'Pobrano więcej niż 19% — odliczysz tylko ' + v + '. Nadwyżkę odzyskasz u\u00a0zagranicznego płatnika.',
      divUs: 'Z USA przy W-8BEN pobiera się zwykle 15% — w\u00a0Polsce dopłacasz 4%.',
      lossTitle: 'Straty z\u00a0lat ubiegłych',
      lossSub: 'akcje i\u00a0pochodne · odliczane od najstarszej, bo wygasają po 5 latach',
      lossYear: year => 'odliczysz w\u00a0' + year,
      lossAmount: 'strata',
      lossUsed: 'już odliczono',
      lossLast: 'ostatni rok!',
      lossUntil: year => 'do ' + year,
      lossHalf: 'do 50%',
      lossHalfTitle: 'w\u00a0jednym roku najwyżej połowa straty',
      lossOnce: 'jednorazowo',
      lossOnceTitle: 'do 5 mln zł naraz',
      lossDeduct: v => 'odliczysz ' + v,
      lossNoIncome: 'brak dochodu do odliczenia',
      lossLeft: v => 'zostaje ' + v,
      lossLapses: v => 'przepada ' + v,
      lossDone: '✓ rozliczona',
      lossNote: 'Co roku możesz odliczyć do 50% danej straty. Jednorazowo — do 5 mln zł, raz na stratę; resztę i\u00a0tak odliczasz po 50%.',
      formTitle: 'Do przepisania w\u00a0PIT-38',
      formSub2025: 'pełne złote · numery pól wzoru PIT-38(18) za 2025 r.',
      formSubOther: 'pełne złote · według części — numery pól zmieniają się z\u00a0każdym wzorem',
      field: n => 'poz. ' + n,
      part: p => 'część ' + p,
      lines: {
        p8Income: 'Przychód z\u00a0PIT-8C', p8Cost: 'Koszty z\u00a0PIT-8C',
        otherIncome: 'Inne przychody', otherIncomeSub: 'dziennik i\u00a0zagraniczni brokerzy', otherCost: 'Inne koszty',
        income: 'Przychód razem', cost: 'Koszty razem', gain: 'Dochód', loss: 'Strata',
        losses: 'Straty z\u00a0lat ubiegłych', base: 'Podstawa obliczenia podatku', tax: 'Podatek 19%', due: 'Podatek należny',
        cIncome: 'Przychód z\u00a0kryptowalut', cCost: 'Koszty kryptowalut', cCostSub: 'z\u00a0tego roku i\u00a0lat ubiegłych', cGain: 'Dochód z\u00a0kryptowalut',
        cCarry: 'Nadwyżka kosztów na kolejny rok', cTax: 'Podatek należny od kryptowalut',
        dTax: 'Zryczałtowany podatek od dywidend', dCredit: 'Podatek zapłacony za granicą', dDue: 'Różnica do zapłaty',
        total: 'Razem do zapłaty'
      },
      disclaimer: 'Wypłaty z\u00a0prop firm nie trafiają do PIT-38 — to zwykle przychód z\u00a0innych źródeł lub działalności, skonsultuj z\u00a0księgowym. Szacunek pomaga wypełnić zeznanie, ale nie jest doradztwem podatkowym; sprawdź kwoty z\u00a0PIT-8C i\u00a0wyciągami brokera.'
    },
    us: {
      heroCap: (year, next) => 'SZACOWANY PODATEK · ROK ' + year + ' · TERMIN 15 KWIETNIA ' + next,
      heroLoss: v => 'strata ' + v + ' — brak podatku, zobacz carryback',
      heroSub: (v, blended, state) => 'z\u00a0wyniku ' + v + ' · stawka łączna ' + blended + (state ? ' + stan ' + state + '%' : ''),
      partLt: '60% długoterminowo',
      partSt: '40% krótkoterminowo',
      partAll: 'Gdyby wszystko krótko',
      partSave: v => 'oszczędzasz ' + v,
      rateTitle: 'Twoja stawka',
      rateSub: 'próg podatku federalnego — od niego zależą obie części 60/40',
      other: 'Inne kontrakty z\u00a01099-B',
      otherHint: 'pole 11 — konta spoza dziennika',
      mtm: 'Korekta otwartych pozycji',
      mtmHint: 'wycena na 31 grudnia (mark-to-market)',
      state: 'Podatek stanowy',
      stateHint: 'opcjonalnie, np. 4,95 w\u00a0Illinois',
      splitTitle: 'Podział 60 / 40',
      splitSub: 'bez względu na czas trzymania kontraktu',
      lt: r => 'long-term ' + r + '%',
      st: r => 'short-term ' + r + '%',
      blended: 'STAWKA ŁĄCZNA',
      blendedSub: r => 'zamiast ' + r + '% krótkoterminowo',
      save: 'OSZCZĘDZASZ',
      saveSub: 'wobec zwykłego zysku krótkoterminowego',
      instTitle: 'Instrumenty z\u00a0dziennika',
      instSub: 'zaznaczone to kontrakty z\u00a0sekcji 1256 — dotknij, żeby zmienić',
      yes: '§1256',
      no: 'NIE §1256',
      trades: n => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji'),
      pickNote: 'Znane kontrakty terminowe i\u00a0opcje na indeksy są zaznaczone. Odznacz akcje, ETF-y, kryptowaluty spot i\u00a0forex — to nie są kontrakty z sekcji 1256. Transakcje zapisane na koncie prop są pominięte automatycznie.',
      noTrades: year => 'Brak realnych transakcji w\u00a0dzienniku za ' + year + ' r.',
      formTitle: 'Form 6781 · część I',
      formSub: year => 'kontrakty §1256, rok ' + year,
      line: n => 'linia ' + n,
      lines: {
        inst: root => root + ' — futures', instSub: n => 'z\u00a0dziennika · ' + n + ' tr.',
        other: 'Pozostałe z\u00a01099-B', mtm: 'Korekta mark-to-market', net: 'Wynik netto §1256',
        st: '40% krótkoterminowo', stSub: '→ Schedule D, linia 4', lt: '60% długoterminowo', ltSub: '→ Schedule D, linia 11'
      },
      notes: {
        noLots: 'Bez dopasowywania lotów i\u00a0reguły wash sale — liczy się wynik roku, więc wystarczy dziennik.',
        which: 'Dotyczy kontraktów regulowanych (ES, MES, NQ, MNQ, CL…). CFD i\u00a0forex spot tu nie wchodzą.',
        lossNow: 'Strata netto: możesz ją przenieść 3 lata wstecz na zyski §1256 (pole D w\u00a0Form 6781) albo do przodu.',
        lossLater: 'Stratę netto §1256 można przenieść 3 lata wstecz — przyda się w\u00a0słabszym roku.',
        approx: 'Stawka długoterminowa wynika z\u00a0progu tylko w\u00a0przybliżeniu: jej prawdziwe progi to osobne kwoty dochodu.'
      },
      disclaimer: 'Szacunek podatku federalnego, bez NIIT i\u00a0podatku stanowego, o\u00a0ile go nie wpiszesz. Wyniki z\u00a0dziennika przyjmujemy jako zrealizowane na 31 grudnia. Wypłaty z\u00a0prop firm to dochód z\u00a0działalności, a\u00a0nie zysk §1256. Sprawdź je z\u00a0polem 11 formularza 1099-B od brokera; to nie jest doradztwo podatkowe.'
    },
    us8949: {
      heroCap: (year, next) => 'SZACOWANY PODATEK · ' + year + ' · TERMIN 15 KWIETNIA ' + next,
      heroSub: (net, state) => 'od zysku kapitałowego netto ' + net + (state ? ' · plus stan ' + state + '%' : ''),
      heroLossCap: (year, next) => 'STRATA KAPITAŁOWA NETTO · ' + year + ' · ZEZNANIE DO 15 KWIETNIA ' + next,
      heroLossSub: (allowed, saving) => allowed + ' odliczysz w\u00a0tym roku od innych dochodów — około ' + saving + ' mniej podatku',
      partShort: 'Krótkoterminowo', partShortSub: rate => 'jak dochód, ' + rate + '%',
      partLong: 'Długoterminowo', partLongSub: rate => 'trzymane ponad rok, ' + rate + '%',
      part6781: 'Futures (Form 6781)', part6781Sub: 'w\u00a0wierszach 4 i\u00a011', part6781Off: 'nie uwzględnione',
      partAllowed: 'Odliczone w\u00a0tym roku', partAllowedSub: limit => 'do ' + limit + ' rocznie',
      partCarryShort: 'Na kolejne lata, krótko', partCarryLong: 'Na kolejne lata, długo', partCarrySub: next => 'na ' + next,
      rateTitle: 'Twoja stawka', rateSub: 'próg podatku federalnego — według niego opodatkowane są zyski krótkoterminowe, długoterminowe według 0, 15 lub 20%',
      separate: 'Rozliczenie osobne w\u00a0małżeństwie', separateSub: 'roczne odliczenie straty wynosi 1 500 $ zamiast 3 000 $',
      with6781: 'Uwzględnij futures (Form 6781)', with6781Sub: 'podział 40/60 z\u00a0karty futures, w\u00a0wierszach 4 i\u00a011 Schedule D',
      carryShort: 'Strata krótkoterminowa z\u00a0lat ubiegłych', carryLong: 'Strata długoterminowa z\u00a0lat ubiegłych',
      jTitle: 'Transakcje z\u00a0dziennika', jSub: 'to, co nie jest kontraktem z\u00a0sekcji 1256 — akcje, ETF-y, opcje na akcje',
      jText: (n, roots) => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + ' (' + roots + ') · jako krótkoterminowe, pole A',
      jNone: 'w\u00a0tym roku brak transakcji poza kontraktami z\u00a0sekcji 1256',
      jNote: 'Liczą się tu instrumenty odznaczone na karcie futures. Jeśli wpiszesz niżej sumy z\u00a0formularza 1099-B od brokera, wyłącz „Uwzględnij dziennik” — 1099-B zawiera już te transakcje.',
      shortLab: 'krótkoterminowo', longLab: 'długoterminowo',
      boxTitle: key => 'Pole ' + key + ' · 1099-B',
      boxSub: {
        A: 'krótkoterminowo, koszt zgłoszony do IRS', B: 'krótkoterminowo, koszt niezgłoszony', C: 'krótkoterminowo, bez 1099-B',
        D: 'długoterminowo, koszt zgłoszony do IRS', E: 'długoterminowo, koszt niezgłoszony', F: 'długoterminowo, bez 1099-B'
      },
      proceeds: 'przychód ze sprzedaży', cost: 'koszt nabycia', adjustment: 'korekta', adjustmentHint: 'niedozwolona strata wash sale (kod W)',
      moreBoxes: 'POKAŻ POLA B, C, E, F', fewerBoxes: 'UKRYJ POLA B, C, E, F',
      salesTitle: 'Sprzedaże bez 1099-B', salesSub: 'jeden wiersz na sprzedaż — daty decydują, czy krótko-, czy długoterminowo',
      desc: 'Opis', descPh: 'np. 100 akcji XYZ', acquired: 'Data nabycia', sold: 'Data sprzedaży',
      addSale: '+ DODAJ SPRZEDAŻ', removeSale: 'Usuń tę sprzedaż',
      descNeeded: 'Opisz sprzedaż, np. „100 akcji XYZ”.',
      datesNeeded: 'Podaj obie daty; sprzedaż nie może być przed zakupem.',
      amountsNeeded: 'Podaj przychód ze sprzedaży i\u00a0koszt nabycia.',
      salesNote: 'Trzymane ponad rok — od dnia po rocznicy zakupu — to długoterminowo.',
      formTitle: 'Schedule D · z\u00a0Form 8949', formSub: year => 'zyski i\u00a0straty kapitałowe, ' + year,
      part: p => 'część ' + p, line: n => 'linia ' + n,
      partI: 'Krótkoterminowo', partII: 'Długoterminowo',
      boxLine: key => 'Pole ' + key, boxLineSub: { A: 'i\u00a0dziennik', C: 'i\u00a0sprzedaże bez 1099-B', F: 'i\u00a0sprzedaże bez 1099-B' },
      from6781Short: 'Form 6781, 40%', from6781Long: 'Form 6781, 60%',
      netShort: 'Wynik krótkoterminowy', netLong: 'Wynik długoterminowy', netAll: 'Zysk albo strata kapitałowa netto',
      lossLine: limit => 'Odliczona strata (do ' + limit + ')',
      totalTax: 'Szacowany podatek federalny i\u00a0stanowy', totalLoss: 'Strata na kolejne lata',
      notes: {
        basis: 'Sprzedaże z\u00a0pól A i\u00a0D bez korekt można wpisać od razu w\u00a0liniach 1a i\u00a08a Schedule D — Form 8949 jest dla pozostałych.',
        holding: 'Zyski krótkoterminowe opodatkowane są jak dochód; długoterminowe według 0, 15 lub 20% — stawka długoterminowa wynika z\u00a0progu tylko w\u00a0przybliżeniu.',
        wash: 'Strata na akcjach odkupionych w\u00a0ciągu 30 dni to wash sale: nie odliczasz jej teraz, tylko dodajesz do kosztu nowych akcji. Broker pokazuje ją w\u00a0kolumnie korekty formularza 1099-B.',
        niit: 'Bez 3,8% podatku od dochodów inwestycyjnych (NIIT) przy wyższych dochodach.'
      },
      disclaimer: 'Szacunek pomocny przy wypełnianiu Form 8949 i\u00a0Schedule D, a\u00a0nie doradztwo podatkowe. Sprawdź kwoty z\u00a0formularzem 1099-B od brokera, a\u00a0w\u00a0razie wątpliwości zapytaj doradcę podatkowego.'
    },
    de: {
      heroCap: (year, next) => 'SZACOWANY PODATEK · ' + year + ' · ZEZNANIE DO 31 LIPCA ' + next,
      heroLoss: (v, next) => 'strata ' + v + ' przechodzi na ' + next,
      heroSub: (allowance, base) => 'po wykorzystaniu ' + allowance + ' kwoty wolnej · podstawa ' + base,
      partCap: 'Abgeltungsteuer',
      partCapSub: v => '25% od ' + v,
      partSoli: 'Soli',
      partSoliSub: '5,5% podatku',
      partChurch: 'Kirchensteuer',
      partChurchSub: p => (p ? p + '% podatku' : 'nie dotyczy'),
      settings: 'Ustawienia',
      joint: 'Rozliczenie wspólne',
      jointSub: 'kwota wolna rośnie z\u00a01 000 € do 2 000 €',
      no: 'NIE',
      yes: 'TAK',
      church: 'Kirchensteuer',
      churchSub: '8% w\u00a0Bawarii i\u00a0Badenii-Wirtembergii, 9% w\u00a0pozostałych',
      none: 'BRAK',
      futTitle: 'Futures i\u00a0CFD',
      futSub: 'Termingeschäfte · straty bez limitu od 2024, łączą się z\u00a0innymi dochodami kapitałowymi',
      shareTitle: 'Akcje i\u00a0ETF',
      shareSub: 'osobny koszyk — straty z\u00a0akcji odliczasz tylko od zysków z\u00a0akcji',
      shareGains: 'zyski ze sprzedaży akcji',
      shareLosses: 'straty ze sprzedaży akcji',
      shareCarry: 'straty z\u00a0akcji z\u00a0lat ubiegłych',
      shareLossLab: 'strata na kolejny rok',
      shareCarryNote: (v, next) => 'Przeniesiesz ' + v + ' straty z\u00a0akcji na ' + next + '.',
      divTitle: 'Dywidendy i\u00a0odsetki',
      divSub: 'zagraniczny podatek odliczysz do wysokości niemieckiego',
      dividends: 'dywidendy i\u00a0odsetki brutto',
      withheld: 'podatek pobrany za granicą',
      otherCarry: 'inne straty z\u00a0lat ubiegłych',
      formTitle: 'Do przepisania w\u00a0Anlage KAP',
      formSub: 'kwoty w\u00a0euro',
      lines: {
        total: 'Dochody kapitałowe razem', totalSub: 'Kapitalerträge — po kompensacie koszyków',
        shareGains: 'w\u00a0tym zyski z\u00a0akcji', shareGainsSub: 'Gewinne aus Aktienveräußerungen',
        shareLosses: 'Straty z\u00a0akcji', shareLossesSub: 'Verluste aus Aktienveräußerungen',
        fut: 'Wynik z\u00a0transakcji terminowych', futSub: 'Termingeschäfte',
        allowance: 'Kwota wolna', allowanceSub: v => 'Sparer-Pauschbetrag ' + v,
        cap: 'Abgeltungsteuer', capSub: (church, credit) => (church ? '25% skorygowane o\u00a0Kirchensteuer' : '25%' + (credit ? ' minus ' + credit + ' z\u00a0zagranicy' : '')),
        soli: 'Solidaritätszuschlag 5,5%', church: 'Kirchensteuer', total2: 'Razem'
      },
      notes: {
        german: 'Niemiecki broker pobiera podatek sam — wtedy Anlage KAP składasz tylko po zwrot lub przy zagranicznym koncie.',
        foreign: 'Zagraniczny broker (np. IBKR) niczego nie pobiera: wszystko trzeba wykazać samodzielnie.',
        guenstiger: 'Gdy osobista stawka jest niższa niż 25%, zaznacz Günstigerprüfung — urząd policzy korzystniej.'
      },
      disclaimer: 'Transakcje z\u00a0dziennika przeliczamy po kursach z\u00a0Preferencji; w\u00a0zeznaniu użyj kursów EBC z\u00a0dnia transakcji. Nie jest to doradztwo podatkowe — sprawdź kwoty z\u00a0Jahressteuerbescheinigung lub raportem brokera.'
    },
    uk: {
      heroCap: (label, due) => 'SZACOWANY CGT · ' + label + ' · ZAPŁATA DO 31 STYCZNIA ' + due,
      heroLoss: v => 'strata ' + v + ' — zgłoś ją, żeby przenieść',
      heroSub: v => 'z\u00a0wyniku ' + v + ' po kwocie wolnej 3 000 £',
      partNet: 'Wynik netto',
      partNetSub: v => 'zyski ' + v,
      partAea: 'Kwota wolna',
      partAeaSub: 'Annual Exempt Amount',
      partCf: 'Straty na kolejne lata',
      partCfSub: 'losses carried forward',
      settings: 'Ustawienia',
      band: 'Twój próg dochodu',
      bandSub: 'zysk mieszczący się w\u00a0progu podstawowym: 18%, powyżej: 24%',
      basic: 'PODST. 18%',
      higher: 'WYŻSZY 24%',
      futTitle: 'Futures i\u00a0CFD',
      futSub: 'kontrakty i\u00a0CFD — bez reguł dopasowania, wynik liczony z\u00a0dziennika',
      shareTitle: 'Akcje i\u00a0ETF',
      shareSub: 'sumy po regułach: ten sam dzień → 30 dni → pula Section 104',
      proceeds: 'przychód ze zbycia',
      cost: 'koszty: zakup i\u00a0prowizje',
      shareNote: 'Raport „Capital gains” od brokera zwykle ma już dopasowane loty — przepisz jego sumy.',
      bfTitle: 'Straty z\u00a0lat ubiegłych',
      bfSub: 'zgłoszone HMRC w\u00a0ciągu 4 lat od roku straty',
      bf: 'straty przeniesione (brought forward)',
      bfLab: 'użyjesz teraz',
      bfNote: 'Używamy ich tylko do zejścia do kwoty wolnej 3 000 £ — reszta zostaje na kolejne lata.',
      formTitle: 'Do przepisania w\u00a0SA108',
      formSub: label => 'kwoty w\u00a0funtach · rok ' + label,
      lines: {
        gains: 'Zyski w\u00a0roku', losses: 'Straty w\u00a0roku', net: 'Wynik netto',
        bf: 'Straty z\u00a0lat ubiegłych użyte', aea: 'Kwota wolna', aeaSub: 'Annual Exempt Amount 3 000 £',
        taxable: 'Do opodatkowania', tax: rate => 'Capital Gains Tax ' + rate + '%', cf: 'Straty do przeniesienia'
      },
      notes: {
        must: 'Trzeba złożyć SA108: zyski powyżej 3 000 £ lub przychód ze zbycia powyżej 50 000 £.',
        mustNot: 'Poniżej progów — SA108 nie jest wymagany, ale warto zgłosić straty, żeby je zachować.',
        year: 'Rok podatkowy trwa od 6 kwietnia do 5 kwietnia — dziennik dzielimy automatycznie po dacie.',
        spread: 'Spread betting jest zwolniony z\u00a0CGT i\u00a0nie trafia do tego wyliczenia.'
      },
      disclaimer: 'Transakcje z\u00a0dziennika przeliczamy po kursach z\u00a0Preferencji; HMRC akceptuje kursy z\u00a0dnia transakcji. Nie jest to doradztwo podatkowe — w\u00a0razie wątpliwości zapytaj księgowego.'
    },
    later: {
      title: 'Inne kraje',
      sub: 'zrobimy, jeśli będzie zapotrzebowanie — kliknij, jeśli potrzebujesz',
      subDemo: 'zrobimy, jeśli będzie zapotrzebowanie — załóż konto, żeby zgłosić swój kraj',
      items: {
        ca: ['Kanada', 'średni koszt nabycia'],
        au: ['Australia', 'ulga 50% po 12 mies.'],
        nl: ['Holandia', 'Box 3 — podatek od majątku, nie od transakcji']
      },
      need: '+ POTRZEBUJĘ',
      asked: '✓ ZGŁOSZONO',
      skip: 'POMIJAMY',
      skipTitle: 'nie ma czego liczyć z\u00a0transakcji'
    }
  },
  /* Panel administracyjny -- pokazywany wyłącznie kontu właściciela. */
  admin: {
    taxVotes: 'Zestawienia podatkowe, o\u00a0które proszą',
    taxVoteNames: { us8949: 'USA · akcje', ca: 'Kanada', au: 'Australia' },
    open: 'PANEL ADMINISTRACYJNY',
    sidebar: 'ADMIN',
    title: 'Panel administracyjny',
    refresh: 'ODŚWIEŻ',
    loading: 'Wczytywanie…',
    loadFailed: 'Nie udało się wczytać danych panelu. Spróbuj odświeżyć.',
    statsAt: when => 'Stan na ' + when,
    accounts: 'Konta',
    accountsSub: (week, month) => '+' + week + ' w 7 dni · +' + month + ' w 30 dni',
    active: 'Aktywne',
    activeSub: (week, month) => week + ' w 7 dni · ' + month + ' w 30 dni',
    paying: 'Płacący',
    payingSub: (monthly, yearly) => monthly + ' miesięcznie · ' + yearly + ' rocznie',
    mrr: 'Przychód miesięczny',
    mrrSub: 'z subskrypcji, bez okresów próbnych',
    subsNote: (trial, cancelling, pastDue) =>
      'W okresie próbnym: ' + trial + ' · anulowane na koniec okresu: ' + cancelling + ' · zaległe płatności: ' + pastDue,
    plansTitle: 'Plany',
    plan: { free: 'Free', trial: 'Okres próbny', gift: 'Prezent na start', manual: 'Nadany ręcznie', stripe: 'Subskrypcja', demo: 'Demo' },
    usageTitle: 'Korzystanie',
    verified: 'Potwierdzone adresy e-mail',
    discord: 'Połączone z Discordem',
    terms: 'Zaakceptowany aktualny regulamin',
    demos: 'Konta demo',
    aiLabels: 'Podpowiedzi AI w tym miesiącu',
    aiSub: n => 'dla ' + n + ' ' + pl(n, 'konta', 'kont', 'kont'),
    imports: 'Importy CSV',
    importsSub: (month, entries) => month + ' w tym miesiącu · łącznie ' + entries + ' ' + pl(entries, 'wpis', 'wpisy', 'wpisów'),
    entries: 'Wpisy w rejestrach',
    trades: 'Transakcje w dziennikach',
    signups: 'Nowe konta w kolejnych miesiącach',
    listTitle: 'Konta',
    search: 'Szukaj po adresie e-mail',
    none: 'Żadne konto nie pasuje do wyszukiwania.',
    created: 'założone',
    lastSeen: 'ostatnio',
    confirmed: 'potwierdzony',
    unconfirmed: 'niepotwierdzony',
    adminBadge: 'admin',
    until: date => 'do ' + date,
    noEnd: 'bez daty końcowej',
    subscription: (interval, status, date) =>
      'Stripe: ' + (interval === 'year' ? 'roczna' : 'miesięczna') + ', ' + status + (date ? ', okres do ' + date : ''),
    subStatus: {
      active: 'aktywna', trialing: 'w okresie próbnym', past_due: 'zaległa płatność', canceled: 'anulowana',
      unpaid: 'nieopłacona', incomplete: 'nieukończona', incomplete_expired: 'wygasła', paused: 'wstrzymana'
    },
    cancelling: 'kończy się z końcem okresu',
    untilLabel: 'Do (opcjonalnie)',
    grant: 'NADAJ LIQUIDITY+',
    update: 'ZAPISZ DATĘ',
    revoke: 'ODBIERZ',
    stripeManaged: 'Płatność przez Stripe — plan zmienia się w panelu Stripe.',
    grantNote: 'Liquidity+ nadany tutaj bez daty nie wygasa. „Odbierz” cofa tylko to, co nadano ręcznie: okres próbny i prezent na start trwają dalej według własnych dat.'
  },
  carType: {
    Maintenance: 'Serwis', Repair: 'Naprawa', Insurance: 'Ubezpieczenie',
    Registration: 'Rejestracja', Tyres: 'Opony', 'Loan payment': 'Rata kredytu',
    Inspection: 'Przegląd techniczny', Other: 'Inne'
  },
  alloc: { investments: 'inwestycje', cash: 'gotówka', debt: 'dług' },
  strategy: { rate: 'NAJWYŻSZE OPROCENTOWANIE', balance: 'NAJMNIEJSZE SALDO' },
  months: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'],
  seedLabels: {
    salary: 'Wynagrodzenie — Northwind',
    freelance: 'Zlecenia / konsultacje',
    dividends: 'Dywidendy'
  },
  units: {
    km: 'km', mile: 'mi', litre: 'l', gallon: 'gal',
    per100: 'l/100 km', mpg: 'mpg',
    metric: 'Metryczny', imperial: 'US / imperialny',
    metricNote: 'kilometry, litry, l/100 km',
    imperialNote: 'mile, galony, mpg'
  },
  fx: {
    ofIncome: p => p + ' przychodu',
    servicing: a => 'raty: ' + a + '/mies.',
    topCat: (c, a) => c + ' na czele: ' + a,
    nothing: 'brak wpisów',
    vsMonth: (s, key) => s + ' wobec ' + monthIn(MONTHS_GENITIVE_PL, key),
    vsLast: s => s + ' wobec poprzedniego miesiąca',
    firstMonth: 'pierwszy miesiąc',
    incomeForWorked: 'za przepracowany poprzedni miesiąc',
    monthsLeft: n => 'jeszcze ~' + n + ' mies.',
    noPlan: 'brak planu',
    interestOutpaces: 'rata nie pokrywa odsetek',
    derivedPayment: 'z liczby rat',
    debtFormNote:
      'Podaj ratę albo liczbę miesięcy — to, co jest w umowie. '
      + 'Prowizja to jednorazowy koszt kredytu i wlicza się do całkowitego kosztu. '
      + 'Dług jest liczony od miesiąca wybranego u góry ekranu.',
    debtTrend: 'Dług miesiąc po miesiącu',
    debtTrendNote: 'stan na koniec każdego miesiąca',
    debtSame: key => 'bez zmian wobec ' + monthIn(MONTHS_GENITIVE_PL, key),
    debtFirst: 'pierwszy zapisany miesiąc',
    debtCleared: 'spłacony w tym miesiącu',
    debtProjected: 'Przyszłe miesiące pokazują ostatnie znane saldo — spłata liczy się od miesiąca, w którym ją zapisano.',
    debtProjectedBar: 'Ten miesiąc jeszcze nie nadszedł, więc pokazano ostatnie znane saldo.',
    costsInterest: total => 'odsetki: ' + total,
    repaid: (p, o) => 'spłacono ' + p + ' z ' + o,
    costPerUnit: unit => 'KOSZT NA ' + unit.toUpperCase(),
    lastFill: v => 'ostatnio ' + v,
    fuelOf: a => a + ' na paliwo',
    alertDebtTitle: n => n + ' to Twój najdroższy dług',
    alertDebtBody: (r, b, i) =>
      'Oprocentowanie ' + r + '% od ' + b + ' — to około ' + i + ' odsetek w tym miesiącu.',
    alertSaveTitle: (a, key) => 'Zaoszczędzono ' + a + ' ' + inMonthPl(key),
    alertSaveBody: (p, mo) => p + '% przychodu.'
      + (mo ? ' W tym tempie spłacisz najmniejszy dług w ' + mo + ' ' + pl(mo, 'miesiąc', 'miesiące', 'miesięcy') + '.' : ''),
    alertShortTitle: 'Wydatki przekroczyły przychód',
    alertBudgetTitle: category => category + ': budżet przekroczony',
    alertBudgetBody: (amount, others) => amount + ' ponad limit w\u00a0tym miesiącu' + (others ? '; ponad limit jest też ' + others + ' ' + pl(others, 'inna kategoria', 'inne kategorie', 'innych kategorii') + '.' : '.'),
    alertShortBody: (a, c) => 'Brakuje ' + a + ' w tym miesiącu. Największa pozycja: ' + c + '.',
    alertCarTitle: c => 'Pojazd kosztuje ' + c,
    alertCarBody: (tot, dist, econ) => tot + ' na ' + dist + ' od zakupu, średnio ' + econ + '.',
    alertCarBodyNoEconomy: (tot, dist) => tot + ' na ' + dist + ' od zakupu.'
  },
  mobile: {
    cat: { ...CAT_PL, Dining: 'Restauracje', Subscriptions: 'Subskrypcje' },
    expensesCap: 'WYDATKI',
    savedMonth: 'OSZCZĘDNOŚCI',
    debtOutstanding: 'DŁUG',
    interestYear: 'ODSETKI · ROK',
    avgEconomy: 'ŚR. SPALANIE',
    spentTotal: 'wydano razem',
    quickAdd: 'Szybkie dodawanie',
    pay: 'WPŁATA',
    sheet: { income: 'Przychód', expense: 'Wydatek', fuel: 'Paliwo' },
    nav: { overview: 'Przegląd', cash: 'Przepływy', debt: 'Długi', invest: 'Inwest.', car: 'Pojazd', journal: 'Trading', tax: 'Podatki', settings: 'Ustaw.' },
    pages: {
      overview: ['PANEL', 'Przegląd'],
      cash: ['PRZEPŁYWY', 'Przychody i wydatki'],
      debt: ['ZOBOWIĄZANIA', 'Długi'],
      invest: ['AKTYWA', 'Inwestycje'],
      car: ['UTRZYMANIE', 'Pojazd'],
      journal: ['NARZĘDZIA', 'Trading'],
      tax: ['ZEZNANIE', 'Podatki']
    },
    fx: {
      servicing: a => a + '/mies.',
      alertSaveBody: p => p + '% przychodu zostało w tym miesiącu.',
      alertShortBody: (a, c) => 'Brakuje ' + a + '. Największa pozycja: ' + c + '.',
      alertCarBody: (tot, econ) => tot + ' od zakupu, średnio ' + econ + '.',
      alertCarBodyNoEconomy: tot => tot + ' od zakupu.'
    }
  },
  shell: {
    signIn: 'ZALOGUJ SIĘ',
    signUp: 'ZAŁÓŻ KONTO',
    tryDemo: 'WYPRÓBUJ DEMO',
    email: 'e-mail',
    password: 'hasło',
    passwordAgain: 'powtórz hasło',
    currentPassword: 'obecne hasło',
    addPassword: 'DODAJ HASŁO',
    changePassword: 'ZMIEŃ HASŁO',
    addPasswordNote: 'Hasło pozwala logować się bez Discorda. Dopiero po jego ustawieniu możesz bezpiecznie odłączyć konto Discord.',
    haveAccount: 'Masz już konto?',
    noAccount: 'Nie masz konta?',
    signOut: 'WYLOGUJ SIĘ',
    account: 'KONTO',
    guest: 'konto tymczasowe',
    claim: 'ZAŁÓŻ KONTO',
    claimTitle: 'Załóż własne konto',
    claimHint: 'Podaj e-mail i hasło, a ten rejestr zostanie przypisany do Twojego konta i będzie dostępny na każdym urządzeniu.',
    claimFresh: 'Zacznij od pustego rejestru',
    claimFreshNote: 'Przykładowe dane zostaną usunięte. Język i waluta zostają bez zmian.',
    claimKeep: 'Zachowaj wszystko, co tu teraz jest',
    claimKeepNote: 'Przykładowe dane zostaną obok Twoich wpisów — możesz je usunąć w dowolnej chwili.',
    claimedFresh: 'Konto założone — Twój rejestr jest pusty i gotowy.',
    demoBanner: 'To jest demo — wszystkie liczby są przykładowe.',
    demoBannerSub: 'Konto demo znika po 3 dniach nieużywania. Załóż konto, żeby zachować zmiany — podstawowe funkcje są bezpłatne.',
    cancel: 'ANULUJ',
    save: 'ZAPISZ',
    synced: 'ZSYNCHRONIZOWANO',
    syncing: 'SYNCHRONIZACJA…',
    offline: 'OFFLINE',
    pendingChanges: n => n + ' ' + pl(n, 'zmiana', 'zmiany', 'zmian') + ' w kolejce',
    offlineHint: 'Zmiany są zapisane na tym urządzeniu i zsynchronizują się same.',
    dismiss: 'ZAMKNIJ',
    loading: 'Wczytujemy Twój rejestr…',
    passwordsDiffer: 'Hasła nie są takie same.',
    passwordShort: 'Hasło musi mieć co najmniej 8 znaków.',
    pricesUpdated: 'Ceny zaktualizowane.',
    pricesPartial: missed => 'Zaktualizowano. Brak notowań dla: ' + missed,
    pricesEurHint: ticker => 'Ticker w euro potrzebuje oznaczenia giełdy, np. ' + ticker + '.DE dla Xetry, ' + ticker + '.AS dla Amsterdamu — zmienisz go przyciskiem ✎.',

    heading: 'Zaloguj się',
    headingSignUp: 'Załóż konto',
    subSignUp: 'Wystarczą adres e-mail i hasło.',
    or: 'ALBO',
    discordNote: 'Discord przekazuje tylko nazwę, awatar i adres e-mail. Niczego nie publikujemy i nie czytamy Twoich serwerów.',
    subPassword: 'Zaloguj się e-mailem i hasłem albo załóż konto w kilka sekund.',
    ctaDiscord: 'KONTYNUUJ PRZEZ DISCORD',
    termsAgree: ['Akceptuję ', 'regulamin', ' i ', 'politykę prywatności', '.'],
    termsNeeded: 'Aby założyć konto, zaakceptuj regulamin i politykę prywatności.',
    termsName: 'Regulamin',
    privacyName: 'Polityka prywatności',
    giftTitle: 'Liquidity+ na trzy miesiące w prezencie',
    giftBody: date => 'Dziękujemy, że jesteś z nami od początku. Twoje konto ma Liquidity+ za darmo do ' + date + ':',
    giftIncludes: [
      'import wyciągów CSV bez limitu',
      'podpowiedzi kategorii AI (Claude od Anthropic)',
      'automatyczne notowania i historia cen',
      'dziennik tradera bez miesięcznego limitu'
    ],
    giftAfter: 'Nie musisz nic robić i nic nie zostanie pobrane. Po tym czasie konto przejdzie na plan Free, chyba że zdecydujesz się na subskrypcję.',
    giftOk: 'ŚWIETNIE',
    giftSeePlan: 'ZOBACZ PLAN',
    termsGateTitle: 'Regulamin i polityka prywatności',
    termsGateBody: 'Aby dalej korzystać z konta, zaakceptuj regulamin i politykę prywatności. Opisują, jak działa aplikacja i co dzieje się z Twoimi danymi.',
    termsAccept: 'AKCEPTUJĘ',
    termsGateNeeded: 'Zaznacz pole akceptacji, aby dalej korzystać z konta.',
    yourData: 'TWOJE DANE',
    yourDataNote: 'Pobierz kopię wszystkiego, co jest na Twoim koncie, albo usuń konto. Usunięcia nie da się cofnąć.',
    downloadData: 'POBIERZ KOPIĘ DANYCH',
    deleteAccount: 'USUŃ KONTO',
    deleteDemo: 'USUŃ KONTO DEMO',
    deleteWarning: 'Usuniemy konto i wszystko, co się na nim znajduje: przychody, wydatki, długi, inwestycje, pojazd i dziennik. Tego nie da się cofnąć — jeśli chcesz, najpierw pobierz kopię danych.',
    deleteDemoWarning: 'Usuniemy konto demo razem ze wszystkimi zmianami. Tego nie da się cofnąć.',
    deletePasswordNeeded: 'Aby usunąć konto, wpisz hasło.',
    deleteForever: 'USUŃ NA ZAWSZE',
    accountDeleted: 'Konto zostało usunięte razem ze wszystkimi danymi.',
    connectingTitle: 'Przekierowanie do Discorda',
    connectingBody: 'Zatwierdź dostęp w otwartym oknie autoryzacji.',
    steps: ['Przygotowujemy bezpieczne żądanie', 'Czekamy na Twoją zgodę', 'Kończymy logowanie'],
    cancel: 'ANULUJ',
    verified: 'POŁĄCZONO',
    session: 'sesja',
    tokenExpiry: 'sesja wygasa',
    enter: 'WEJDŹ DO REJESTRU',
    switchAccount: 'zmień konto',
    authErrorTitle: 'Autoryzacja przerwana',
    authErrorBody: 'Discord nie potwierdził logowania. Spróbuj ponownie albo zaloguj się hasłem.',
    retry: 'SPRÓBUJ PONOWNIE',
    back: 'WRÓĆ DO LOGOWANIA',
    footer: 'OAUTH 2.0 · PKCE · BCRYPT',
    linkDiscord: 'POŁĄCZ DISCORD',
    unlinkDiscord: 'ODŁĄCZ DISCORD',
    linkedAs: 'połączono jako',
    settingsTitle: 'Preferencje',
    optHideBalances: 'Ukryj salda',
    optHideBalancesNote: 'Zamienia każdą kwotę na kropki — przydatne, gdy ktoś patrzy Ci przez ramię.',
    optStrategy: 'Strategia spłaty',
    optStartTab: 'Ekran startowy',
    strategyRate: 'Najwyższe oprocentowanie',
    strategyBalance: 'Najmniejsze saldo',
    savedForAccount: 'Zapisano na Twoim koncie.',
    close: 'ZAMKNIJ',
    preferences: 'PREFERENCJE',

    verifyBannerTitle: 'Potwierdź adres e-mail',
    verifyBannerBody: 'Wysłaliśmy link na Twoją skrzynkę. Dopóki go nie klikniesz, konto pozostaje niepotwierdzone.',
    verifyResend: 'WYŚLIJ NOWY LINK',
    verifyOk: 'Adres potwierdzony — dziękujemy.',
    verifyFailed: 'Ten link potwierdzający stracił ważność. Poproś o nowy.',
    verifySent: 'Nowy link jest w drodze.',
    verifyNotSent: 'Ten serwer nie może wysyłać e-maili — poproś administratora o potwierdzenie adresu.',
    verifyBlocked: 'Ten adres nie został jeszcze potwierdzony.',

    forgotLink: 'Nie pamiętasz hasła?',
    forgotSend: 'WYŚLIJ LINK',
    forgotBack: 'Wróć do logowania',
    forgotNote: 'Wyślemy link na ten adres, jeśli jest do niego przypisane konto. Link działa raz i wygasa po godzinie.',
    emailNeeded: 'Najpierw podaj adres e-mail.',
    emailInvalid: 'To nie wygląda na adres e-mail — sprawdź, czy nie ma literówki.',
    resetHeading: 'Ustaw nowe hasło',
    resetSub: 'Wybierz nowe hasło do swojego konta. Zalogujemy Cię od razu.',
    resetSubmit: 'ZAPISZ I ZALOGUJ SIĘ',
    newPassword: 'nowe hasło',
    resetSent: 'Jeśli ten adres jest przypisany do konta, link jest już w drodze.',
    resetDone: 'Hasło zmienione.',
    resetExpired: 'Ten link stracił ważność. Poproś o nowy.',
    rateBackfill: 'UZUPEŁNIJ HISTORIĘ',
    rateBackfillNote: 'Przycisk „Uzupełnij historię” sprawdza, ile waluta była warta w każdym miesiącu, w którym masz wpisy w obcej walucie, i zapisuje kurs osobno dla każdego z nich. Kursy wpisane ręcznie pozostają bez zmian.',
    rateRefreshNote: month =>
      'Przycisk „Pobierz kursy” zapisuje dzisiejszy kurs dla miesiąca ' + month + ', a nie dla miesiąca widocznego na ekranie.',
    ratesBackfilled: n =>
      'Uzupełniono ' + n + ' ' +
      pl(n, 'kurs miesięczny.', 'kursy miesięczne.', 'kursów miesięcznych.'),
    ratesBackfillNone: 'Nie ma czego uzupełniać — każdy miesiąc ma już swój kurs.',
    aiSection: 'Podpowiedzi AI',
    aiNote: 'Na ekranie przeglądu importu przycisk „Podpowiedz kategorie” prosi model Claude od Anthropic o przypisanie kategorii wierszom, których nie rozpoznał słownik. '
      + 'Wysyłane są wyłącznie opisy — nigdy kwoty, daty ani dane konta — a nieznaną nazwę Claude może sprawdzić w sieci. '
      + 'Podpowiedzi to tylko propozycje: nic nie zostanie zapisane, dopóki nie naciśniesz „Importuj”.',
    aiSearched: n => n + ' ' + pl(n, 'wyszukanie w sieci', 'wyszukania w sieci', 'wyszukań w sieci'),
    aiSort: 'PODPOWIEDZ KATEGORIE',
    aiSorted: (applied, asked, searches) =>
      'Przypisano ' + applied + ' z ' + asked + ' ' + pl(asked, 'wiersza', 'wierszy', 'wierszy')
      + (searches
        ? ', po ' + searches + ' ' + pl(searches, 'wyszukaniu w sieci', 'wyszukaniach w sieci', 'wyszukaniach w sieci')
        : '')
      + '. Pozostałe zostały bez zmian — sprawdź je przed importem.',
    aiNothing: 'Brak wydatków do przypisania.',
    aiAllSet: 'Wszystkie wydatki mają już kategorię — nic nie zostało wysłane do AI.',
    costTitle: 'Koszt zakupu',
    costUnits: 'sztuki',
    costPerUnit: 'koszt za sztukę',
    costTotal: 'zapłacone łącznie',
    positionValue: 'wartość pozycji',
    costNote:
      'Wystarczy wypełnić jedno z tych pól — drugie policzy się samo. '
      + 'Zapisywana jest cena ZA SZTUKĘ, więc łączna kwota wpisana w to pole zostałaby '
      + 'pomnożona przez liczbę sztuk, a pozycja pokazałaby stratę, której nigdy nie było.',
    costEdit: 'Koszt zakupu — kliknij, aby poprawić',
    exitTitle: 'Co zrobić z tą pozycją?',
    exitSell: month => 'SPRZEDANE W ' + month,
    exitSellNote: month =>
      'Pozycja przestanie być liczona od ' + month + '. Wcześniejsze miesiące ją zachowają, '
      + 'bo wtedy rzeczywiście była w portfelu.',
    exitDelete: 'USUŃ Z CAŁEJ HISTORII',
    exitDeleteNote:
      'Usuwa ją ze wszystkich miesięcy, tak jakby nigdy nie została kupiona. Ta opcja jest '
      + 'dla wpisu dodanego przez pomyłkę, nie dla sprzedanej pozycji.',
    exitReopen: 'PRZYWRÓĆ DO PORTFELA',
    exitReopenNote: month => 'Sprzedano w ' + month + '. Ta opcja cofa sprzedaż i pozycja znów będzie liczona.',
    soldIn: month => 'sprzedane ' + month,
    purchasePrice: 'cena zakupu',
    purchaseDate: 'data zakupu',
    ownershipTotal: 'KOSZT CAŁKOWITY',
    serviceDate: 'data',
    weekdays: ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'],
    jClear: 'WYCZYŚĆ',
    jUnset: 'wybierz…',
    jPairHint: 'np. MES',
    jReportHint: 'https://…',
    jPlHint: '+ / −',
    jYesShort: 'TAK',
    jNoShort: 'NIE',
    serviceDateTitle: 'Kiedy to było',
    serviceDateNote: 'Wybierz dzień serwisu albo tankowania — nie musi to być dzisiaj.',
    jDate: 'data',
    jPair: 'instrument',
    jSession: 'sesja',
    jReport: 'link do raportu',
    jLots: 'loty',
    jSide: 'kierunek',
    jSetup: 'setup',
    jTimeFrame: 'interwał',
    jExit: 'wyjście',
    jRules: 'zasady',
    jNetPl: 'wynik netto',
    jCurrency: 'waluta',
    jMm: 'zarz. kapitałem',
    jRisk: 'ryzyko',
    jAdd: 'ZAPISZ TRANSAKCJĘ',
    jWinRate: 'SKUTECZNOŚĆ',
    jFollowRate: 'ZGODNIE Z PLANEM',
    jPlanSplit: (followed, broken) => 'wg planu ' + followed + ' · poza planem ' + broken,
    jLoading: 'Wczytujemy dziennik…',
    jFailed: 'Nie udało się wczytać dziennika. Nic nie przepadło — problem dotyczy tylko odczytu.',
    jRetry: 'SPRÓBUJ PONOWNIE',
    jMixed: list => 'różne waluty (' + list + ') — brak wspólnej sumy',
    jEmpty: 'Jeszcze nic nie zapisano. Jedna transakcja zapisana uczciwie jest warta więcej niż trzy zapisane na swoją korzyść.',
    jModeLive: 'Dziennik',
    jModeBacktest: 'Backtesting',
    btSession: 'Sesja backtestu',
    btIntro: 'Sesja to strategia sprawdzana na historycznych wykresach. Jej transakcje nie trafiają do dziennika ani do jego statystyk i nie liczą się do limitu planu Free.',
    btNamePh: 'nazwa sesji, np. MES ORB — marzec 2025',
    btNew: 'NOWA SESJA',
    btNameNeeded: 'Podaj nazwę sesji.',
    btNone: 'Nie ma jeszcze żadnej sesji. Nazwij pierwszą — strategię i okres, który testujesz — i zacznij zapisywać transakcje.',
    btDelete: 'USUŃ SESJĘ',
    btDeleteAsk: n => 'Usunąć tę sesję razem z ' + n + ' ' + pl(n, 'transakcją', 'transakcjami', 'transakcjami') + '? Tego nie da się cofnąć. Dziennik pozostaje bez zmian.',
    btDeleteYes: 'USUŃ',
    btEmpty: 'W tej sesji nie ma jeszcze transakcji. Zapisuj je tak samo jak w dzienniku, z datą świecy, na której była pozycja.',
    btProfitFactor: 'profit factor',
    btProfitFactorSub: 'zyski brutto ÷ straty brutto',
    btExpectancy: 'na transakcję',
    btExpectancySub: 'średni wynik jednej transakcji',
    btDrawdown: 'maks. obsunięcie',
    btDrawdownSub: n => 'najdłuższa seria strat: ' + n,
    btAvg: 'śr. zysk / śr. strata',
    btEquity: 'Krzywa kapitału',
    btEquitySub: 'wynik narastająco, transakcja po transakcji',
    btMixed: 'Ta sesja łączy kilka walut, więc kwoty zostały pominięte.',
    gapTitle: 'Luka dyscypliny',
    gapSub: 'wynik na transakcję: backtest kontra rzeczywistość',
    gapHint: 'Nadaj setupowi tę samą nazwę w sesji backtestu i w dzienniku, a zobaczysz tu, ile przewagi ze strategii dociera do Twojego realnego wyniku.',
    gapBacktest: 'backtest',
    gapLive: 'realnie',
    gapDiff: 'różnica',
    gapCounts: (backtest, live) => backtest + ' w backteście · ' + live + ' realnie',
    gapFew: 'Za mało transakcji na wniosek: potrzeba co najmniej 5 w backteście i 3 realnych.',
    gapBroken: (amount, n) => 'Transakcje wbrew planowi (' + n + ') dają średnio o ' + amount + ' mniej niż zgodne z planem.',
    gapTilt: (amount, n) => 'Transakcje zawarte zaraz po stracie (' + n + ') dają średnio o ' + amount + ' mniej niż pozostałe.',
    gapAhead: 'Tutaj realny wynik jest lepszy niż w backteście.',
    gapMixed: 'Ten setup łączy kilka walut, więc kwoty zostały pominięte.',
    jModeProp: 'Prop firmy',
    jModePit: 'PIT-38',
    pitYear: 'Rok podatkowy',
    pitJournal: 'Uwzględnij dziennik',
    pitJournalNote: 'Tylko jeśli dziennik zawiera transakcje na Twoim własnym rachunku maklerskim. Transakcje i wypłaty z prop firm nie trafiają do PIT-38 — ich rozliczenie skonsultuj z księgowym.',
    pitConvert: 'PRZELICZ PO KURSACH NBP',
    pitConverting: 'Przeliczanie…',
    pitConverted: (n, year) => 'Przeliczono ' + n + ' ' + pl(n, 'transakcję', 'transakcje', 'transakcji') + ' z ' + year + ' r. po kursie NBP z dnia roboczego poprzedzającego każdą z nich',
    pitNoTrades: year => 'W dzienniku nie ma realnych transakcji z ' + year + ' r.',
    pitManual: 'Wpisz ręcznie (w złotych)',
    pitSecurities: 'Akcje, ETF-y, fundusze',
    pitSecIncome: 'przychód ze sprzedaży',
    pitSecCost: 'koszty (zakup, prowizje)',
    pitCrypto: 'Kryptowaluty — osobny koszyk',
    pitCryptoIncome: 'przychód',
    pitCryptoCost: 'koszty z tego roku',
    pitCryptoCarried: 'nadwyżka kosztów z lat ubiegłych',
    pitDividends: 'Dywidendy zagraniczne',
    pitDivGross: 'brutto, w złotych',
    pitDivWithheld: 'podatek pobrany za granicą',
    pitLosses: 'Straty z lat ubiegłych (akcje i instrumenty pochodne)',
    pitLossAmount: year => 'strata ' + year,
    pitLossUsed: 'już odliczono',
    pitLossOnce: 'odlicz jednorazowo (do 5 mln zł)',
    pitCapital: 'Papiery wartościowe i instrumenty pochodne',
    pitCryptoSection: 'Kryptowaluty',
    pitDivSection: 'Dywidendy zagraniczne',
    pitIncome: 'przychód',
    pitCost: 'koszty',
    pitGain: 'dochód',
    pitLoss: 'strata (do odliczenia w kolejnych pięciu latach)',
    pitDeducted: 'odliczone straty z lat ubiegłych',
    pitBase: 'podstawa opodatkowania',
    pitTax: 'podatek 19%',
    pitCarry: 'nadwyżka kosztów na kolejny rok',
    pitDivDue: '19% od dywidend',
    pitDivCredit: 'podatek zapłacony za granicą (do 19%)',
    pitDue: 'do zapłaty',
    pitTotal: (amount, year) => 'Do zapłaty z PIT-38 do 30 kwietnia ' + year + ' r.: ' + amount,
    pitFromJournal: 'z dziennika',
    pitDisclaimer: 'Szacunek pomocny przy wypełnianiu PIT-38, a nie doradztwo podatkowe. Liczy to, co dostanie: sprawdź kwoty z wyciągami od brokera, a w razie wątpliwości zapytaj księgowego.',
    pitRate: 'NBP',
    apiTaxRates: 'Nie udało się teraz pobrać kursów NBP. Spróbuj ponownie za chwilę.',
    apiReviewWait: 'Jeden przegląd dziennie: kolejny będzie można przygotować jutro.',
    rvTitle: 'Przegląd tygodnia',
    rvSub: 'przygotowuje go Claude na podstawie Twoich liczb, tylko na Twoją prośbę',
    rvAsk: 'PRZYGOTUJ PRZEGLĄD',
    rvWriting: 'Przygotowywanie…',
    rvNext: date => 'Kolejny przegląd od ' + date + '.',
    rvWritten: date => 'Przygotowano ' + date,
    rvPlus: 'Przegląd tygodnia jest częścią Liquidity+.',
    rvNone: 'Nie ma jeszcze przeglądu. Naciśnij przycisk, a Claude przyjrzy się Twojemu miesiącowi i dziennikowi.',
    rvPrivacy: 'Claude dostaje tylko sumy, wskaźniki i zmiany — bez nazw, opisów i czegokolwiek, co Cię identyfikuje — i tylko po naciśnięciu przycisku. Anthropic nie trenuje na tym modeli.',
    tmNotice: date => 'Nowy regulamin od ' + date + ': import wyciągów w\u00a0planie Free bez limitu; Free obejmuje jedną sesję backtestu i\u00a0jedno konto prop firmy, a\u00a0zestawienia podatkowe (Polska, a\u00a0teraz też USA, Niemcy i\u00a0Wielka Brytania) są częścią Liquidity+. Do tego tygodniowy przegląd od Claude’a.',
    tmRead: 'Przeczytaj regulamin',
    tmAccept: 'AKCEPTUJĘ TERAZ',
    wiTitle: 'Co jeśli?',
    wiSub: 'Twoja wartość netto miesiąc po miesiącu — bez zmian i ze zmianą.',
    wiSubShort: 'Wartość netto bez zmian i ze zmianą',
    wiPresets: ['Ostrożny', 'Zrównoważony', 'Agresywny'],
    wiPresetTitle: (extra, invest, ret) => 'Nadpłata ' + extra + ', inwestycja ' + invest + ', zwrot ' + ret + ' rocznie',
    wiPerMonthShort: amount => amount + '/mies.',
    wiKpiEnd: years => 'ZA ' + years.toUpperCase(),
    wiKpiGain: 'ZMIANA DAJE',
    wiKpiDebt: 'BEZ DŁUGÓW',
    wiKpiInterest: 'ZAOSZCZĘDZONE ODSETKI',
    wiKpiLessInterest: 'MNIEJ ODSETEK',
    wiWithout: value => 'bez zmian ' + value,
    wiInstead: value => 'zamiast ' + value,
    wiAboutMonth: value => '≈ ' + value + ' na miesiąc',
    wiAboutMonthShort: value => '≈ ' + value + '/mies.',
    wiInterestInstead: (now, before) => 'odsetki ' + now + ' zamiast ' + before,
    wiBeyond: 'poza horyzontem',
    wiGainOverdrawn: 'zakłada pieniądze, których nie masz — patrz niżej',
    wiShortTitle: month => 'Gotówka spadnie poniżej zera w ' + month,
    wiShortTrim: amount => 'Przy tych kwotach wydajesz więcej, niż zostaje z miesiąca. Zmniejsz nadpłatę lub inwestycję o ok. ' + amount + ', żeby zachować płynność.',
    wiShortAlways: 'Także bez zmian wydajesz więcej, niż zostaje z miesiąca — to nie skutek suwaków.',
    wiChart: 'Wartość netto',
    wiLegendDiff: 'różnica',
    wiHover: 'najedź na wykres',
    wiTouch: 'przesuń palcem po wykresie',
    wiTouching: 'przesuwasz',
    wiDiff: 'różnica',
    wiTagShort: month => 'gotówka < 0 · ' + month,
    wiTagDebt: month => 'bez długów · ' + month,
    wiTagDebtNow: month => 'bez zmian · ' + month,
    wiMarkDebt: 'Bez długów — ze zmianą',
    wiMarkDebtNow: 'Bez długów — bez zmian',
    wiMarkShort: 'Gotówka poniżej zera',
    wiMixTitle: 'Z czego składa się wynik',
    wiMixAt: month => 'NA KONIEC · ' + month.toUpperCase(),
    wiMixDebt: 'długi',
    wiMixCash: 'gotówka',
    wiMixInvest: 'inwestycje',
    wiMixDebtOf: value => 'dług ' + value,
    wiMixAssets: (cash, investments) => 'gotówka ' + cash + ' · inwestycje ' + investments,
    wiMixAssetsShort: value => 'aktywa ' + value,
    wiQueue: 'Nadpłata trafia do pierwszego długu w kolejce spłat. Po spłacie wszystkich długów nadpłata zasila inwestycje.',
    wiAxisK: ' tys.',
    wiAxisM: ' mln',
    bt2Now: 'dziś',
    bt2NoSettings: 'bez ustawień',
    bt2Settings: 'Ustawienia sesji',
    bt2NoTrades: 'brak transakcji',
    bt2Sessions: 'SESJE BACKTESTU',
    bt2SessionsSub: 'osobno od dziennika — nie wpływają na jego statystyki ani na limit planu Free',
    bt2New: '+ NOWA SESJA',
    bt2NewHint: 'strategia i okres, który testujesz',
    bt2NumbersNeeded: 'Sprawdź liczby — kapitał nie może być ujemny, a 1R musi być większe od zera.',
    bt2PeriodOrder: 'Okres musi się zaczynać przed końcem.',
    bt2PeriodNote: 'Miesiące wykresu, który odtwarzasz.',
    bt2FormNew: 'NOWA SESJA',
    bt2FormEdit: 'USTAWIENIA SESJI',
    bt2Name: 'Nazwa sesji',
    bt2NamePh: 'np. MES ORB — Q1 2025',
    bt2From: 'Okres od',
    bt2To: 'Okres do',
    bt2Capital: 'Kapitał startowy',
    bt2Risk: 'Ryzyko na transakcję (1R)',
    bt2Delete: 'Usuń sesję',
    bt2RiskNote: '1R to kwota, którą ryzykujesz w jednej transakcji — dzięki niej wynik da się porównać między strategiami.',
    bt2RiskNoteKept: 'Transakcje zachowują wynik w pieniądzach: nowe 1R zmienia to, ile każda z nich wynosi w R.',
    bt2Create: '+ UTWÓRZ SESJĘ',
    bt2Ev: 'OCZEKIWANA WARTOŚĆ',
    bt2EvSub: money => '≈ ' + money + ' na transakcję',
    bt2WinSub: (wins, losses, be) => wins + ' ' + pl(wins, 'zysk', 'zyski', 'zysków') + ' · ' + losses + ' ' + pl(losses, 'strata', 'straty', 'strat') + ' · ' + be + ' BE',
    bt2PfSub: (win, loss) => 'śr. zysk ' + win + ' · śr. strata ' + loss,
    bt2DdSub: 'największy spadek od szczytu',
    bt2SampleShort: n => 'Próba: ' + n + ' / 30',
    bt2SampleShortSub: left => 'jeszcze ' + left + ' ' + pl(left, 'transakcja', 'transakcje', 'transakcji') + ', zanim wynik zacznie coś znaczyć',
    bt2SampleOk: n => '✓ Próba wystarczająca: ' + n,
    bt2SampleOkSub: 'przy 100+ pewniejsze będzie też obsunięcie',
    bt2SampleStrong: n => '✓ Mocna próba: ' + n,
    bt2SampleStrongSub: 'wynik jest statystycznie wiarygodny',
    bt2Capital2: (from, to, change) => 'kapitał ' + from + ' → ' + to + ' (' + change + ')',
    bt2SetCapital: 'ustaw kapitał w ✎ sesji',
    bt2CurveR: 'w R, transakcja po transakcji',
    bt2CurveEmpty: 'Krzywa pojawi się po pierwszej transakcji.',
    bt2ListSub: n => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + ' · najnowsze na górze',
    bt2QuickHint: 'szybki wpis: kierunek i wynik w R — Enter zapisuje i czyści pole na kolejną',
    bt2PairFallback: 'BACKTEST',
    bt2Quick: 'SZYBKI WPIS',
    bt2OneR: amount => '1R = ' + amount,
    bt2NoR: 'bez 1R — wyniki w walucie',
    bt2Candle: 'Data świecy',
    bt2Result: 'Wynik',
    bt2Exits: { 'TP hit': 'TP', 'SL hit': 'SL', BE: 'BE', Manual: 'Ręcznie' },
    bt2Fast: 'szybko:',
    bt2More: 'setup i notatka ▾',
    bt2Less: 'mniej ▴',
    bt2SetupPh: 'zostaje na kolejne wpisy',
    bt2Note: 'Notatka',
    bt2NotePh: 'np. wejście za wcześnie',
    bt2AutoExit: 'Bez wybranego wyjścia: dodatni wynik = TP, −1R = SL, 0 = BE.',
    bt2SetRisk: 'Ustaw 1R w ✎ sesji, żeby wpisywać wyniki w R.',
    bt2Save: 'ZAPISZ ↵',
    bt2Spread: 'Rozkład wyników',
    bt2SpreadSub: 'ile transakcji w każdym przedziale R',
    bt2Sides: 'Long vs short',
    bt2Total: total => 'łącznie ' + total,
    tlNet: 'WYNIK NETTO',
    tlNetSub: (n, approx) => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + (approx ? ' · ≈ ' + approx : ''),
    tlWinSub: (wins, losses) => wins + ' ' + pl(wins, 'zysk', 'zyski', 'zysków') + ' · ' + losses + ' ' + pl(losses, 'strata', 'straty', 'strat'),
    tlAvg: 'ŚREDNIO / TRANSAKCJĘ',
    tlAvgSub: (win, loss) => 'zysk ' + win + ' · strata ' + loss,
    tlAvgNoLoss: 'jeszcze bez strat — stosunek zysku do straty policzy się po pierwszej',
    rcOkTitle: amount => 'Twój kapitał ryzyka: ' + amount,
    rcOkSub: 'tyle możesz przeznaczyć na trading bez ruszania poduszki',
    rcProgress: (savings, cushion, n) => savings + ' / ' + cushion + ' · ' + n + ' mies. wydatków',
    rcOpen: 'SZCZEGÓŁY ▾',
    rcClose: 'ZWIŃ ▴',
    tlList: 'Dziennik transakcji',
    tlListBacktest: 'Transakcje sesji',
    tlListSub: 'kliknij transakcję, żeby zobaczyć szczegóły',
    tlAll: 'Wszystkie',
    tlLong: 'Long',
    tlShort: 'Short',
    tlOff: 'Poza planem',
    tlRange: label => 'Okres: ' + label,
    tlRangeClear: 'Pokaż wszystkie daty',
    tlNoMatch: 'Brak transakcji dla tego filtra.',
    tlLots: lots => lots + ' lota',
    tlNoCtx: 'bez kontekstu',
    tlOnPlan: '✓ wg planu',
    tlOffPlan: list => '! poza planem: ' + list,
    tlBreakRules: 'zasady',
    tlBreakMm: 'kapitał',
    tlExitL: 'Wyjście',
    tlRiskL: 'Ryzyko',
    tlRulesL: 'Zasady',
    tlMmL: 'Zarz. kapitałem',
    tlReport: '↗ RAPORT',
    tlEdit: '✎ Edytuj',
    tlNew: '+ TRANSAKCJA',
    tlNewHint: 'wynik, kontekst i 4 pytania o dyscyplinę — ok. 20 sekund',
    tiButton: '⇪ IMPORT Z\u00a0TRADOVATE',
    tiHint: 'raport Performance wyeksportowany do CSV',
    tiUnknown: 'To nie jest eksport raportu Performance z\u00a0Tradovate. W\u00a0Tradovate otwórz Reports → Performance i\u00a0wyeksportuj go do CSV.',
    tiEmpty: 'W\u00a0tym eksporcie Performance nie ma zamkniętych transakcji.',
    tiFound: n => 'Znaleziono ' + n + ' ' + pl(n, 'transakcję', 'transakcje', 'transakcji'),
    tiWinLoss: (wins, losses) => 'zyskowne: ' + wins + ', stratne: ' + losses,
    tiAbout: 'Wynik tak, jak podaje go Tradovate — przed prowizjami. Częściowe wyjścia łączą się w\u00a0jedną transakcję, a\u00a0ceny i godziny trafiają do notatki. Transakcje wchodzą jako „wg planu” — otwórz którąś, żeby dopisać setup albo zaznaczyć złamaną zasadę. Transakcja, która już jest w\u00a0dzienniku, zostaje pominięta, więc ten sam eksport można zaimportować ponownie.',
    tiSkipped: lines => 'Wiersze, których nie udało się odczytać (pominięte): ' + lines,
    tiRoom: n => 'Plan darmowy zmieści w\u00a0tym miesiącu jeszcze ' + n + ' ' + pl(n, 'transakcję', 'transakcje', 'transakcji') + ': najstarsze zostaną zaimportowane, resztę można dodać w\u00a0przyszłym miesiącu albo z\u00a0Liquidity+.',
    tiNoRoom: 'Darmowe transakcje na ten miesiąc są wykorzystane. Import można powtórzyć w\u00a0przyszłym miesiącu albo z\u00a0Liquidity+.',
    tiImport: n => 'IMPORTUJ ' + n,
    tiCancel: 'Anuluj',
    tiClose: 'Gotowe',
    tiDone: n => n === 0 ? 'Nie ma nic nowego do zaimportowania.' : 'Dodano do dziennika: ' + n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + '.',
    tiDuplicates: n => 'Już w\u00a0dzienniku: ' + n + '.',
    tiOverLimit: n => 'Poza darmowym limitem tego miesiąca: ' + n + '.',
    tiTyped: n => (n === 1
      ? 'Jedna transakcja wygląda jak wpisana ręcznie — ten sam dzień, instrument, kierunek i\u00a0podobny wynik. Zostanie pominięta, żeby nic nie liczyło się podwójnie:'
      : n + ' ' + pl(n, 'transakcja wygląda', 'transakcje wyglądają', 'transakcji wygląda') + ' jak wpisane ręcznie — ten sam dzień, instrument, kierunek i\u00a0podobny wynik. Zostaną pominięte, żeby nic nie liczyło się podwójnie:'),
    tiTypedAs: (pair, result) => '↔ wpisana: ' + pair + ' ' + result,
    tiKeepTyped: 'Zaimportuj je mimo to',
    tiTypedSkipped: n => 'Pominięte jako wpisane ręcznie: ' + n + '.',
    tiUpdated: n => 'Zaktualizowane z\u00a0wcześniejszego importu (dalsze wyjścia z\u00a0pozycji): ' + n + '.',
    tlFormNew: 'NOWA TRANSAKCJA',
    tlFormEdit: 'EDYTUJESZ TRANSAKCJĘ',
    tlStep1: 'Transakcja',
    tlStep2: 'Kontekst',
    tlStep3: 'Dyscyplina',
    tlOptional: 'opcjonalnie',
    tlDateL: 'Data',
    tlPairL: 'Instrument',
    tlSideL: 'Kierunek',
    tlLotsL: 'Loty',
    tlPlL: 'Wynik netto',
    tlRecent: 'ostatnio:',
    tlSessionL: 'Sesja',
    tlSessionPh: 'np. Nowy Jork, NY AM',
    tlTfL: 'Interwał',
    tlSetupL: 'Setup',
    tlSetupPh: 'co było na wykresie, np. sweep + FVG',
    tlReportL: 'Link do raportu',
    tlQExit: 'Jak zamknięto transakcję?',
    tlQRisk: 'Wielkość ryzyka',
    tlQRules: 'Zgodnie z zasadami?',
    tlQMm: 'Zarządzanie kapitałem OK?',
    tlExits: { 'TP hit': 'TP hit', 'SL hit': 'SL hit', BE: 'BE', Manual: 'Ręcznie' },
    tlRisks: { FULL: 'Full', HALF: 'Half', QUARTER: 'Quarter' },
    tlVerdictAsk: 'Odpowiedz na pytania o zasady i kapitał.',
    tlVerdictOn: '✓ Ta transakcja liczy się jako zgodna z planem.',
    tlVerdictOff: '! Trafi do filtra „Poza planem”.',
    tlResultNeeded: 'Wpisz wynik netto — liczbę, z plusem przy zysku i minusem przy stracie.',
    tlDisciplineNeeded: 'Odpowiedz, czy transakcja była zgodna z zasadami i z zarządzaniem kapitałem — bez tego nie da się jej uczciwie ocenić.',
    tlCal: 'Kalendarz PnL',
    tlCalSub: (n, up, down) => n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji') + ' · ' + up + ' ' + pl(up, 'dzień', 'dni', 'dni') + ' na plus · ' + down + ' na minus',
    tlCalMixed: base => 'różne waluty — w ' + base + ' po dzisiejszym kursie',
    tlModes: ['Dzień', 'Tydzień', 'Miesiąc'],
    tlDow: ['PN', 'WT', 'ŚR', 'CZ', 'PT', 'SO', 'ND'],
    tlMonthsFull: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
    tlWeek: n => 'Tydzień ' + n,
    tlWeekShort: n => 'tydz. ' + n,
    tlTrShort: n => n + ' tr.',
    tlGain: 'zysk',
    tlLoss: 'strata',
    tlCalHint: 'kliknij okres, żeby przefiltrować dziennik',
    vhCar: 'POJAZD',
    vhNoName: 'Bez nazwy — dodaj ją przez ✎',
    vhOdo: 'Przebieg',
    vhSince: 'Od zakupu',
    vhTank: 'Bak',
    vhRange: 'Zasięg',
    vhPerKmSub: distance => 'paliwo + utrzymanie · ' + distance,
    vhPerKmNoDistance: 'pojawi się, gdy licznik przekroczy pierwszy odczyt',
    vhConsSub: distance => 'zmierzone na ' + distance,
    vhYear: year => 'WYDANO W ' + year,
    vhYearSub: fuel => 'w tym paliwo ' + fuel,
    vhOwnSub: (price, running) => 'zakup ' + price + ' + eksploatacja ' + running,
    vhNoPrice: 'tylko eksploatacja — cenę zakupu dodasz przez ✎',
    vhFuelSub: (n, total) => n + ' ' + pl(n, 'wpis', 'wpisy', 'wpisów') + ' · ' + total,
    vhFull: 'PEŁNY',
    vhFirstFull: 'pierwszy pełny bak',
    vhWaitsFull: 'czeka na pełny bak',
    vhFuelNew: '+ TANKOWANIE',
    vhFuelNewHint: 'spalanie liczy się między dwoma tankowaniami do pełna',
    vhFuelForm: 'NOWE TANKOWANIE',
    vhFuelEdit: 'EDYTUJESZ TANKOWANIE',
    vhOdoF: 'Licznik',
    vhVolF: 'Zatankowano',
    vhCostF: 'Zapłacono',
    vhFullToggle: 'Tankowanie do pełna',
    vhFullNote: 'Zostaw zaznaczone, gdy dystrybutor sam odbił. Dolanie zostanie policzone z następnym pełnym bakiem.',
    vhAdd: '+ DODAJ',
    vhSplit: 'Na co idą pieniądze',
    vhSplitSub: 'od zakupu, bez ceny auta',
    vhFuel: 'Paliwo',
    vhUpcoming: 'Nadchodzące',
    vhUpcomingSub: 'wyliczone z historii wpisów',
    vhUpcomingEmpty: 'Dodaj wpis z ubezpieczeniem, przeglądem technicznym albo serwis oznaczony jako wymiana oleju, a tutaj pojawi się następny termin.',
    vhPolicy: 'Odnowienie ubezpieczenia',
    vhOilMark: 'Wymiana oleju — licz od tego wpisu kolejną',
    vhOilTag: 'OLEJ',
    vhInspection: 'Przegląd techniczny',
    vhIcsFile: 'pojazd-terminy.ics',
    vhIcsCalendar: 'Pojazd — terminy',
    vhIcsTitle: 'Pobierz te terminy jako plik kalendarza (.ics), każdy z przypomnieniem 14 dni wcześniej',
    icsButton: '↓ KALENDARZ (.ICS)',
    icsFrom: 'Z Portfolio Tracker · liquidityraiders.pl',
    vhLastTime: (date, amount) => 'ostatnio ' + date + ' · ' + amount,
    vhInDays: n => 'za ' + n + ' ' + pl(n, 'dzień', 'dni', 'dni'),
    vhOverdueDays: n => n + ' ' + pl(n, 'dzień', 'dni', 'dni') + ' po terminie',
    vhSvcOdoF: 'Przebieg',
    vhOptional: 'opcjonalnie',
    vhOilAt: reading => ' przy ' + reading,
    vhOilEstimated: ' · przebieg z\u00a0najbliższego tankowania — wpisz go we wpisie serwisowym, żeby był dokładny',
    vhOil: 'Wymiana oleju',
    vhOilSub: (interval, date) => 'co ' + interval + ' · ostatnio ' + date,
    vhOilNoOdo: 'brak tankowania przed nią, od którego można liczyć',
    vhInDist: distance => 'za ' + distance,
    vhOverdueDist: distance => distance + ' po terminie',
    vhInterval: 'Interwał',
    vhThousands: n => n + ' tys.',
    vhService: 'Serwis i utrzymanie',
    vhServiceSub: (n, total) => n + ' ' + pl(n, 'wpis', 'wpisy', 'wpisów') + ' · ' + total,
    vhAll: 'Wszystko',
    vhServiceEmpty: 'Nie ma jeszcze wpisów serwisowych.',
    vhServiceNew: '+ WPIS SERWISOWY',
    vhServiceNewHint: 'naprawa, OC, opony, rejestracja',
    vhServiceForm: 'NOWY WPIS',
    vhServiceEdit: 'EDYTUJESZ WPIS',
    vhWhat: 'Co zrobiono',
    vhWhatPh: 'np. Wymiana oleju i filtrów',
    vhAmount: 'Kwota',
    vhServiceAdd: '+ DODAJ WPIS',
    ivValueSub: (n, foreign) => n + ' ' + pl(n, 'pozycja', 'pozycje', 'pozycji') + (foreign ? ' · ' + foreign + ' w walutach' : ''),
    ivCostSub: (n, month) => n + ' ' + pl(n, 'zakup', 'zakupy', 'zakupów') + (month ? ' od ' + month : ''),
    ivRes: 'NIEZREALIZOWANY WYNIK',
    ivResSub: (up, down) => up + ' na plusie · ' + down + ' na minusie',
    ivPctSub: 'od kosztu zakupu, bez dywidend',
    ivPositions: 'Pozycje',
    ivPricesSub: (base, rates) => 'wartości w ' + (base === 'PLN' ? 'zł' : base) + (rates ? ' · kurs ' + rates : ''),
    ivHistTitle: 'Sprawdza, ile każdy walor był wart w miesiącach bez ceny, żeby miniony miesiąc pokazywał ówczesną wycenę',
    ivColAsset: 'WALOR',
    ivColUnits: 'SZTUKI · ŚR. KOSZT',
    ivColPrice: 'CENA',
    ivColValue: 'WARTOŚĆ',
    ivColShare: 'UDZIAŁ',
    ivColRes: 'WYNIK',
    ivLots: n => n + ' ' + pl(n, 'ZAKUP', 'ZAKUPY', 'ZAKUPÓW'),
    ivLotsTitle: 'Pokaż zakupy tej pozycji',
    ivEditLot: 'Edytuj zakup',
    ivRemoveLot: 'Sprzedaj lub usuń',
    ivUnits: n => n + ' szt.',
    ivUnitShort: 'szt.',
    ivAvg: amount => 'śr. ' + amount,
    ivYieldLine: rate => rate + '% rocznie',
    ivNoRate: 'brak kursu',
    ivNoMonth: 'BEZ DATY',
    ivEmpty: 'Nie masz jeszcze inwestycji. Dodaj pierwszy zakup — akcje, ETF, metale, krypto albo gotówkę.',
    ivNew: '+ NOWY ZAKUP',
    ivNewHint: 'akcje, ETF, metale, krypto — ten sam ticker dopisze się do istniejącej pozycji',
    ivFormNew: 'NOWY ZAKUP',
    ivFormEdit: 'EDYTUJESZ ZAKUP',
    ivTicker: 'Ticker',
    ivTickerPh: cls => ({ Crypto: 'np. BTC, ETH', 'Bonds / cash': 'np. IB01.L', 'Real estate': 'np. VNQ', Cash: 'np. LOKATA' })[cls] || 'np. VWCE.DE, AAPL',
    ivName: 'Nazwa',
    ivNamePh: cls => ({ Crypto: 'np. Bitcoin', Retirement: 'np. Konto IKE', 'Bonds / cash': 'np. Obligacje skarbowe', 'Real estate': 'np. Mieszkanie w Krakowie', Cash: 'np. Konto oszczędnościowe' })[cls] || 'np. Vanguard FTSE All-World',
    ivClass: 'Klasa',
    ivUnitsF: 'Sztuki',
    ivCostF: 'Koszt za sztukę',
    ivPriceF: 'Cena teraz / szt.',
    ivPricePh: 'opcjonalnie',
    ivYieldF: 'Oprocentowanie',
    ivSinceF: 'Miesiąc zakupu',
    ivFormL: 'Forma',
    ivPhysical: 'Fizyczne (sztabki, monety)',
    ivPaper: 'Papierowe (ETF, ETC)',
    ivMetalL: 'Metal',
    ivMetals: { XAU: 'Złoto', XAG: 'Srebro', XPT: 'Platyna', XPD: 'Pallad' },
    ivMetalsOf: { XAU: 'Cena złota', XAG: 'Cena srebra', XPT: 'Cena platyny', XPD: 'Cena palladu' },
    ivWeightL: 'Jednostka',
    ivGrams: 'Gramy',
    ivOunces: 'Uncje trojańskie',
    ivUnitAbbr: { g: 'g', oz: 'oz t' },
    ivWeightF: 'Ilość',
    ivCostPer: per => 'Koszt za ' + per,
    ivPricePer: per => 'Cena teraz / ' + per,
    ivPaperTickerPh: 'np. PHAU.L, SGLN.L',
    ivPaperNamePh: 'np. ETC na fizyczne złoto',
    ivMetalNamePh: 'np. Sztabka 20 g, Krugerrand',
    ivMetalHint: (metal, month, price, stamp, piece) => metal + ' · ' + month + ': ' + price + ' czystego kruszcu'
      + (stamp ? ' · przy próbie ' + stamp + ': ' + piece : '') + ' — wpisana jako koszt; zmień ją, jeśli koszt był inny.',
    ivMetalLoading: 'Sprawdzam cenę z\u00a0miesiąca zakupu…',
    ivMetalNoRate: month => 'Brak kursu waluty za ' + month + ' — wpisz koszt ręcznie albo dodaj kurs w\u00a0Preferencjach.',
    ivMetalMissing: 'Nie ma ceny z\u00a0tego miesiąca — wpisz koszt ręcznie.',
    ivMetalManual: 'W Liquidity+ cena z\u00a0miesiąca zakupu wpisuje się sama. W\u00a0planie Free wpisz koszt ręcznie.',
    ivPhysicalNote: 'Wpisz masę sztabki lub monety w\u00a0wybranej jednostce. Wyceniana jest zawartość czystego kruszcu — masa × próba — według ceny uncji, przeliczonej na gramy, jeśli tak ją ważysz.',
    ivPurityL: 'Próba',
    ivPurityOther: 'Inna',
    ivPurityF: 'Próba',
    ivPurityNeeded: 'Wpisz próbę w\u00a0promilach, np. 900 — od 1 do 1000.',
    ivCurF: 'Waluta',
    ivAmountF: 'Kwota',
    ivNote: 'Kupujesz kawałek? Wpisz ułamek sztuki, np. 0,0472. Cena teraz jest wspólna dla całej pozycji — przy odświeżeniu nadpisze się sama.',
    ivAdd: '+ DODAJ ZAKUP',
    ivClasses: 'Podział według klas',
    ivClassesSub: 'udział w wartości rynkowej portfela',
    ivWhen: 'Kiedy kupiono',
    ivWhenSub: 'zakupy miesiąc po miesiącu, najnowsze na górze',
    ivPaidIn: 'wpłacono łącznie',
    guideLink: 'Poradnik',
    guideSub: 'jak korzystać z aplikacji',
    guideAria: 'Poradnik: jak korzystać z aplikacji (otwiera się w nowej karcie)',
    dbLeft: 'POZOSTAŁO',
    dbLeftSub: n => n + ' ' + pl(n, 'zobowiązanie', 'zobowiązania', 'zobowiązań'),
    dbPaid: 'SPŁACONO',
    dbPaidSub: (paid, original) => paid + ' z ' + original,
    dbRata: 'RATY MIESIĘCZNE',
    dbNoRata: n => n + ' ' + pl(n, 'dług', 'długi', 'długów') + ' bez ustalonej raty',
    dbAllRata: 'wszystkie z ustaloną ratą',
    dbNoDebtsSub: 'brak zapisanych długów',
    dbInt12: 'ODSETKI · 12 MIES.',
    dbInt12Sub: amount => '≈ ' + amount + '/mies. przy samych ratach',
    dbChart: 'Dług miesiąc po miesiącu',
    dbChartSub: 'stan na koniec miesiąca i prognoza na 12 miesięcy',
    dbLegRecorded: 'zapisane saldo',
    dbLegBase: 'same raty',
    dbLegPlan: 'z planem spłaty',
    dbQueueRate: 'najwyższe oprocentowanie na górze',
    dbQueueBalance: 'najmniejsze saldo na górze',
    dbKindLoan: 'Kredyt / pożyczka',
    dbKindCard: 'Karta kredytowa',
    dbCardTag: 'KARTA',
    dbCardRow: 'karta — saldo zmieniasz wpłatami',
    dbCardShort: 'karta',
    dbCardRataF: 'Stała spłata (opcjonalnie)',
    dbCardNote: 'Karta nie jest prognozowana: jej saldo zmienia się tylko wtedy, gdy zapiszesz wpłatę albo je poprawisz. Oprocentowanie służy planowi spłaty.',
    dbPayFuture: 'Wpłatę zapisujesz w\u00a0miesiącu, w\u00a0którym ją robisz. Miesiące po dzisiejszym to prognoza z\u00a0rat.',
    dbProjected: 'PROGNOZA',
    dbPayDayF: 'Dzień płatności',
    dbPayDayShort: d => d + '. dnia mies.',
    dbPayDayNeeded: 'Dzień płatności to liczba całkowita od 1 do 31.',
    dbIcsTitle: 'Pobierz raty jako plik kalendarza (.ics) do Google, Apple albo Outlooka, z przypomnieniem dzień wcześniej',
    dbIcsNone: 'Ustaw dzień płatności przy długu (✎), żeby dodać jego raty do kalendarza.',
    dbIcsFile: 'raty-dlugow.ics',
    dbIcsCalendar: 'Raty długów',
    dbIcsSummary: (name, amount) => 'Rata: ' + name + ' — ' + amount,
    dbIcsLeft: n => pl(n, 'Pozostała', 'Pozostały', 'Pozostało') + ' ' + n + ' ' + pl(n, 'rata', 'raty', 'rat') + ' · z Portfolio Tracker',
    dbProjectedTitle: 'Miesiąc po dzisiejszym: salda są wyliczone od ostatniego znanego przy płaceniu samych rat',
    dbCount: n => n + ' ' + pl(n, 'pozycja', 'pozycje', 'pozycji'),
    dbRepaid: (pct, paid, original) => 'spłacono ' + pct + ' · ' + paid + ' z ' + original,
    dbFee: amount => 'prowizja ' + amount,
    dbPaidOff: 'spłacony ✓',
    dbNoRataRow: 'brak raty — ustaw ją, żeby policzyć termin',
    dbMonthsLeft: (n, month) => 'jeszcze ~' + n + ' mies. · do ' + month,
    dbInterest: amount => 'odsetki ' + amount,
    dbRataLine: amount => 'rata ' + amount,
    dbRataDerived: 'z liczby rat',
    dbNoRataShort: 'bez raty',
    dbPayOpen: '+ WPŁATA',
    dbPayClose: '× ZAMKNIJ',
    dbPayIn: key => 'Wpłata ' + inMonthPl(key),
    dbPayHint: 'puste pole = rata',
    dbNew: '+ NOWY DŁUG',
    dbNewHint: 'kredyt, karta, raty 0% — wpisz saldo i ratę, a trafi do kolejki i planu spłaty',
    dbFormNew: 'NOWY DŁUG',
    dbFormEdit: 'EDYTUJESZ DŁUG',
    dbLender: 'Bank / pożyczkodawca',
    dbLenderPh: 'np. mBank, Allegro Pay',
    dbBalNow: 'Saldo teraz',
    dbOrig: 'Kwota pierwotna',
    dbRateF: 'Oprocentowanie',
    dbRataF: 'Rata',
    dbMonthsF: 'lub liczba miesięcy',
    dbFeeF: 'Prowizja',
    dbCurrencyF: 'Waluta',
    dbAdd: '+ DODAJ DŁUG',
    dbPlan: 'Plan spłaty',
    dbPlanSub: 'nadpłata i raty spłaconych długów trafiają do pierwszego w kolejce',
    dbStrat: { rate: ['Najdroższy najpierw', 'najwyższe oprocentowanie · najmniej odsetek'], balance: ['Najmniejszy najpierw', 'najniższe saldo · szybciej znikają pozycje'] },
    dbExtra: 'Nadpłata co miesiąc',
    dbFree: 'BEZ DŁUGU',
    dbBaseFree: month => 'same raty: ' + month,
    dbNever: 'nigdy',
    dbIntTotal: 'ODSETKI ŁĄCZNIE',
    dbBaseInt: amount => 'same raty: ' + amount,
    dbBaseIntNever: 'same raty: rośnie bez końca',
    dbOrder: 'KOLEJNOŚĆ I TERMIN SPŁATY',
    dbPaidOffShort: 'spłacony',
    dbNoteNoRata: (names, n) => names + (n === 1 ? ' nie ma raty — bez planu ten dług nie zniknie.' : ' nie mają rat — bez planu te długi nie znikną.')
      + ' Plan spłaci ' + (n === 1 ? 'go' : 'je') + ' z rat, które zwolnią się po spłacie pozostałych.',
    dbNoteSave: (months, amount) => 'Oszczędzasz ' + months + ' ' + pl(months, 'miesiąc', 'miesiące', 'miesięcy') + ' i ' + amount + ' odsetek wobec płacenia samych rat.',
    dbNoteMove: 'Przesuń suwak, żeby zobaczyć, ile da nadpłata.',
    dbEmpty: 'Nie ma długów. Jeśli masz kredyt, kartę albo raty, dodaj je tutaj — trafią do planu spłaty.',
    cfIncome: 'PRZYCHÓD',
    cfSpend: 'WYDATKI',
    cfLeft: 'ZOSTAJE',
    cfFixed: 'OPŁATY STAŁE',
    cfSources: n => n + ' ' + pl(n, 'źródło', 'źródła', 'źródeł'),
    cfItems: (n, c) => n + ' ' + pl(n, 'pozycja', 'pozycje', 'pozycji') + ' · ' + c + ' ' + pl(c, 'kategoria', 'kategorie', 'kategorii'),
    cfOfIncome: pct => pct + ' przychodu',
    cfPerMonth: amount => amount + '/mies.',
    cfFixedSub: (pct, n) => (pct === null ? '' : pct + ' przychodu · ') + n + ' ' + pl(n, 'opłata', 'opłaty', 'opłat'),
    cfOpenFixed: '+ NOWA OPŁATA STAŁA',
    cfOpenFixedHint: 'np. czynsz, Netflix, ubezpieczenie — zapisujesz ją raz, a wraca w swoim cyklu',
    cfPostHint: (n, key) => n + ' ' + pl(n, 'opłata', 'opłaty', 'opłat') + ' z ' + monthIn(MONTHS_GENITIVE_PL, key)
      + ' ' + pl(n, 'czeka', 'czekają', 'czeka') + ' na wpisanie do listy transakcji.',
    cfAllPostedHint: key => 'Opłaty z ' + monthIn(MONTHS_GENITIVE_PL, key) + ' są już na liście transakcji.',
    cfShowIncome: 'Pokaż tylko przychody',
    cfShowSpend: 'Pokaż tylko wydatki',
    cfShowAll: 'Pokaż wszystko',
    cfFlowTitle: 'Gdzie poszedł przychód',
    cfFlowHint: 'kliknij kategorię, żeby przefiltrować listę',
    cfFlowLeft: 'Zostaje',
    cfTx: 'Transakcje',
    cfAdd: '+ DODAJ',
    cfClose: '× ZAMKNIJ',
    cfAll: 'Wszystko',
    cfIncomes: 'Przychody',
    cfExpenses: 'Wydatki',
    cfOut: 'Wydatek',
    cfIn: 'Przychód',
    cfNameOut: 'na co? np. Biedronka',
    cfNameIn: 'skąd? np. wynagrodzenie',
    cfCustom: 'własna kategoria, np. Prezenty',
    cfRecurringToggle: 'opłata stała co miesiąc',
    cfDay: 'dnia',
    cfCancel: 'Anuluj',
    cfSave: 'ZAPISZ',
    cfCount: n => n + ' ' + pl(n, 'pozycja', 'pozycje', 'pozycji'),
    cfShare: pct => pct + '% przychodu',
    cfFixedBadge: 'STAŁA',
    cfRate: (original, rate) => original + ' · kurs ' + rate,
    cfNoRate: 'brak kursu',
    cfEmptyTx: 'W tym miesiącu nie ma jeszcze wpisów. Dodaj pierwszy albo zaimportuj wyciąg.',
    cfFilterClear: 'Wyczyść filtr',
    cfFxSub: (pct, monthly, rarer) => (pct === null ? '' : pct + ' przychodu · ') + monthly + ' ' + pl(monthly, 'miesięczna', 'miesięczne', 'miesięcznych') + ', ' + rarer + ' rzadziej',
    cfMonthly: 'miesięcznie',
    cfPostN: n => 'WPISZ ' + n + ' DO TRANSAKCJI',
    cfAllPosted: '✓ WSZYSTKIE W TRANSAKCJACH',
    cfAllPostedTitle: 'Opłaty stałe z tego miesiąca są już w transakcjach. Kliknij, żeby dodać je ponownie.',
    cfCharged: amount => 'obciążenia ' + amount,
    cfEveryHead: { 1: 'CO MIESIĄC', 3: 'CO KWARTAŁ', 6: 'CO PÓŁ ROKU', 12: 'CO ROK' },
    cfPer: { 3: 'kwartał', 6: 'pół roku', 12: 'rok' },
    cfIncomeHead: 'PRZYCHODY STAŁE',
    cfApproxMonth: amount => '≈ ' + amount + '/mies.',
    cfNewFixed: 'NOWA OPŁATA STAŁA',
    cfEditFixed: 'EDYTUJESZ OPŁATĘ',
    cfCycle: 'CYKL PŁATNOŚCI',
    cfCycles: { 1: ['Co miesiąc', '12× w roku'], 3: ['Co kwartał', '4× w roku'], 6: ['Co pół roku', '2× w roku'], 12: ['Co rok', '1× w roku'] },
    cfFxName: 'nazwa, np. Netflix',
    cfInMonth: 'w miesiącu',
    cfFromMonth: 'od',
    cfAddFixed: '+ DODAJ OPŁATĘ',
    cfSaveChanges: 'ZAPISZ ZMIANY',
    cfMore: 'Data końca i wstrzymanie →',
    cfFxNoteA: 'Opłaty z tego miesiąca dodajesz do transakcji przyciskiem u góry. Na liście mają znacznik ',
    cfFxNoteB: ', a aplikacja pilnuje, żeby nic się nie zdublowało. Kwartalne, półroczne i roczne liczą się tu proporcjonalnie na miesiąc.',
    cfPaused: 'wstrzymana',
    cfMonths: MONTHS_FULL_PL,
    ovIncome: name => 'PRZYCHÓD · ' + name.toUpperCase(),
    ovSpend: name => 'WYDATKI · ' + name.toUpperCase(),
    ovSaved: name => 'OSZCZĘDNOŚCI · ' + name.toUpperCase(),
    ovIncomeShort: 'PRZYCHÓD',
    ovSpendShort: 'WYDATKI',
    ovSavedShort: 'OSZCZĘDNOŚCI',
    ovDebtShort: 'DŁUG',
    ovMonthName: key => monthIn(MONTHS_FULL_PL, key),
    ovMonthOnMonth: value => value + ' m/m',
    ovTopShare: (pct, name, amount) => pct + ' to ' + name.toLowerCase() + ' (' + amount + ')',
    ovTopShareShort: (pct, name) => pct + ' to ' + name.toLowerCase(),
    ovOfIncome: pct => pct + ' przychodu',
    ovDebtSub: (amount, n) => 'raty ' + amount + '/mies. · ' + n + ' ' + pl(n, 'pozycja', 'pozycje', 'pozycji'),
    ovDebtSubShort: amount => 'raty ' + amount + '/mies.',
    ovTrendSub: 'aktywa minus zobowiązania',
    ovNoDataBefore: key => 'brak danych przed ' + monthIn(MONTHS_INSTRUMENTAL_PL, key),
    ovNow: 'TERAZ',
    ovActual: 'rzeczywista',
    ovForecast: 'prognoza wg dzisiejszych cen',
    ovForecastShort: 'prognoza',
    ovBelowZero: 'poniżej zera',
    ovNoData: 'brak danych',
    ovSlide: 'przesuń palcem po wykresie',
    ovPicked: 'wybrany miesiąc',
    ovClickToOpen: 'kliknij, aby otworzyć ten miesiąc',
    ovForecastTag: 'PROGNOZA',
    ovFirstMonth: 'pierwszy miesiąc w rejestrze',
    ovAtPrices: 'wg dzisiejszych cen',
    ovVsPrev: (value, key) => value + ' względem ' + monthIn(MONTHS_GENITIVE_PL, key),
    ovNet: 'NETTO',
    ovDebts: 'Długi',
    ovInvestments: 'Inwestycje',
    ovCash: 'Gotówka',
    ovLeftDebts: '← długi',
    ovRightAssets: 'aktywa →',
    ovInstalments: amount => 'raty ' + amount + '/mies.',
    ovUnrealised: amount => amount + ' niezreal.',
    ovFillCash: 'uzupełnij saldo',
    ovCoverPre: 'Aktywa pokrywają ',
    ovCoverPost: ' długu.',
    ovCover: 'pokrycie',
    ovTrendEmpty: 'Tu pojawi się Twoja wartość netto miesiąc po miesiącu — wystarczy dodać przychód, dług, oszczędności albo inwestycję.',
    ovNetRecorded: 'stan zapisany na koniec miesiąca · podział poniżej z dzisiejszą gotówką',
    ovCoverTimes: n => 'Aktywa są ' + n + ' razy większe niż dług.',
    ovNoDebt: 'Brak długów: wszystko, co tu widać, należy do Ciebie.',
    ovAvg3: 'średnia z 3 mies.',
    ovAvg: 'średnia',
    ovAboutAvg: '≈ średnia',
    ovTotal: 'Razem',
    ovAvgTotal: 'średnia',
    ovVsAvg: value => value + ' wobec średniej',
    ovOverNote: 'czerwone tylko tam, gdzie wydajesz ponad 20% więcej niż zwykle',
    ovNoAverage: 'nie ma jeszcze wcześniejszych miesięcy do porównania',
    ovUpcomingSub: (days, from, to) => 'opłaty stałe w ciągu ' + days + ' dni · ' + from + ' – ' + to,
    ovUpcomingSubShort: days => 'opłaty stałe · ' + days + ' dni',
    ovForeign: (amount, converted, rate) => 'w tym ' + amount + ' ≈ ' + converted + (rate ? ' · kurs ' + rate : ''),
    ovThisWeek: 'TEN TYDZIEŃ',
    ovLater: 'PÓŹNIEJ',
    ovToday: 'DZIŚ',
    ovShowAll: n => 'POKAŻ WSZYSTKIE (' + n + ')',
    ovCollapse: 'ZWIŃ',
    rvForWeek: 'Na ten tydzień:',
    rvPrepared: (date, next) => 'Przygotowano ' + date + (next ? ' · kolejny przegląd od ' + next : '') + '.',
    rvByClaude: date => 'przygotował Claude · ' + date,
    wiSummary: (extra, invest, ret, years) => 'nadpłata ' + extra + ' · inwestycja ' + invest + ' · ' + ret + ' · ' + years,
    wiOpen: 'OTWÓRZ SYMULATOR →',
    wiBack: 'Przegląd',
    wiInYears: years => 'za ' + years,
    wiChangeGivesLabel: 'zmiana daje',
    wiDebtFreeLabel: 'bez długów',
    wiChangeGives: amount => 'Nadpłata i inwestowanie dają ' + amount,
    wiShortAround: month => 'Gotówka spadnie poniżej zera ok. ' + month + '.',
    wiSetSliders: 'Ustaw w symulatorze nadpłatę lub inwestycję, żeby zobaczyć różnicę.',
    wiExtra: 'Nadpłata długów',
    wiInvest: 'Inwestuj co miesiąc',
    wiReturn: 'Zakładany roczny zwrot',
    wiYears: 'Horyzont',
    wiPerMonth: amount => amount + ' / mies.',
    wiYearsValue: n => n + ' ' + pl(n, 'rok', 'lata', 'lat'),
    wiHeadline: (years, amount, baseline) => 'Za ' + years + ': ' + amount + ' (bez zmian: ' + baseline + ')',
    wiGain: amount => 'Zmiana daje ' + amount,
    wiLoss: amount => 'Zmiana kosztuje ' + amount,
    wiDebtFree: (scenario, baseline) => 'Bez długów: ' + scenario + ' (bez zmian: ' + baseline + ')',
    wiNever: 'nie w tym horyzoncie',
    wiDebtFreeSame: date => 'Bez długów: ' + date,
    wiNoDebt: 'brak długów',
    wiInterest: amount => 'Zaoszczędzone odsetki: ' + amount,
    wiShort: date => 'Uwaga: przy tych kwotach oszczędności spadną poniżej zera około ' + date + '.',
    wiLegendNow: 'bez zmian',
    wiLegendChange: 'ze zmianą',
    wiHint: 'Przesuń suwak nadpłaty albo inwestowania, żeby zobaczyć różnicę.',
    wiHeadlineSame: (years, amount) => 'Za ' + years + ' bez zmian: ' + amount,
    wiAxis: symbol => 'wartość netto na koniec każdego roku, w tys. ' + symbol,
    wiNote: 'Prognoza, nie przepowiednia: przychody i wydatki pozostają na poziomie z trzech ostatnich miesięcy, a zwrot jest stały i taki, jak ustawisz.',
    rcTitle: 'Twój kapitał ryzyka',
    rcNone: 'Oszczędności nie pokrywają jeszcze poduszki — na trading przeznaczaj tylko to, co zostaje z miesiąca',
    rcUnknown: 'Zapisuj wydatki i oszczędności, a policzymy tu bezpieczną kwotę na trading.',
    rcWarning: share => 'Uwaga: w tym miesiącu trading zabrał już ' + share + ' kapitału ryzyka',
    rcSavings: 'oszczędności',
    rcCushion: 'poduszka',
    rcCushionSub: n => n + ' ' + pl(n, 'miesiąc', 'miesiące', 'miesięcy') + ' wydatków',
    rcRunway: 'bez dochodu',
    rcRunwayValue: n => n + ' ' + pl(n, 'miesiąc', 'miesiące', 'miesięcy'),
    rcRunwaySub: amount => 'przy ' + amount + ' miesięcznie',
    rcTraded: 'trading w tym miesiącu',
    rcTradedSub: share => share + ' kapitału ryzyka',
    rcMonths: 'Poduszka',
    rcMonthsOption: n => n + ' mies.',
    rcUnconverted: list => 'Transakcje w ' + list + ' zostały pominięte — w Preferencjach brakuje dla nich kursu.',
    rcNote: 'Poduszka to pieniądze na życie, nie na trading: tyle miesięcy Twoich średnich wydatków z trzech ostatnich miesięcy. Kapitał ryzyka to tylko to, co oszczędności mają ponad nią. Backtesty nigdy nie są wliczane.',
    rcPropCheck: 'Korzystam z prop firmy',
    rcPropCheckSub: 'straty pokrywa kapitał firmy, nie Twoje oszczędności — więc poduszka nie jest potrzebna',
    rcPropTitle: 'Trading na kontach prop firm',
    rcPropSub: 'Twoje oszczędności nie są zagrożone, tylko opłaty — poduszka nie jest potrzebna',
    rcPropMonth: (spent, back) => 'w tym miesiącu: opłaty ' + spent + ' · wypłaty ' + back,
    rcPropSavingsSub: 'nie idą na trading',
    rcPropFees: 'opłaty w tym miesiącu',
    rcPropFeesSub: 'challenge, resety, aktywacje',
    rcPropBack: 'wypłaty w tym miesiącu',
    rcPropBackSub: 'wypłaty i zwroty',
    rcPropTradedSub: 'na kapitale firmy',
    rcPropNote: 'Na koncie prop firmy stratny dzień zabiera pieniądze firmy, a nie Twoje: trading kosztuje Cię opłaty, a zarabia wypłaty. Obie kwoty pochodzą z kont w zakładce „Prop firmy”. Handlujesz też za własne pieniądze? Odznacz pole, a poduszka wróci.',
    propVerdictUp: amount => 'Prop trading się opłaca: ' + amount,
    propVerdictDown: amount => 'Na razie prop trading kosztuje Cię ' + amount,
    propVerdictNone: 'Dodaj pierwsze konto prop firmy i zapisz, ile kosztowało. Wypłaty trafiają w to samo miejsce.',
    propVerdictNote: 'Wszystkie opłaty, resety i aktywacje zestawione ze wszystkimi wypłatami i zwrotami, ze wszystkich Twoich kont.',
    propCosts: 'wydane',
    propCostsSub: 'opłaty, resety, aktywacje',
    propReturns: 'odzyskane',
    propReturnsSub: n => 'wypłaty i zwroty: ' + n,
    propRoi: 'zwrot z opłat',
    propPassed: 'zaliczone',
    propPassedSub: (passed, failed) => 'zaliczone: ' + passed + ' · oblane: ' + failed,
    propPerPayout: 'opłaty na wypłatę',
    propPerPayoutSub: 'ile kosztowała jedna wypłata',
    propByFirm: 'Według firm',
    propMixed: 'Twoje konta są w kilku walutach, więc sumy zostały pominięte.',
    propNew: 'Nowe konto',
    propFirm: 'firma, np. Apex',
    propProgram: 'program, np. 50K',
    propSize: 'wielkość',
    propFee: 'zapłacona opłata',
    propAmount: 'kwota',
    propAdd: 'DODAJ KONTO',
    propFirmNeeded: 'Podaj nazwę firmy.',
    propAmountNeeded: 'Podaj kwotę.',
    propStatus: { challenge: 'Challenge', phase2: 'Faza 2', funded: 'Funded', failed: 'Oblane', closed: 'Zamknięte' },
    pfHeroCap: 'BILANS PROP TRADINGU · WSZYSTKIE KONTA',
    pfHeroDown: (amount, covered) => 'na razie prop trading kosztuje Cię ' + amount + ' — ' + (covered === null ? 'pierwsza wypłata to zmieni' : 'wypłaty pokryły ' + covered + '% opłat'),
    pfHeroUp: amount => 'wypłaty pokryły opłaty i dały ' + amount + ' zysku',
    pfHeroEven: 'wypłaty pokryły opłaty co do grosza',
    pfHeroNothing: 'nie ma jeszcze opłat ani wypłat — dopisz, ile kosztowało konto',
    pfSpent: amount => 'wydane ' + amount,
    pfBack: amount => 'odzyskane ' + amount,
    pfRoiLater: 'policzymy po pierwszej wypłacie',
    pfPerPayout: amount => 'jedna wypłata kosztowała ' + amount + ' opłat',
    pfPassedSub: (passed, failed, active) => 'funded: ' + passed + ' · oblane: ' + failed + ' · w toku: ' + active,
    pfAccounts: 'KONTA',
    pfAccountsSub: 'koszty trafiają do Przychodów i wydatków jako „Opłaty”, wypłaty jako przychód — chyba że wybierzesz „tylko tutaj”',
    pfNew: '+ NOWE KONTO',
    pfNewSub: 'firma, program i opłata — opłata zapisze się od razu',
    pfNewTitle: 'NOWE KONTO',
    pfFirm: 'Firma', pfFirmPh: 'np. Apex',
    pfProgram: 'Program', pfProgramPh: 'np. 50K',
    pfSize: 'Wielkość konta', pfFee: 'Zapłacona opłata', pfCurrency: 'Waluta',
    pfPopular: 'popularne:',
    pfAdd: '+ DODAJ KONTO',
    pfSince: day => 'od ' + day,
    pfNet: 'bilans konta',
    pfSettingsTitle: 'Zasady i ustawienia',
    pfRulesAdd: '+ ZASADY KONTA',
    pfRulesAddSub: 'dzienny limit, drawdown i cel — zobaczysz, ile zostało miejsca',
    pfBalance: 'SALDO KONTA',
    pfTarget: 'Cel zysku', pfTargetDone: '✓ cel osiągnięty', pfTargetLeft: amount => 'brakuje ' + amount,
    pfDaily: 'Dzienny limit straty', pfLeft: amount => 'zostało ' + amount,
    pfDailySub: (today, limit) => 'dziś ' + today + ' z limitu ' + limit,
    pfDd: 'Maks. drawdown', pfDdSub: (floor, type) => 'konto zamyka się przy ' + floor + ' · ' + type,
    pfSettings: 'ZASADY I USTAWIENIA',
    pfDailyPh: 'np. 1250', pfDdPh: 'np. 2500', pfTargetPh: 'np. 3000',
    pfDelete: 'Usuń konto',
    pfEntries: 'OPŁATY I WYPŁATY',
    pfEntryCount: n => n + ' ' + pl(n, 'wpis', 'wpisy', 'wpisów'),
    pfToFees: '→ budżet: Opłaty', pfToIncome: '→ budżet: przychód', pfOnlyHere: 'tylko tutaj',
    pfRemoveEntry: 'Usuń ten wpis',
    pfEntryAdd: '+ OPŁATA / WYPŁATA',
    pfDate: 'Data', pfAmount: 'Kwota',
    pfToBudget: 'Dopisz do budżetu', pfOnlyHereOption: 'Tylko tutaj',
    pfLog: '+ DODAJ',
    propKind: { fee: 'Opłata za challenge', reset: 'Reset', activation: 'Aktywacja', payout: 'Wypłata', refund: 'Zwrot opłaty' },
    propInLedger: 'w budżecie',
    propToLedger: { yes: 'dopisz do budżetu', no: 'tylko tutaj' },
    propLog: 'DODAJ',
    propNoEvents: 'Na tym koncie nie zapisano jeszcze opłat ani wypłat.',
    propDelete: 'USUŃ KONTO',
    propDeleteAsk: 'Usunąć to konto ze wszystkim, co na nim zapisano, razem z wpisami dodanymi do budżetu? Tego nie da się cofnąć.',
    propLedgerNote: 'Koszty trafiają do wydatków jako Opłaty, a wypłaty do przychodów, w miesiącu, w którym były — chyba że wybierzesz „tylko tutaj”.',
    adLabel: 'SPONSOROWANE',
    adSettings: 'zmień',
    adConsentBody:
      'Wersja darmowa wyświetla tutaj jedną reklamę. Działa ona w odizolowanej ramce — sieć '
      + 'reklamowa nie widzi tej strony, Twoich kwot ani zaimportowanych danych. '
      + 'Czy może używać plików cookie, aby dopasować reklamy do Ciebie?',
    adConsentYes: 'ZEZWÓL NA SPERSONALIZOWANE',
    adConsentNo: 'TYLKO NIESPERSONALIZOWANE',
    cashAmount: 'kwota',
    cashNote:
      'Gotówka trzymana jako część alokacji. Bez sztuk i bez ceny zakupu — gotówka traci siłę '
      + 'nabywczą i właśnie to pokazuje jej kolumna zysku. Lokata albo konto oszczędnościowe '
      + 'mogą to nadrobić: podaj oprocentowanie, a odsetki zostaną doliczone z roczną kapitalizacją, '
      + 'licząc od miesiąca wpłaty.',
    cashYieldNote: 'Opcjonalne. Wpisz oprocentowanie, które konto faktycznie płaci — po podatku, jeśli jest pobierany.',
    holdNote:
      'Obie kwoty dotyczą jednej sztuki, nie całej pozycji. Koszt/szt. to cena jednej sztuki '
      + 'w chwili zakupu, cena/szt. — jej dzisiejsza wartość. Różnica między nimi to '
      + 'wynik pozycji.',
    holdNoteTotal:
      'Znasz tylko kwotę łączną? Podziel ją przez liczbę sztuk — sumy poniżej '
      + 'przeliczają się na bieżąco, więc od razu sprawdzisz, czy zgadzają się '
      + 'z zapłaconą kwotą.',
    apiRefused: 'Serwer tego nie przyjął. Sprawdź liczby i spróbuj ponownie.',
    apiRefusedField: field => `Serwer nie przyjął pola „${field}” — sprawdź tę wartość.`,
    apiNotFound: 'Tego wpisu już nie ma — mógł zostać usunięty na innym urządzeniu.',
    apiConflict: 'To już istnieje.',
    apiForbidden: 'Nie masz do tego uprawnień.',
    apiUnauthorized: 'Nieprawidłowy e-mail lub hasło.',
    apiWrongPassword: 'To nie jest Twoje hasło.',
    apiRateLimited: 'Zbyt wiele prób — odczekaj chwilę i spróbuj ponownie.',
    apiQuotesOff: 'Pobieranie notowań jest na tym serwerze wyłączone.',
    apiQuotesDown: 'Dostawca notowań nie odpowiedział. Spróbuj za chwilę.',
    apiPlanImports: 'Wykorzystano już wszystkie importy wyciągów w\u00a0planie Free. W\u00a0Liquidity+ import nie ma limitu.',
    apiPlanBacktests: 'Plan Free obejmuje jedną sesję backtestu i\u00a0jest ona już założona. W\u00a0Liquidity+ nie ma limitu — możesz też usunąć sesję i\u00a0założyć nową.',
    apiPlanProp: 'Plan Free obejmuje jedno konto prop firmy i\u00a0jest ono już dodane. W\u00a0Liquidity+ nie ma limitu — możesz też usunąć konto i\u00a0dodać nowe.',
    apiPlanTaxes: 'Zestawienia podatkowe są częścią Liquidity+.',
    apiPlanJournal: 'Plan Free pozwala dodać ' + FREE.journalPerMonth + ' '
      + pl(FREE.journalPerMonth, 'transakcję', 'transakcje', 'transakcji')
      + ' w miesiącu, a limit na ten miesiąc został wykorzystany. W Liquidity+ dziennik nie ma limitu.',
    apiPlanQuotes: 'Automatyczne notowania są dostępne w Liquidity+. Cenę nadal można wpisać ręcznie.',
    apiAiQuota: 'Miesięczny limit podpowiedzi w Liquidity+ został wykorzystany. Odnowi się pierwszego dnia miesiąca.',
    /* Z miesiącem w dopełniaczu: „15 stycznia 2027”. */
    dateFull: iso => {
      const date = new Date(iso);
      return date.getUTCDate() + ' ' + MONTHS_GENITIVE_PL[date.getUTCMonth()] + ' ' + date.getUTCFullYear();
    },
    planSection: 'Plan',
    planStarts: date => 'Plany Free i Liquidity+ zaczną obowiązywać ' + date + '. Do tego dnia wszystko działa bez limitów, tak jak teraz.',
    planGiftAhead: date => 'Od tego dnia to konto będzie miało Liquidity+ za darmo do ' + date + ' — to podziękowanie dla osób, które są z nami od początku.',
    planTrial: date => 'Okres próbny do ' + date + '. Potem konto przejdzie na plan Free — bez żadnych opłat.',
    planGift: date => 'Podziękowanie dla osób, które są z nami od początku: Liquidity+ za darmo do ' + date + '.',
    planGranted: 'Plan Liquidity+ przyznany temu kontu bezterminowo.',
    planGrantedUntil: date => 'Plan Liquidity+ przyznany temu kontu do ' + date + '.',
    planDemo: 'Konto demo pokazuje wszystko, co obejmuje Liquidity+, poza podpowiedziami kategorii AI.',
    planFreeNote: 'Pełny rejestr finansów bez limitu wpisów.',
    planIncludes: 'Liquidity+: dziennik bez miesięcznego limitu, backtest i\u00a0konta prop firm bez limitu, zestawienia podatkowe (Polska, USA, Niemcy, Wielka Brytania), automatyczne notowania, podpowiedzi kategorii AI i\u00a0przegląd tygodnia.',
    planSeePlans: 'ZOBACZ LIQUIDITY+',
    planUsedBacktests: (used, limit) => 'Sesje backtestu: ' + used + ' z ' + limit,
    planUsedProp: (used, limit) => 'Konta prop firm: ' + used + ' z ' + limit,
    planBacktestsUsed: (used, limit) => 'Plan Free — sesje backtestu: ' + used + ' z ' + limit + '.',
    planBacktestsSpent: 'Plan Free obejmuje jedną sesję backtestu. W\u00a0Liquidity+ nie ma limitu; obecna sesja działa dalej w\u00a0każdym planie.',
    planPropUsed: (used, limit) => 'Plan Free — konta prop firm: ' + used + ' z ' + limit + '.',
    planPropSpent: 'Plan Free obejmuje jedno konto prop firmy. W\u00a0Liquidity+ nie ma limitu; obecne konto działa dalej w\u00a0każdym planie.',
    planTaxesTitle: 'Zestawienia podatkowe',
    planTaxesLocked: 'Kwoty do zeznania wyliczone z\u00a0dziennika: w\u00a0Polsce PIT-38 — każda transakcja po kursie NBP z\u00a0dnia roboczego przed nią, dywidendy z\u00a0odliczeniem podatku zapłaconego za granicą i\u00a0straty z\u00a0lat poprzednich; w\u00a0USA Form 6781 — kontrakty terminowe i\u00a0opcje na indeksy w\u00a0podziale 60/40; w\u00a0Niemczech Anlage KAP, w\u00a0Wielkiej Brytanii SA108. Część Liquidity+; dziennik pozostaje bezpłatny.',
    planKeepData: 'Niezależnie od planu dane pozostają Twoje: po zakończeniu planu nic nie jest ukrywane ani usuwane.',
    planUsedImports: (used, limit) => 'Wykorzystane importy wyciągów: ' + used + ' z ' + limit,
    planUsedJournal: (used, limit) => 'Transakcje dodane w tym miesiącu: ' + used + ' z ' + limit,
    planImportsUsed: (used, limit) => 'Plan Free — wykorzystane importy wyciągów: ' + used + ' z ' + limit + '.',
    planImportsSpent: 'Darmowe importy wyciągów zostały wykorzystane. W\u00a0Liquidity+ import nie ma limitu.',
    planJournalUsed: (used, limit) => 'Plan Free — transakcje dodane w tym miesiącu: ' + used + ' z ' + limit + '.',
    planJournalSpent: 'Limit ' + FREE.journalPerMonth + ' transakcji na ten miesiąc został wykorzystany. W Liquidity+ dziennik nie ma miesięcznego limitu.',
    planQuotesNote: 'Automatyczne notowania są dostępne w Liquidity+. W planie Free wpisz cenę ręcznie — wycena działa tak samo.',
    aiServerOn: (used, limit) => 'W tym miesiącu wykorzystano ' + used + ' z ' + limit + ' opisów.',
    aiServerOff: 'Podpowiedzi kategorii są częścią planu Liquidity+.',
    planRegionLabel: 'Ceny dla',
    planRegions: {
      'eu:PLN': 'Unia Europejska · złoty',
      'eu:EUR': 'Unia Europejska · euro',
      'global:USD': 'USA, Kanada, Australia i inne · dolar',
      'global:GBP': 'Wielka Brytania · funt',
      'global:CHF': 'Szwajcaria · frank',
      'emerging:USD': 'Pozostałe kraje · dolar'
    },
    planBuyMonth: amount => amount + ' MIESIĘCZNIE',
    planBuyYear: amount => amount + ' ROCZNIE',
    planYearNote: 'Plan roczny kosztuje tyle co 8 miesięcy.',
    planTrialBuyNote: date => 'Wykupienie subskrypcji teraz nie skraca okresu próbnego: pierwsza płatność zostanie pobrana po jego zakończeniu, ' + date + '.',
    planTrialBuyNow: 'Okres próbny kończy się za mniej niż dwa dni, więc pierwsza płatność zostanie pobrana od razu.',
    planRenewNote: 'Subskrypcja odnawia się automatycznie. Możesz ją anulować w każdej chwili, ze skutkiem na koniec opłaconego okresu. Płatności obsługuje Stripe — dane karty nie trafiają na nasz serwer.',
    planManage: 'ZARZĄDZAJ SUBSKRYPCJĄ',
    planSubActive: (amount, interval, date) => 'Subskrypcja: ' + amount + ' ' + (interval === 'year' ? 'rocznie' : 'miesięcznie') + '. Następna płatność ' + date + '.',
    planSubTrialing: date => 'Subskrypcja aktywna. Pierwsza płatność zostanie pobrana ' + date + '.',
    planSubCancelled: date => 'Subskrypcja anulowana — Liquidity+ działa do ' + date + '.',
    planSubPastDue: 'Nie udało się pobrać ostatniej płatności. Zaktualizuj kartę pod przyciskiem „Zarządzaj subskrypcją”, żeby zachować Liquidity+.',
    planNative: 'Plan Liquidity+ można wykupić na stronie: liquidityraiders.pl/portfolio.',
    billingSuccess: 'Dziękujemy! Liquidity+ jest aktywny.',
    billingPending: 'Płatność jest jeszcze przetwarzana. Liquidity+ włączy się, gdy Stripe ją potwierdzi.',
    billingCancel: 'Płatność przerwana — nic nie zostało pobrane.',
    apiBillingClosed: 'Liquidity+ nie jest jeszcze w sprzedaży.',
    apiBillingActive: 'To konto ma już subskrypcję — możesz nią zarządzać w sekcji Plan.',
    apiBillingUnavailable: 'Nie udało się połączyć z operatorem płatności. Nic nie zostało pobrane — spróbuj za chwilę.',
    apiBillingGuest: 'Konto demo nie może wykupić planu — najpierw załóż konto.',
    apiBillingNone: 'To konto nie ma subskrypcji do zarządzania.',
    apiAdminStripe: 'To konto płaci przez Stripe — jego plan zmienia się w panelu Stripe.',
    adminSaved: email => 'Zapisano plan konta ' + email + '.',
    editIncomeTitle: 'Edytuj przychód',
    editExpenseTitle: 'Edytuj wydatek',
    editEntryNote:
      'Miesiąc można tu zmienić — przypisanie wpisu do złego miesiąca '
      + 'to najczęstsza pomyłka i właśnie tak się ją poprawia.',
    editFuelTitle: 'Edytuj tankowanie',
    editFuelNote:
      'Stan licznika liczy się podwójnie: to odczyt przy tym tankowaniu i punkt odniesienia '
      + 'dla następnego, więc jedna błędna cyfra psuje dwa wyniki spalania.',
    editCarExpenseTitle: 'Edytuj koszt',
    editCarExpenseNote: 'Powiązany wpis w wydatkach zmieni się razem z tym.',
    editTradeTitle: 'Edytuj transakcję',
    editTradeNote:
      'Błędnie wpisany wynik zmienia skuteczność i odsetek transakcji zgodnych z zasadami '
      + '— a właśnie dla tych liczb prowadzi się dziennik.',
    monthNeeded: 'Podaj miesiąc w formacie 2026-08.',
    dateNeeded: 'Podaj datę w formacie 2026-08-14.',
    monthLabel: 'miesiąc',
    dateLabel: 'data',
    categoryLabel: 'kategoria',
    typeLabel: 'rodzaj',
    editHoldingTitle: 'Edytuj pozycję',
    editDebtTitle: 'Edytuj dług',
    editDebtNote:
      'Saldo zostanie zapisane dla miesiąca wybranego u góry ekranu — wcześniejsze miesiące zachowają swoje. '
      + 'Jeśli zostawisz liczbę miesięcy pustą, plan spłaty zostanie wyliczony z raty. '
      + 'Zapisane wpłaty pozostają bez zmian.',
    debtBalanceIn: m => 'saldo · ' + m,
    tickerNeeded: 'Pozycja wymaga tickera.',
    classLabel: 'klasa',
    currencyLabel: 'waluta',
    numberNeeded: 'Sprawdź pola — potrzebne są liczba sztuk i cena, obie w postaci liczb.',
    amountNeeded: 'Sprawdź pole — potrzebna jest kwota w postaci liczby.',
    pairNeeded: 'Podaj instrument — bez niego transakcji nie da się zapisać.',
    rateNeeded: 'Kurs musi być liczbą większą od zera.',
    inflationNeeded: 'Stopa inflacji musi być liczbą — minus jest dozwolony, ceny czasem spadają.',
    labelNeeded: 'Podaj nazwę i kwotę.',
    balanceNeeded: 'Saldo wymaga trzyliterowego kodu waluty i kwoty.',
    fuelNeeded: 'Tankowanie wymaga stanu licznika i ilości paliwa — obu w postaci liczb.',
    cashTitle: 'Pozycja gotówkowa',
    cashRate: 'oprocentowanie roczne %',
    cashEdit: 'Oprocentowanie i kwota — kliknij, aby poprawić',
    cashNoYield: 'bez odsetek',
    cashEarned: 'odsetki',
    inflationSection: 'Inflacja',
    inflationNote: 'Roczna stopa inflacji dla każdej waluty, używana do wyceny pozycji gotówkowych. Nic nie jest zakładane z góry: waluta bez wpisanej stopy po prostu nie pokazuje spadku siły nabywczej.',
    inflationMissing: 'brak stopy inflacji',
    inflationHeld: (months, rate) => months + ' ' + pl(months, 'miesiąc', 'miesiące', 'miesięcy') + ' przy ' + rate + '%',
    phInflation: 'inflacja %',
    pricesConverted: n =>
      'Ceny zaktualizowane. ' + n + ' ' + pl(n, 'pozycja była notowana', 'pozycje były notowane', 'pozycji było notowanych')
      + ' w innej walucie i ' + pl(n, 'została przeliczona', 'zostały przeliczone', 'zostało przeliczonych')
      + ' po Twoim zapisanym kursie — sprawdź ten kurs, jeśli któraś kwota wydaje się nieprawidłowa.',
    pricesNoRate: list =>
      'Nie zaktualizowano: ' + list + ' — dla waluty notowania nie ma zapisanego kursu. Dodaj kurs i odśwież.',
    version: 'WERSJA',
    versionNote: 'Podaj ją, jeśli zgłaszasz coś, co wygląda na błąd.',
    ai_unavailable: 'Nie udało się połączyć z modelem Claude. Nic nie zostało zmienione — spróbuj za chwilę.',
    ai_unconfigured: 'Podpowiedzi kategorii nie są dostępne na tym serwerze.',
    ai_plan: 'Podpowiedzi kategorii są częścią planu Liquidity+.',
    ai_quota: 'Miesięczny limit podpowiedzi w Liquidity+ został wykorzystany. Odnowi się pierwszego dnia miesiąca.',
    heldSince: 'Posiadane od — miesiąc, od którego walor się liczy',
    heldSinceTitle: 'MIESIĄC ZAKUPU',
    heldSinceNote: 'Wybierz miesiąc zakupu waloru. Będzie on liczony od tego miesiąca, a jego cena zostanie pobrana z Yahoo dla każdego kolejnego miesiąca.',
    heldSinceNoteCash: 'Wybierz miesiąc, w którym odłożono te pieniądze. Od niego liczą się odsetki i inflacja.',
    trendScale: (low, high) => 'SKALA ' + low + ' – ' + high,
    trendProjected: 'Przyszłe miesiące są pokazane według dzisiejszych cen — cen na te miesiące jeszcze nie ma.',
    trendProjectedBar: 'Ten miesiąc jeszcze nie nadszedł. Nie ma dla niego cen, więc przyjęto ostatnie znane.',
    ratesBackfillMissed: list => 'Brak historii dla: ' + list + '.',
    pricesBackfilled: n =>
      'Uzupełniono ' + n + ' ' + pl(n, 'cenę miesięczną.', 'ceny miesięczne.', 'cen miesięcznych.'),
    pricesBackfillNone: 'Nie ma czego uzupełniać — każdy miesiąc ma już swoje ceny.',
    pricesBackfillMissed: list => 'Brak historii cen dla: ' + list + '.',
    pricesNote: 'Ceny odświeżają się raz dziennie. Przycisk „Uzupełnij historię” sprawdza, ile każdy walor był wart w kolejnych miesiącach, dzięki czemu miniony miesiąc pokazuje ówczesną wycenę.',
    ratesMonthNote: month =>
      'Wpisany kurs obowiązuje od miesiąca ' + month +
      ' do kolejnego wpisu, dzięki czemu zamknięte miesiące zachowują kurs, po którym je wyceniono.',
    rateFrom: month => 'obowiązuje od ' + month,
    rateStanding: 'stały',
    rateDropMonth: 'Usuń ten kurs',
    importTitle: 'Sprawdź przed importem',
    importColumns: 'Kolumny',
    importField: {
      date: 'Data', label: 'Opis', amount: 'Kwota', currency: 'Waluta'
    },
    importNoColumn: 'brak w pliku',
    importNote: base =>
      'Wiersze bez waluty są traktowane jako ' + base +
      '. Wydatki są ujemne, tak jak zapisuje je bank.',
    importRows: 'Wiersze',
    importInclude: 'Uwzględnij ten wiersz',
    importUnusable: 'Nie udało się odczytać daty lub kwoty z tego wiersza',
    importUnusableNote: n =>
      (n === 1 ? 'Jeden wiersz' : n + ' ' + pl(n, 'wiersz', 'wiersze', 'wierszy')) +
      ' pominięto: brak daty lub kwoty. Sprawdź przypisanie kolumn powyżej.',
    importIn: 'PRZYCHÓD',
    importOut: 'WYDATEK',
    importFlip: 'Zamień przychód i wydatek',
    importMore: n => n === 1
      ? 'Jeszcze 1 wiersz nie jest pokazany — zostanie zaimportowany tak samo.'
      : 'Jeszcze ' + n + ' ' + pl(n, 'wiersz', 'wiersze', 'wierszy') + ' '
        + pl(n, 'nie jest pokazany', 'nie są pokazane', 'nie jest pokazanych')
        + ' — zostaną zaimportowane tak samo.',
    importSummary: (included, bad, transfers = 0) =>
      included + ' ' + pl(included, 'wybrany', 'wybrane', 'wybranych') +
      (bad ? ', ' + bad + ' ' + pl(bad, 'nieczytelny', 'nieczytelne', 'nieczytelnych') : '') +
      (transfers
        ? ', ' + transfers + ' ' + pl(transfers, 'przelew własny pominięty', 'przelewy własne pominięte', 'przelewów własnych pominiętych')
        : ''),
    importCategory: 'Kategoria',
    importNoCategory: 'brak (Inne)',
    importSource: {
      file: 'z pliku', rule: 'Twoja reguła', dictionary: 'słownik', bank: 'kategoria banku', ai: 'AI', manual: 'ustawione przez Ciebie'
    },
    importTransfer: 'przelew własny — pominięty',
    importDuplicate: 'już jest w rejestrze — pominięty',
    importVoided: 'cofnięta albo odrzucona przez bank — pominięta',
    pushTitle: 'Przypomnienia o\u00a0płatnościach',
    pushNote: 'Powiadomienie na tym telefonie lub komputerze, zanim przyjdzie termin opłaty stałej, subskrypcji albo raty — około 9 rano.',
    pushThisDevice: 'Przypomnienia na tym urządzeniu',
    pushOnNote: n => (n === 1 ? 'włączone — dostaje je tylko to urządzenie' : 'włączone — dostaje je ' + n + ' ' + pl(n, 'urządzenie', 'urządzenia', 'urządzeń')),
    pushOffNote: 'wyłączone — po włączeniu przeglądarka zapyta o\u00a0zgodę',
    pushOffBlocked: 'wyłączone — powiadomienia są zablokowane dla tej strony',
    pushWhen: 'Kiedy',
    pushLead: { 0: 'W\u00a0dniu płatności', 1: 'Dzień wcześniej', 3: '3 dni wcześniej' },
    pushWhat: 'O\u00a0czym',
    pushBills: 'Opłaty stałe i\u00a0subskrypcje',
    pushDebts: 'Raty kredytów i\u00a0kart',
    pushIncome: 'Wpływy',
    pushTest: 'WYŚLIJ TEST',
    pushPrivacy: 'Przypomnienia dotyczą płatności z\u00a0ustawionym dniem miesiąca. Powiadomienie przechodzi przez usługę push producenta przeglądarki, zaszyfrowane tak, że ta usługa nie może go odczytać.',
    pushGuest: 'Załóż konto, żeby włączyć przypomnienia — demo to tylko przykładowe liczby.',
    pushIosHome: 'Na iPhonie i\u00a0iPadzie najpierw dodaj aplikację do ekranu początkowego (Udostępnij → Do ekranu początkowego), otwórz ją stamtąd i\u00a0włącz przypomnienia.',
    pushNative: 'W\u00a0tej aplikacji jeszcze niedostępne — otwórz Portfolio Tracker w\u00a0przeglądarce na tym urządzeniu, żeby włączyć przypomnienia.',
    pushNone: 'Ta przeglądarka nie obsługuje powiadomień.',
    pushBlocked: 'Powiadomienia są zablokowane dla tej strony — zezwól na nie w\u00a0ustawieniach strony w\u00a0przeglądarce, a\u00a0potem włącz przypomnienia.',
    pushFailed: 'Nie udało się dostarczyć powiadomienia. Wyłącz i\u00a0włącz przypomnienia na tym urządzeniu.',
    pushOn: 'Przypomnienia są włączone na tym urządzeniu.',
    pushOff: 'Przypomnienia są wyłączone na tym urządzeniu.',
    pushTestSent: 'Test wysłany — powinien pojawić się za chwilę.',
    prefTestSent: '✓ WYSŁANO — SPRAWDŹ URZĄDZENIE',
    prefSections: {
      general: ['Ogólne', 'język, ekran startowy i\u00a0wygląd kwot'],
      currencies: ['Waluty i\u00a0kursy', 'waluta bazowa, kursy i\u00a0inflacja'],
      finance: ['Finanse', 'strategia spłaty i\u00a0gotówka'],
      reminders: ['Powiadomienia', 'przypomnienia o\u00a0płatnościach'],
      ai: ['AI i\u00a0prywatność', 'co i\u00a0kiedy trafia do Claude'],
      account: ['Konto i\u00a0dane', 'logowanie, kopia danych, usunięcie konta'],
      plan: ['Plan', 'co obejmuje Twój plan']
    },
    prefAutoSave: 'zapisuje się samo',
    prefSaved: '✓ zapisano',
    prefBack: 'Wstecz',
    prefVersion: v => 'wersja ' + v,
    prefCopy: 'kopiuj',
    prefCopied: '✓ skopiowano',
    prefClaim: 'Załóż konto',
    prefLinkDiscord: 'Połącz Discord',
    prefUnlinkDiscord: 'Odłącz Discord',
    prefChangePw: 'Zmień hasło',
    prefAddPw: 'Dodaj hasło',
    prefYourData: 'Twoje dane',
    prefLanguage: 'Język',
    prefLangSub: 'interfejs, daty i\u00a0formaty liczb',
    prefStartSub: 'co widzisz po zalogowaniu',
    prefBaseSub: 'w\u00a0niej pokazujemy wszystkie sumy · wpisane kwoty nie są przeliczane',
    prefRatesTitle: 'Kursy i\u00a0inflacja',
    prefRatesSub: (base, month) => 'ile ' + base + ' kosztuje 1\u00a0jednostka waluty · kurs obowiązuje od ' + month + ' do kolejnego wpisu, więc zamknięte miesiące zachowują swój',
    prefColCurrency: 'WALUTA',
    prefColRate: 'KURS',
    prefColInflation: 'INFLACJA R/R',
    prefColHistory: 'OSTATNIE MIESIĄCE',
    prefBase: 'bazowa',
    prefNoRate: 'brak kursu',
    prefRemoveCurrency: 'Usuń kurs',
    prefAddCurrency: 'Dodaj walutę:',
    prefInflationNote: 'Inflacja jest opcjonalna — służy tylko do pokazania spadku siły nabywczej gotówki. Bez wpisanej stopy nic nie zakładamy.',
    prefStrategySub: 'ustala kolejkę w\u00a0zakładce Długi i\u00a0podpowiedzi w\u00a0Przeglądzie',
    prefStrategyRateSub: 'najmniej odsetek łącznie',
    prefStrategyBalanceSub: 'szybciej znikają pozycje',
    prefCashTitle: 'Oszczędności · gotówka',
    prefCashSub: 'jedno saldo na każdą walutę · trafia do „Gdzie leżą pieniądze” i\u00a0kapitału ryzyka',
    prefAddBalance: '+ SALDO',
    prefFixedTitle: 'Opłaty stałe',
    prefFixedSub: 'subskrypcje i\u00a0czynsz są w\u00a0Przychodach i\u00a0wydatkach',
    prefCsvTitle: 'Import i\u00a0eksport CSV',
    prefCsvSub: 'obok wpisów, do których trafiają, w\u00a0Przychodach i\u00a0wydatkach',
    prefPushWhen: 'Kiedy · ok. 9:00',
    prefAiTitle: 'Podpowiedzi kategorii',
    prefAiSub: 'przycisk „Podpowiedz kategorie” przy imporcie prosi Claude o\u00a0kategorie dla wierszy, których nie zna słownik',
    prefAiUsed: 'wykorzystano w\u00a0tym miesiącu',
    prefAiOf: (used, limit) => used + ' / ' + limit + ' opisów',
    prefAiSend: 'WYSYŁAMY',
    prefAiSendBody: 'wyłącznie opisy transakcji',
    prefAiNever: 'NIGDY',
    prefAiNeverBody: 'kwot, dat ani danych konta',
    prefAiNote: 'Nieznaną nazwę Claude może sprawdzić w\u00a0sieci. Podpowiedzi to tylko propozycje — nic się nie zapisze, dopóki nie klikniesz „Importuj”.',
    prefAiOff: 'Niedostępne na tym serwerze.',
    prefReviewTitle: 'Przegląd tygodnia',
    prefReviewSub: 'też przygotowuje go Claude — tylko na Twoją prośbę, w\u00a0Przeglądzie',
    prefSyncedEntries: n => 'ZSYNCHRONIZOWANO · ' + n + ' ' + pl(n, 'WPIS', 'WPISY', 'WPISÓW'),
    prefLinkDiscordSub: 'logowanie jednym albo drugim',
    prefUnlinkSub: 'logowanie będzie wtedy tylko e-mailem i\u00a0hasłem',
    prefChangePwSub: 'obecne hasło i\u00a0nowe',
    prefAddPwSub: 'żeby logować się także bez Discorda',
    prefAdminSub: 'liczby i\u00a0konta — tylko dla Ciebie',
    prefSignOutSub: 'na tym urządzeniu',
    prefClaimSub: 'zachowaj to demo jako własne konto',
    prefDataSub: 'pobierz kopię wszystkiego albo usuń konto — usunięcia nie da się cofnąć',
    prefDownload: '↓ POBIERZ KOPIĘ (JSON)',
    prefMenuPrefs: 'Preferencje',
    prefMenuAdmin: 'Panel administracyjny',
    prefMenuSignOut: 'Wyloguj się',
    prefAccountMenu: 'Menu konta',
    prefCardLine: (n, plan) => '✓ ' + n + ' ' + pl(n, 'wpis', 'wpisy', 'wpisów') + ' · ' + plan,
    prefCardSyncing: 'synchronizacja…',
    prefCardOffline: n => 'offline' + (n ? ' · ' + n + ' w\u00a0kolejce' : ''),
    prefPlanGift: 'GRATIS',
    prefPlanTrial: 'PRÓBA',
    prefPlanGiftLine: date => 'dla osób z\u00a0nami od początku · do ' + date,
    prefPlanTrialLine: date => 'okres próbny do ' + date,
    prefPlanSubLine: 'subskrypcja · zarządzanie tutaj',
    prefPlanGrantLine: date => (date ? 'do ' + date : 'przyznany'),
    prefPlanFreeLine: 'zobacz, co daje Liquidity+',
    prefPlanSoonLine: date => 'plany od ' + date,
    prefSumRates: n => n + ' ' + pl(n, 'kurs', 'kursy', 'kursów'),
    prefSumNoCash: 'brak sald',
    prefSumCash: n => n + ' ' + pl(n, 'saldo', 'salda', 'sald'),
    prefOn: 'włączone',
    prefOff: 'wyłączone',
    importShowAll: n => 'POKAŻ WSZYSTKIE (' + n + ')',
    importLearnNote:
      'Zmieniona kategoria zostanie zapamiętana dla tego sprzedawcy i przy kolejnym wyciągu przypisze się automatycznie.',
    importConfirm: n => 'IMPORTUJ ' + n,
    importDone: (imported, skipped, duplicates) =>
      'Zaimportowano ' + imported + ' ' + pl(imported, 'wpis', 'wpisy', 'wpisów') +
      (duplicates
        ? ', ' + duplicates + ' ' + pl(duplicates, 'był już w rejestrze', 'były już w rejestrze', 'było już w rejestrze')
        : '') +
      (skipped ? ', pominięto ' + skipped : '') + '.',
    importNothing: 'Nie wybrano żadnego wiersza.',
    importUnreadable: 'Nie udało się odczytać tego pliku.',
    importEmpty: 'Ten plik nie zawiera wierszy.',
    dataSection: 'Wyciągi',
    dataNote: month =>
      'Wczytaj wyciąg CSV z banku — transakcje zostaną zaimportowane na bieżący miesiąc: '
      + month + '. Przed zapisem sprawdzisz każdy wiersz.',
    dataMoved: tab => 'Import i eksport CSV są teraz w zakładce „' + tab + '” — obok wpisów, do których trafiają.',
    monthFull: key => monthIn(MONTHS_FULL_PL, key) + ' ' + String(key).slice(0, 4),
    importMonthTitle: 'Miesiąc',
    importMonthViewed: month => 'Bieżący miesiąc: ' + month,
    importMonthDated: 'Według dat z pliku',
    importMonthViewedNote: month => 'Wszystkie zaznaczone wiersze zostaną zaimportowane na bieżący miesiąc: ' + month + '.',
    importMonthDatedNote:
      'Każdy wiersz trafi do miesiąca ze swojej daty — wyciąg za kilka miesięcy rozłoży się na nie.',
    importMonthOutside: (n, months) =>
      n + ' ' + pl(n, 'wiersz ma', 'wiersze mają', 'wierszy ma') + ' datę z innego miesiąca (' + months + '). '
      + 'Jeśli to wyciąg za kilka miesięcy, wybierz „Według dat z pliku”.',
    importFrom: month => 'w pliku: ' + month,
    dataImport: 'IMPORTUJ CSV',
    dataExport: 'EKSPORTUJ CSV',
    exportEmpty: 'Nie ma jeszcze czego eksportować.',
    exportDone: n => 'Zapisano ' + n + ' ' + pl(n, 'wiersz.', 'wiersze.', 'wierszy.'),
    recurring: 'Opłaty stałe',
    recurringNote: 'Czynsz, pensja, ubezpieczenie, subskrypcje — wszystko, co wraca. '
      + 'Zapisujesz raz, a w miesiącu obciążenia dodajesz jednym kliknięciem.',
    recurringAdd: 'DODAJ',
    recurringPost: n => 'DODAJ ' + n,
    fixedCosts: 'Opłaty stałe',
    fixedCostsNote: 'To, co się powtarza: subskrypcje, czynsz, ubezpieczenie, pensja.',
    fixedEmpty: 'Nie ma jeszcze żadnych opłat stałych. Dodaj subskrypcję albo rachunek — będzie tu na Ciebie czekać.',
    fixedCommitted: 'STAŁE / MIESIĄC',
    fixedIncoming: 'STAŁE PRZYCHODY',
    fixedAveraged: 'Opłaty roczne i kwartalne są tu pokazane jako część miesięczna. '
      + 'Sam wpis dodaje się w pełnej kwocie, w miesiącu obciążenia.',
    fixedShare: pct => pct + '% przychodów tego miesiąca',
    fixedNoIncome: 'brak przychodów w tym miesiącu',
    fixedThisMonth: 'OBCIĄŻENIA W TYM MIESIĄCU',
    fixedNextCharge: month => 'najbliższa: ' + month,
    fixedNoNext: 'już się nie powtórzy',
    fixedOnDay: day => day + '. dnia',
    fixedUnconverted: n => n === 1
      ? 'Jedna opłata jest w walucie bez kursu i nie wchodzi do tej sumy.'
      : n + ' ' + pl(n, 'opłata', 'opłaty', 'opłat')
        + ' ' + pl(n, 'jest', 'są', 'jest') + ' w walucie bez kursu i nie '
        + pl(n, 'wchodzi', 'wchodzą', 'wchodzi') + ' do tej sumy.',
    cadence: { 1: 'co miesiąc', 3: 'co kwartał', 6: 'co pół roku', 12: 'co rok' },
    cadenceShort: { 1: 'MIES.', 3: 'KW.', 6: 'PÓŁ ROKU', 12: 'ROK' },
    fixedEvery: 'powtarza się',
    fixedFrom: 'pierwsze obciążenie',
    fixedUntil: 'kończy się po',
    fixedUntilNone: 'trwa, dopóki nie przerwiesz',
    editFixedTitle: 'Edytuj opłatę stałą',
    upcoming: 'Co Cię czeka',
    upcomingNote: days => 'Opłaty stałe wypadające w ciągu najbliższych ' + days + ' dni.',
    upcomingLeaving: 'ZEJDZIE Z KONTA',
    upcomingEmpty: 'W najbliższych tygodniach nic nie wypada. Wszystko, co się powtarza, '
      + 'pojawia się tutaj przed obciążeniem, nie po.',
    upcomingToday: 'dziś',
    upcomingTomorrow: 'jutro',
    upcomingInDays: n => 'za ' + n + ' ' + pl(n, 'dzień', 'dni', 'dni'),
    upcomingSomeTime: 'bez podanego dnia',
    upcomingPosted: 'jest w rejestrze',
    fixedMoved: 'Subskrypcje, czynsz i inne powtarzające się opłaty są teraz w zakładce '
      + 'z przychodami i wydatkami.',
    recurringPosted: n => 'Dodano ' + n + ' ' + pl(n, 'wpis.', 'wpisy.', 'wpisów.'),
    recurringNone: 'W tym miesiącu nie ma już nic do dodania.',
    recurringAgainTitle: 'Opłaty stałe są już dodane',
    recurringAgainQuestion: 'Czy chcesz ponownie dodać opłaty stałe do rozliczeń tego miesiąca?',
    recurringAgainNote: (n, key) => 'Są już w rozliczeniach za ' + monthIn(MONTHS_FULL_PL, key) + ' '
      + String(key).slice(0, 4) + '. Potwierdzenie doda je drugi raz: ' + n + ' ' + pl(n, 'wpis', 'wpisy', 'wpisów') + '.',
    recurringAgainYes: 'TAK, DODAJ PONOWNIE',
    queueHeld: 'Sesja się zakończyła. Zmiany wprowadzone bez sieci są zachowane — zaloguj się, żeby je wysłać.',
    queueRestored: 'Zmiany wprowadzone bez sieci są w drodze.',
    economyNeedsTwo: 'spalanie pojawi się po drugim tankowaniu do pełna',
    economyNoFills: 'brak tankowań',
    recurringPaused: 'wstrzymana',
    recurringResume: 'WZNÓW',
    recurringPause: 'WSTRZYMAJ',
    undoDeleted: kind => kind + ' — usunięto',
    paymentLogged: name => 'Zapisano spłatę: ' + name,
    undo: 'COFNIJ',
    removeLabel: 'Usuń',
    undoKinds: {
      income: 'Przychód', expense: 'Wydatek', debt: 'Dług',
      holding: 'Walor', fuel: 'Tankowanie', service: 'Serwis',
      recurring: 'Opłata stała', trade: 'Transakcja'
    },
    optBaseCurrency: 'Waluta bazowa',
    optBaseCurrencyNote: 'W tej walucie pokazywane są wszystkie sumy. Wpisane wcześniej kwoty nie są przeliczane.',
    savings: 'Oszczędności',
    savingsNote: 'Jedno saldo na każdą posiadaną walutę.',
    addBalance: 'DODAJ SALDO',
    rates: 'Kursy walut',
    ratesNote: n => 'Ile ' + n + ' kosztuje jedna jednostka danej waluty.',
    rateRefresh: 'POBIERZ KURSY',
    rateManual: 'ręczny',
    ratesUpdated: 'Kursy zaktualizowane.',
    ratesPartial: missed => 'Zaktualizowano. Brak kursu dla: ' + missed,
    ratesAuto: codes => 'Dodano kurs dla ' + codes + ' — kwoty liczą się już w\u00a0sumach. Zmienisz go w\u00a0Preferencjach.',
    prRules: 'ZASADY',
    prRulesNone: 'Ustaw dzienny limit straty, drawdown i\u00a0cel zysku, a\u00a0ta karta pokaże, ile zostało miejsca.',
    prSetRules: 'USTAW ZASADY',
    prEditRules: 'Zmień zasady',
    prDaily: 'Dzienny limit straty',
    prMaxDd: 'Maksymalny drawdown',
    prDdType: 'Drawdown',
    prDdTypes: { static: 'Stały', eod: 'Kroczący, na koniec dnia', intraday: 'Kroczący, w\u00a0trakcie dnia' },
    prTarget: 'Cel zysku',
    prSave: 'ZAPISZ ZASADY',
    prToday: 'DZIŚ',
    prTodayVal: (room, limit) => 'zostało ' + room + ' z\u00a0' + limit,
    prTodaySub: pnl => 'dziś ' + pnl,
    prDrawdown: 'DRAWDOWN',
    prDdVal: room => room + ' nad progiem',
    prDdSub: (floor, type) => 'próg ' + floor + ' · ' + type,
    prTargetLab: 'CEL',
    prTargetVal: (pnl, target) => pnl + ' z\u00a0' + target,
    prTargetSub: pct => pct + '% drogi',
    prBreached: 'Limit przekroczony — w\u00a0większości firm oznacza to oblane konto. Sprawdź w\u00a0firmie.',
    prWarning: 'Na którymś limicie zostało mniej niż 20% miejsca — dziś ostrożnie.',
    prReached: 'Cel zysku osiągnięty.',
    prNoTrades: 'Wybierz to konto przy dodawaniu transakcji w\u00a0dzienniku, a\u00a0saldo i\u00a0limity policzą się tutaj.',
    prBalance: (balance, n) => 'saldo ' + balance + ' · ' + n + ' ' + pl(n, 'transakcja', 'transakcje', 'transakcji'),
    prTrailNote: 'Kroczący drawdown idzie za najwyższym saldem i\u00a0zatrzymuje się na saldzie początkowym, jak w\u00a0większości firm.',
    jAccount: 'Konto',
    jOwnAccount: 'Własne środki',
    bgTitle: 'Budżety',
    bgSub: month => 'miesięczne limity · ' + month,
    bgEmpty: 'Nie ma jeszcze limitów. Ustaw limit dla kategorii poniżej i\u00a0patrz, ile z\u00a0niego zostaje w\u00a0miesiącu.',
    bgOf: (a, b) => a + ' z\u00a0' + b,
    bgLeft: (left, pct) => 'zostaje ' + left + ' · wykorzystano ' + pct + '%',
    bgOver: amount => amount + ' ponad limit',
    bgTotal: (spent, limit) => 'Kategorie z\u00a0limitem: ' + spent + ' z\u00a0' + limit,
    bgOverCount: n => n + ' ' + pl(n, 'kategoria', 'kategorie', 'kategorii') + ' ponad limit',
    bgCategory: 'Kategoria',
    bgLimit: code => 'Limit miesięczny (' + code + ')',
    bgAdd: 'USTAW LIMIT',
    bgSave: 'ZAPISZ',
    bgEdit: 'Zmień limit',
    bgRemove: 'Usuń limit',
    glTitle: 'Cele oszczędnościowe',
    glSub: 'na co odkładasz i\u00a0kiedy to osiągniesz',
    glEmpty: 'Poduszka finansowa, wakacje, samochód — dodaj cel i\u00a0zobacz, kiedy go osiągniesz w\u00a0swoim tempie.',
    glDone: '✓ osiągnięty',
    glReady: month => 'gotowe: ' + month,
    glOnTrack: month => 'zdążysz na ' + month,
    glNeeds: (amount, month) => 'potrzeba ' + amount + ' miesięcznie, żeby zdążyć na ' + month,
    glMissed: month => 'termin (' + month + ') minął',
    glNeedsFrom: (amount, month) => amount + ' miesięcznie wystarczy na ' + month,
    glNoPace: 'dodaj kwotę miesięczną, żeby zobaczyć, kiedy osiągniesz cel',
    glPace: amount => amount + ' miesięcznie',
    glName: 'Cel',
    glNamePh: 'np. Poduszka finansowa',
    glTarget: 'Kwota docelowa',
    glSaved: 'Odłożone',
    glMonthly: 'Co miesiąc',
    glDeadline: 'Termin',
    glDeadlineNote: 'miesiąc, do którego chcesz zdążyć (opcjonalnie)',
    glNoDeadline: 'BEZ TERMINU',
    glAdd: 'DODAJ CEL',
    glSaveChanges: 'ZAPISZ ZMIANY',
    glNew: '+ NOWY CEL',
    glEdit: 'Zmień cel',
    glRemove: 'Usuń cel',
    missingRateTitle: 'Brak kursu wymiany',
    missingRateBody: codes =>
      'Kwoty w ' + codes + ' są pomijane we wszystkich sumach, dopóki nie ustawisz kursu.',
    setRates: 'USTAW KURSY',
    amount: 'kwota',
    a11yBalance: code => 'Oszczędności w ' + code, a11yBalanceCurrency: 'Waluta nowych oszczędności', a11yBalanceAmount: 'Kwota nowych oszczędności',
    a11yRate: code => 'Kurs ' + code, a11yOtherCurrency: 'Inna waluta',
    rate: 'kurs',
    optUnits: 'Jednostki pojazdu',
    editVehicle: 'Edytuj pojazd',
    vehicleName: 'pojazd',
    odometer: 'licznik',
    startOdometer: 'licznik przy zakupie',
    saveVehicle: 'ZAPISZ POJAZD',
    vehicleSaved: 'Pojazd zaktualizowany.',
    odometerOrder: 'Licznik przy zakupie nie może być wyższy niż obecny.',
    sheetDebt: 'Dług',
    sheetHolding: 'Walor',
    sheetService: 'Serwis',
    sheetVehicle: 'Pojazd',

    ariaPrevMonth: 'Poprzedni miesiąc',
    ariaNextMonth: 'Następny miesiąc',
    ariaLanguage: 'Zmień język',

    fuelFull: 'DO PEŁNA',
    fuelPartial: 'CZĘŚCIOWE',
    fuelFullNote: 'Spalanie jest liczone między dwoma tankowaniami do pełna. Wyłącz tę opcję przy dolewaniu — te litry zostaną doliczone do następnego pełnego baku.',
    fuelPartialShort: 'dolewka',
    fillTooBig: tank => 'To więcej, niż mieści bak (' + tank + ') — sprawdź liczbę albo pojemność baku w danych pojazdu.',
    tankLabel: 'pojemność baku',
    rangeTitle: 'ZASIĘG',
    rangeSub: tank => 'na pełnym baku (' + tank + ')',
    vinLabel: 'VIN',
    vinInvalid: 'VIN ma 17 znaków — litery i cyfry, bez I, O i Q.',

    emptyTitle: 'Twój rejestr jest pusty',
    emptyBody: 'Dodaj przychody i wydatki w zakładce Przychody i wydatki. Długi, inwestycje i pojazd mają własne zakładki.',
    emptyList: 'Na razie pusto',
    nothingToFlag: 'Wszystko wygląda w porządku.'
  }
};

const DICT = { EN, PL };

/**
 * Returns the dictionary for a language, with mobile overrides folded in when
 * the mobile layout is active — so views read `t.expensesCap` either way.
 */
export function strings(lang, { mobile = false } = {}) {
  const base = DICT[lang] || DICT.EN;
  if (!mobile) return base;

  return {
    ...base,
    ...base.mobile,
    nav: base.mobile.nav,
    pages: base.mobile.pages,
    fx: { ...base.fx, ...base.mobile.fx }
  };
}

export const LANGUAGES = [
  { code: 'EN', name: 'English' },
  { code: 'PL', name: 'Polski' }
];
